import React, { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// Import Highcharts modules
import 'highcharts/modules/boost';
import 'highcharts/modules/annotations';
import { Spinner, Form, Row, Col, Button, Modal, Card, Table, Alert, Dropdown } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import mqtt from 'mqtt';
import { toast, ToastContainer } from 'react-toastify';
import Header from './Header.tsx';
import Sidebar from './Sidebar.tsx';
import TreatmentModal from './TreatmentModal.tsx';
import ConcentrationModal from './ConcentrationModal.tsx';
import RulesManagementModal from './RulesManagementModal.tsx';
import ReportsManagementModal from './ReportsManagementModal.tsx';
import SeismosScorecard, { ScorecardLevel, PadRefreshButton } from './SeismosScorecard.tsx';
import styles from './PadData.module.css';
import seismos from '../Images/seismos.png';
import momentumDark  from '../Images/momentumDark.png';
import momentumLight from '../Images/momentumLight.png';
import { useTheme } from '../hooks/useTheme.ts';
import { TREATMENT_TYPE_OPTIONS } from './config/treatmentTypes.ts';
import { TREATMENT_CHANNEL_LABELS, TREATMENT_CHANNELS } from './config/channels.ts'
import {
  descriptionDictionary,
  getDescriptionOptions,
  getDescriptionValue,
} from './config/proppant.ts';


interface TreatmentDataProps {
  padId?: number;
}

interface SeismosMetric {
  id: number;
  intra_stage_uniformity_index: number | null;
  eff: number;
  perf_frict: number;
  pipe_frict: number;
  ehd: number;
  initial_rate: number | null;
  rate_change: number | null;
  processing_status: string | null;
  annotations: string | null;
  issues: string | null;
  event_time: string;
  event_uuid: string;
  seismos_data_id: number;
}

interface SeismosDataResponse {
  metrics: SeismosMetric[];
  stage_id: number;
  uniformity_index: number;
}

interface SeismosFdiMetric {
  id: number;
  seismos_fdi_data_id: number;
  seismos_parent_well_id: number | null;
  seismos_child_well_id:  number | null;
  parent_well_name: string | null;
  child_well_name:  string | null;
  start_ts: number;
  end_ts: number;
  fdi_magnitude: number | null;
  max_fdi_slope: number | null;
  vfr: number | null;
  estimated_parent_child_distance: number | null;
  estimated_fdi_velocity: number | null;
  event_received: number | null;
}

interface SeismosFdiDataResponse {
  metrics: SeismosFdiMetric[];
  stage_id: number;
}

interface SeismosDataComponentProps {
  stageId: number;
  theme: string;
  styles: any;
  onOpenScorecard: () => void;
  onMetricsChange?: (metrics: SeismosMetric[]) => void;
  onOpenPrcPlot?: () => void;
  padId?: number;          // enables the Refresh Pad Data button
  canRefresh?: boolean;    // false for Viewers — status shows, button disabled
}

interface MomentumFdiMetric {
  id: number;
  momentum_fdi_data_id: number;
  momentum_parent_well_id: number | null;
  momentum_child_well_id:  number | null;
  parent_well_name: string | null;
  child_well_name:  string | null;
  start_ts: number;
  end_ts: number;
  fdi_magnitude: number | null;
  max_fdi_slope: number | null;
  vfr: number | null;
  estimated_parent_child_distance: number | null;
  estimated_fdi_velocity: number | null;
  event_received: number | null;
}
 
interface MomentumFdiDataResponse {
  metrics: MomentumFdiMetric[];
  stage_id: number;
}
 
interface MomentumFdiComponentProps {
  stageId: number;
  theme: string;
  styles: any;
}

interface SelectedStage {
  stageId: number;
  wellId: number;
  stageNumber: number;
}

interface Template {
  id: number;
  template_name: string;
  device_type?: string;
  widget_type?: string;
  aggregator: string;
  channels: TemplateChannel[];
}

interface TemplateChannel {
  channel: string;
  display_position: string;
  min_value: number | null;
  max_value: number | null;
}

interface ChannelOption {
  value: string;
  label: string;
  chem_name?: string;
  metric?: string;
  momentum_well_id?: number;
  seismos_well_id?: number;
  seismos_metric?: string;
  scoreboard_channel?: string;
  uom?: string;
}

const CHANNEL_COLORS: Record<string, string> = {
  treat_press: '#FF5F4D', // red
  clean_rate:  '#3E76FE',
  slurry_rate: '#8CC540',
  stage_denso: 'brown',
  step:        '#9aa0a6',
  step_clean:  '#3e31ecff',
  step_slurry: '#5a960cff',
  step_sand:   '#f9c844',
  elapsed_time:'#fff',
  net_pressure:         '#e07b39',
  stage_slurry:         '#a259e6',
  clean_rate_calc:      '#59b8e6',
  stage_sand:           '#e6c459',
  sand_design_percentage: '#f2a541',
  friction_loss_press:  '#e65959',
  stp_b:                '#59e6a2',
  hydrostatic_press:    '#5977e6',
  stage_clean:          '#c2e659',
  bhp:                  '#e659c2',
};

// ── Seismos per-event metrics (seismos_metrics table) ───────────────────────
// These are sparse, event-timed values (~100 max per stage) plotted on their
// own event_time rather than the 1Hz treatment grid.
const SEISMOS_METRIC_PREFIX = 'seismos_metric_';

const seismosMetricNameDictionary: { [key: string]: string } = {
  eff:                          'Perf Efficiency (%)',
  pipe_frict:                   'Pipe Friction',
  perf_frict:                   'Perf Friction',
  ehd:                          'EHD',
  intra_stage_uniformity_index: 'Intra-Stage Uniformity Index',
  initial_rate:                 'Initial Rate',
  rate_change:                  'Rate Change',
};

const SEISMOS_METRIC_COLORS: Record<string, string> = {
  eff:                          '#ffb703',
  pipe_frict:                   '#fb8500',
  perf_frict:                   '#f77f00',
  ehd:                          '#d62828',
  intra_stage_uniformity_index: '#ffd166',
  initial_rate:                 '#e85d04',
  rate_change:                  '#dc2f02',
};

// Folded into CHANNEL_COLORS so the existing series-color lookup needs no change.
Object.entries(SEISMOS_METRIC_COLORS).forEach(([field, color]) => {
  CHANNEL_COLORS[`${SEISMOS_METRIC_PREFIX}${field}`] = color;
});

const buildSeismosMetricChannels = (): ChannelOption[] =>
  Object.keys(seismosMetricNameDictionary).map(field => ({
    value:          `${SEISMOS_METRIC_PREFIX}${field}`,
    label:          seismosMetricNameDictionary[field],
    seismos_metric: field,
  }));

// Display-unit scaling for seismos metrics. The DB stores eff as a fraction;
// the Seismos table renders it as a percentage ("Perf Efficiency (%)"), so the
// chart scales to match. Single source of truth for both the historical and
// live plotting paths — anything not listed passes through unscaled.
const SEISMOS_METRIC_SCALE: Record<string, number> = {
  eff: 100,
};

const seismosMetricValue = (e: any, field: string): number | null => {
  const raw = e?.[field];
  if (raw == null) return null;
  const n = Number(raw);
  if (Number.isNaN(n)) return null;
  return n * (SEISMOS_METRIC_SCALE[field] ?? 1);
};

// Timestamps are epoch ms in principle, but some rows are stored in epoch
// seconds. Shared guard for anything building a Date from a raw value —
// normalizeTimestamp() applies the same check, but ALSO shifts to chart
// space, so use ensureMs (not normalizeTimestamp) for real instants.
const ensureMs = (ts: number): number => (ts < 10000000000 ? ts * 1000 : ts);

// Seismos event_time is naive UTC; Flask serializes it as an RFC 1123 GMT
// string, which Date.parse handles unambiguously. Second resolution is plenty
// for these events. The bulk /seismos/events endpoint precomputes the epoch,
// so prefer it when present.
const seismosEventMs = (e: any): number | null => {
  if (e?.event_time_ms != null) return Number(e.event_time_ms);
  const parsed = Date.parse(e?.event_time);
  return Number.isNaN(parsed) ? null : parsed;
};

// ── Seismos issue / annotation cells ────────────────────────────────────────
// annotations and issues arrive as the raw JSON array string SAFA sent, e.g.
// ["Pre-change pressure not stable", " "]. Parsed here so the cell shows a
// compact badge instead of the array, with the text in a hover popover —
// long strings can't wrap the column open. Blank placeholders are dropped.
const parseIssueList = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  const s = String(raw).trim();
  if (!s) return [];
  try {
    const arr = JSON.parse(s);
    if (Array.isArray(arr)) return arr.map(v => String(v).trim()).filter(Boolean);
  } catch { /* not JSON — treat as plain text */ }
  return [s];
};

const SeismosIssueCell: React.FC<{ raw: string | null; theme: string }> = ({ raw, theme }) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const items = parseIssueList(raw);
  const text  = items.join(' • ');

  if (!items.length) {
    return (
      <td className="seismos-issue-cell" data-print="—">
        <span className="issue-icon">—</span>
      </td>
    );
  }

  const show = (e: React.MouseEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({ x: Math.min(r.left, window.innerWidth - 320), y: r.bottom + 6 });
  };

  return (
    <td className="seismos-issue-cell" data-print={text}>
      <span
        className="issue-icon"
        title={text}
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '4px',
          padding: '1px 7px', borderRadius: '10px', cursor: 'default',
          fontSize: '0.78rem', lineHeight: 1.6, whiteSpace: 'nowrap',
          color: '#F9C430',
          border: `1px solid ${theme === 'dark' ? '#5C646B' : '#e0c070'}`,
          background: theme === 'dark' ? '#3a3d40' : '#fdf6e3',
        }}
      >
        ⓘ {items.length}
      </span>
      {pos && (
        <div
          style={{
            position: 'fixed', left: pos.x, top: pos.y, zIndex: 3000,
            maxWidth: '300px', padding: '8px 10px', borderRadius: '6px',
            fontSize: '0.8rem', lineHeight: 1.4, whiteSpace: 'normal',
            color: theme === 'dark' ? '#e5e5e5' : '#333333',
            background: theme === 'dark' ? '#303335' : '#ffffff',
            border: `1px solid ${theme === 'dark' ? '#5C646B' : '#d0d4d8'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}
        >
          {items.map((t, i) => (
            <div key={i} style={{ marginTop: i ? '4px' : 0 }}>• {t}</div>
          ))}
        </div>
      )}
    </td>
  );
};

// Scoreboard topics live at the broker root ({van}/scoreboard/...), not under propilot/.
const SCOREBOARD_SUFFIXES = ['metadata', 'telemetry'];   // metadata first

const SEISMOS_WELL_COLORS = [
  '#f97316', // orange
  '#fb923c',
  '#fdba74',
  '#ea580c',
  '#c2410c',
  '#fed7aa',
  '#ff6b35',
];

const MOMENTUM_WELL_COLORS = [
  '#00d4ff', // cyan
  '#00b4d8',
  '#0096c7',
  '#0077b6',
  '#023e8a',
  '#48cae4',
  '#90e0ef',
];

interface TreatmentData {
  id: number;
  pad_id: number;
  timestamp: number;
  treat_press: number;
  clean_rate: number;
  slurry_rate: number;
  stage_denso: number;
  elapsed_time: number;
  step: number;
  step_clean: number;
  step_slurry: number;
  step_sand: number;
  net_pressure:        number;
  stage_slurry:        number;
  clean_rate_calc:     number;
  stage_sand:          number;
  sand_design_percentage: number;
  friction_loss_press: number;
  stp_b:               number;
  hydrostatic_press:   number;
  stage_clean:         number;
  bhp:                 number;
}

interface Stage {
  id: number;
  well_id: number;
  stage_number: number;
  start_ts: number;
  end_ts: number;
  uniformity_index: number | null;
}

interface Well {
  id: number;
  pad_id: number;
  well_name: string;
  api: string;
  stage_count: number;
  start_ts: number | null;
  end_ts: number | null;
}

interface TreatmentStep {
  id: number;
  stage_id: number;
  step: number;
  type: string;
  description: string;
  rate: number;
  conc: number;
  clean_step: number;
  clean_actual: number;
  slurry_step: number;
  slurry_actual: number;
  sand_step: number;
  sand_actual: number;
  elapsed_time?: number; // Optional elapsed time field
}

// Seismos Data Component
// "Stage UI: X  |  Seismos Scorecard" — used in every render state of SeismosDataComponent
const ScorecardHeader: React.FC<{ uiValue: React.ReactNode; onOpen: () => void; onOpenPrc?: () => void }> = ({ uiValue, onOpen, onOpenPrc }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
    <span>Stage UI: {uiValue}</span>
    <span style={{ color: '#5c646b', fontSize: 14 }}>|</span>
    <button
      onClick={onOpen}
      style={{
        background: 'transparent', border: 'none', padding: 0,
        color: '#F9C430', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', letterSpacing: '0.02em',
        textDecoration: 'underline', textUnderlineOffset: 3,
      }}
    >
      Scorecard
    </button>
    {onOpenPrc && (
      <>
        <span style={{ color: '#5c646b', fontSize: 14 }}>|</span>
        <button
          onClick={onOpenPrc}
          style={{
            background: 'transparent', border: 'none', padding: 0,
            color: '#F9C430', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.02em',
            textDecoration: 'underline', textUnderlineOffset: 3,
          }}
        >
          PRC Plot
        </button>
      </>
    )}
  </span>
);

const SeismosDataComponent: React.FC<SeismosDataComponentProps> = ({ stageId, theme, styles, onOpenScorecard, onMetricsChange, onOpenPrcPlot, padId, canRefresh = false }) => {
  const [seismosData, setSeismosData] = useState<SeismosDataResponse | null>(null);
  const [seismosFdiData, setSeismosFdiData] = useState<SeismosFdiDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch seismos data for the stage
  const fetchSeismosData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.get(`${backendUrl}/seismos_data/stage/${stageId}/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSeismosData(response.data);
      onMetricsChange?.(response.data?.metrics || []);
    } catch (err) {
      console.error('Error fetching seismos data:', err);
      setError('Data unavailable');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchSeismosData();
  }, [stageId]);

  // Real-time streaming - fetch every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSeismosData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [stageId]);

  const fetchSeismosFdiData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(
        `${backendUrl}/seismos_fdi_data/stage/${stageId}/metrics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSeismosFdiData(response.data);
    } catch (err) {
      console.error('Error fetching Seismos FDI data:', err);
    }
  };

  useEffect(() => { fetchSeismosFdiData(); }, [stageId]);

  useEffect(() => {
    const interval = setInterval(fetchSeismosFdiData, 30000);
    return () => clearInterval(interval);
  }, [stageId]);

  // Queues a SAFA refresh for the whole pad (seismos-refresh worker), then reloads this card
  const refreshRow = padId ? (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 10px' }}>
      <PadRefreshButton
        padId={padId}
        apiBase={backendUrl ?? ''}
        token={localStorage.getItem('token') ?? ''}
        canRefresh={canRefresh}
        align="center"
        onComplete={() => { fetchSeismosData(); fetchSeismosFdiData(); }}
      />
    </div>
  ) : null;

  if (loading && !seismosData) {
    return (
      <div className="card shadow-sm mt-2" style={{ 
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
        borderColor: theme === 'dark' ? '#2c3651' : '#e6e6e6',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div className="seismos-logo" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "85px" }}>
          <img src={seismos} alt="Seismos Logo" height={75} width={325} decoding="sync" loading="eager" style={{ paddingTop: "10px", display: "block" }}/>
        </div>
        <div className="card-body py-2 px-3 text-center">
          <h5 className="card-title mb-1" style={{ 
            color: theme === 'dark' ? '#e5e5e5' : '#333333',
            textAlign: 'center',
            width: '100%'
          }}>
            <ScorecardHeader uiValue="Loading..." onOpen={onOpenScorecard} />
          </h5>
          <p style={{ color: theme === 'dark' ? '#303335' : '#6c757d' }}>Loading seismos data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm mt-2" style={{ 
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
        borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div className="seismos-logo" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "85px" }}>
          <img src={seismos} alt="Seismos Logo" height={75} width={325} decoding="sync" loading="eager" style={{ paddingTop: "10px", display: "block" }}/>
        </div>
        <div className="card-body py-2 px-3 text-center">
          <h5 className="card-title mb-1" style={{ 
            color: theme === 'dark' ? '#e5e5e5' : '#333333',
            textAlign: 'center',
            width: '100%'
          }}>
            <ScorecardHeader uiValue={<span style={{ color: '#FF5F4D' }}>{error}</span>} onOpen={onOpenScorecard} onOpenPrc={onOpenPrcPlot} />
          </h5>
          {refreshRow}
        </div>
      </div>
    );
  }

  if (!seismosData || !seismosData.metrics || seismosData.metrics.length === 0) {
    return (
      <div className="card shadow-sm mt-2" style={{ 
        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
        borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div className="seismos-logo" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "85px" }}>
          <img src={seismos} alt="Seismos Logo" height={75} width={325} decoding="sync" loading="eager" style={{ paddingTop: "10px", display: "block" }}/>
        </div>
        <div className="card-body py-2 px-3 text-center">
          <h5 className="card-title mb-1" style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333', textAlign: 'center', width: '100%' }}>
            <ScorecardHeader uiValue={seismosData?.uniformity_index || 'N/A'} onOpen={onOpenScorecard} onOpenPrc={onOpenPrcPlot} />
          </h5>
          {refreshRow}
          {(!seismosFdiData || !seismosFdiData.metrics || seismosFdiData.metrics.length === 0) && (
            <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>No Seismos FDI event data available for this stage</p>
          )}
          {seismosFdiData && seismosFdiData.metrics && seismosFdiData.metrics.length > 0 && (
            <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>No Seismos rate drop data available for this stage</p>
          )}
        </div>
        {seismosFdiData && seismosFdiData.metrics && seismosFdiData.metrics.length > 0 && (() => {
          const sorted = [...seismosFdiData.metrics].sort((a, b) => a.start_ts - b.start_ts);
          const fmt = (val: number | null, decimals = 2): string => val != null ? val.toFixed(decimals) : '—';
          const formatTs = (epochMs: number): string =>
            new Date(epochMs).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
          return (
            <div className="card-body py-2 px-3" style={{ borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}` }}>
              <h6 style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>FDI Events</h6>
              <div className="table-responsive">
                <table className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`} style={{ lineHeight: '1.5', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      {sorted.map((m, i) => (
                        <th key={m.id}>
                          Event {i + 1}
                          {m.parent_well_name && <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>P: {m.parent_well_name}</div>}
                          {m.child_well_name  && <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>C: {m.child_well_name}</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Start Time</td>{sorted.map(m => <td key={m.id}>{formatTs(m.start_ts)}</td>)}</tr>
                    <tr><td>End Time</td>{sorted.map(m => <td key={m.id}>{formatTs(m.end_ts)}</td>)}</tr>
                    <tr><td>FDI Magnitude</td>{sorted.map(m => <td key={m.id}>{fmt(m.fdi_magnitude)}</td>)}</tr>
                    <tr><td>Max FDI Slope</td>{sorted.map(m => <td key={m.id}>{fmt(m.max_fdi_slope)}</td>)}</tr>
                    <tr><td>VFR</td>{sorted.map(m => <td key={m.id}>{fmt(m.vfr)}</td>)}</tr>
                    <tr><td>Est. Parent-Child Dist.</td>{sorted.map(m => <td key={m.id}>{fmt(m.estimated_parent_child_distance)}</td>)}</tr>
                    <tr><td>Est. FDI Velocity</td>{sorted.map(m => <td key={m.id}>{fmt(m.estimated_fdi_velocity)}</td>)}</tr>
                    <tr><td>Event Received</td>{sorted.map(m => <td key={m.id}>{m.event_received ? formatTs(m.event_received) : '—'}</td>)}</tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div className="card shadow-sm mt-2" style={{ 
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
      borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
      borderRadius: '8px',
      overflow: 'hidden'
    }}>
      <div className="seismos-logo" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", minHeight: "85px" }}>
        <img src={seismos} alt="Seismos Logo" height={75} width={325} decoding="sync" loading="eager" style={{ paddingTop: "10px", display: "block" }}/>
      </div>
      <div className="card-body py-2 px-3">
        <h5 className="card-title mb-1" style={{ 
          color: theme === 'dark' ? '#e5e5e5' : '#333333',
          textAlign: 'center',
          width: '100%'
        }}>
          <ScorecardHeader uiValue={seismosData.uniformity_index || 'N/A'} onOpen={onOpenScorecard} onOpenPrc={onOpenPrcPlot} />
        </h5>
        {refreshRow}
        <div className="table-responsive" id="seismos-prc-table">
          <table className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`} style={{ lineHeight: '1.5', fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th>Parameter</th>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <th key={metric.id}>Drop {index + 1}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Event Time</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <td key={metric.id}>
                      {new Date(metric.event_time).toLocaleString()}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Intra-Stage UI</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <td key={metric.id}>
                      {metric.intra_stage_uniformity_index != null ? metric.intra_stage_uniformity_index.toFixed(3) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Perf Efficiency (%)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <td key={metric.id}>
                      {metric.eff != null ? (metric.eff * 100).toFixed(1) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Pipe Friction (psi)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <td key={metric.id}>
                      {metric.pipe_frict != null ? metric.pipe_frict.toFixed(1) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Perf Friction (psi)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <td key={metric.id}>
                      {metric.perf_frict != null ? metric.perf_frict.toFixed(1) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Hydraulic Diameter (in)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric, index) => (
                    <td key={metric.id}>
                      {metric.ehd != null ? metric.ehd.toFixed(2) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Initial Rate (bpm)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <td key={metric.id}>
                      {metric.initial_rate != null ? metric.initial_rate.toFixed(1) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Rate Change (bpm)</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <td key={metric.id}>
                      {metric.rate_change != null ? metric.rate_change.toFixed(1) : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Status</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <td key={metric.id}>
                      {metric.processing_status ? metric.processing_status : '—'}
                    </td>
                  ))}
              </tr>
              <tr>
                <td>Rate Drop Issues</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <SeismosIssueCell key={metric.id} raw={metric.annotations} theme={theme} />
                  ))}
              </tr>
              <tr>
                <td>Well Issues</td>
                {seismosData.metrics
                  .sort((a, b) => new Date(a.event_time).getTime() - new Date(b.event_time).getTime())
                  .map((metric) => (
                    <SeismosIssueCell key={metric.id} raw={metric.issues} theme={theme} />
                  ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {seismosFdiData && seismosFdiData.metrics && seismosFdiData.metrics.length > 0 && (() => {
        const sorted = [...seismosFdiData.metrics].sort((a, b) => a.start_ts - b.start_ts);
        const fmt = (val: number | null, decimals = 2): string =>
          val != null ? val.toFixed(decimals) : '—';
        const formatTs = (epochMs: number): string =>
          new Date(epochMs).toLocaleString(undefined, {
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
          });
        return (
          <div className="card-body py-2 px-3" style={{
            borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`
          }}>
            <h6 style={{
              color: theme === 'dark' ? '#e5e5e5' : '#333333',
              fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem'
            }}>
              FDI Events
            </h6>
            <div className="table-responsive">
              <table
                className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`}
                style={{ lineHeight: '1.5', fontSize: '0.85rem' }}
              >
                <thead>
                  <tr>
                    <th>Parameter</th>
                    {sorted.map((m, i) => (
                      <th key={m.id}>
                        Event {i + 1}
                        {m.parent_well_name && (
                          <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>
                            P: {m.parent_well_name}
                          </div>
                        )}
                        {m.child_well_name && (
                          <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>
                            C: {m.child_well_name}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Start Time</td>{sorted.map(m => <td key={m.id}>{formatTs(m.start_ts)}</td>)}</tr>
                  <tr><td>End Time</td>{sorted.map(m => <td key={m.id}>{formatTs(m.end_ts)}</td>)}</tr>
                  <tr><td>FDI Magnitude</td>{sorted.map(m => <td key={m.id}>{fmt(m.fdi_magnitude)}</td>)}</tr>
                  <tr><td>Max FDI Slope</td>{sorted.map(m => <td key={m.id}>{fmt(m.max_fdi_slope)}</td>)}</tr>
                  <tr><td>VFR</td>{sorted.map(m => <td key={m.id}>{fmt(m.vfr)}</td>)}</tr>
                  <tr><td>Est. Parent-Child Dist.</td>{sorted.map(m => <td key={m.id}>{fmt(m.estimated_parent_child_distance)}</td>)}</tr>
                  <tr><td>Est. FDI Velocity</td>{sorted.map(m => <td key={m.id}>{fmt(m.estimated_fdi_velocity)}</td>)}</tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const MomentumFdiComponent: React.FC<MomentumFdiComponentProps> = ({ stageId, theme, styles }) => {
  const [momentumFdiData, setMomentumFdiData]     = useState<MomentumFdiDataResponse | null>(null);
  const [loading, setLoading]     = useState<boolean>(true);
  const [error, setError]         = useState<string | null>(null);
  const backendUrl                = process.env.REACT_APP_BACKEND_URL;
 
  const formatTs = (epochMs: number): string =>
    new Date(epochMs).toLocaleString(undefined, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      month:    'short',
      day:      'numeric',
      hour:     '2-digit',
      minute:   '2-digit',
      second:   '2-digit',
    });
 
  const fetchFdiData = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) { setError('Authentication token not found'); return; }
 
      const response = await axios.get(
        `${backendUrl}/momentum_fdi_data/stage/${stageId}/metrics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMomentumFdiData(response.data);
    } catch (err) {
      console.error('Error fetching Momentum FDI data:', err);
      setError('Data unavailable');
    } finally {
      setLoading(false);
    }
  };
 
  useEffect(() => { fetchFdiData(); }, [stageId]);
 
  // Poll every 30 seconds — same interval as SeismosDataComponent
  useEffect(() => {
    const interval = setInterval(fetchFdiData, 30000);
    return () => clearInterval(interval);
  }, [stageId]);
 
  // ── Shared card wrapper ───────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
    borderColor:     theme === 'dark' ? '#5C646B' : '#e6e6e6',
    borderRadius:    '8px',
    overflow:        'hidden',
  };
 
  // ── Header bar — mirrors Seismos logo strip ───────────────────────────────
  const Header = () => (
    <div style={{
      display:         'flex',
      justifyContent:  'center',
      alignItems:      'center',
      width:           '100%',
      minHeight:       '85px',
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
      padding:         '10px 16px',
    }}>
      <img
        src={theme === 'dark' ? momentumDark : momentumLight}
        alt="Momentum Logo"
        height={75}
        style={{ display: 'block', objectFit: 'contain' }}
      />
    </div>
  );
 
  if (loading && !momentumFdiData) {
    return (
      <div className="card shadow-sm mt-2" style={cardStyle}>
        <Header />
        <div className="card-body py-2 px-3 text-center">
          <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d', margin: 0 }}>
            Loading FDI data...
          </p>
        </div>
      </div>
    );
  }
 
  if (error) {
    return (
      <div className="card shadow-sm mt-2" style={cardStyle}>
        <Header />
        <div className="card-body py-2 px-3 text-center">
          <p style={{ color: '#FF5F4D', margin: 0 }}>{error}</p>
        </div>
      </div>
    );
  }
 
  if (!momentumFdiData || !momentumFdiData.metrics || momentumFdiData.metrics.length === 0) {
    return (
      <div className="card shadow-sm mt-2" style={cardStyle}>
        <Header />
        <div className="card-body py-2 px-3 text-center">
          <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d', margin: 0 }}>
            No FDI events detected for this stage
          </p>
        </div>
      </div>
    );
  }
 
  const sorted = [...momentumFdiData.metrics].sort((a, b) => a.start_ts - b.start_ts);
 
  const fmt = (val: number | null, decimals = 2): string =>
    val != null ? val.toFixed(decimals) : '—';
 
  return (
    <div className="card shadow-sm mt-2" style={cardStyle}>
      <Header />
      <div className="card-body py-2 px-3">
        <div className="table-responsive">
          <table
            className={`table ${theme === 'dark' ? 'table-dark' : 'table-light'} ${styles.treatmentTable}`}
            style={{ lineHeight: '1.5', fontSize: '0.9rem' }}
          >
            <thead>
              <tr>
                <th>Parameter</th>
                {sorted.map((metric, index) => (
                  <th key={metric.id}>
                    Event {index + 1}
                    {metric.parent_well_name && (
                      <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>
                        P: {metric.parent_well_name}
                      </div>
                    )}
                    {metric.child_well_name && (
                      <div style={{ fontWeight: 400, fontSize: '0.73rem', color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>
                        C: {metric.child_well_name}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Start Time</td>
                {sorted.map(m => <td key={m.id}>{formatTs(m.start_ts)}</td>)}
              </tr>
              <tr>
                <td>End Time</td>
                {sorted.map(m => <td key={m.id}>{formatTs(m.end_ts)}</td>)}
              </tr>
              <tr>
                <td>FDI Magnitude (psi)</td>
                {sorted.map(m => <td key={m.id}>{fmt(m.fdi_magnitude)}</td>)}
              </tr>
              <tr>
                <td>Max FDI Slope</td>
                {sorted.map(m => <td key={m.id}>{fmt(m.max_fdi_slope)}</td>)}
              </tr>
              <tr>
                <td>VFR</td>
                {sorted.map(m => <td key={m.id}>{fmt(m.vfr)}</td>)}
              </tr>
              <tr>
                <td>Est. Parent-Child Dist. (ft)</td>
                {sorted.map(m => <td key={m.id}>{fmt(m.estimated_parent_child_distance)}</td>)}
              </tr>
              <tr>
                <td>Est. FDI Velocity (ft/min)</td>
                {sorted.map(m => <td key={m.id}>{fmt(m.estimated_fdi_velocity)}</td>)}
              </tr>
              <tr>
                <td>Event Received</td>
                {sorted.map(m => <td key={m.id}>{m.event_received ? formatTs(m.event_received) : '—'}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const TreatmentDataGraph: React.FC<TreatmentDataProps> = ({ padId: propPadId }) => {
  // Get padId from URL parameters
  const { padId: urlPadId } = useParams<{ padId: string }>();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [wells, setWells] = useState<Well[]>([]);
  const [padName, setPadName] = useState<string>('');
  const selectedPadId: number | undefined = propPadId ?? (urlPadId ? parseInt(urlPadId, 10) : undefined);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [pendingRangeLoad, setPendingRangeLoad] = useState(false);
  const [isStageView, setIsStageView] = useState(false);
  const chartRef = useRef<any>(null);
  const { theme } = useTheme();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Template and channel selection state
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<ChannelOption[]>([]);
  const [axisConfig, setAxisConfig] = useState<{
    [channel: string]: { axis: string; min: number | undefined; max: number | undefined };
  }>({
    'treat_press': { axis: 'left1', min: undefined, max: undefined },
    'clean_rate': { axis: 'left2', min: undefined, max: undefined },
    'slurry_rate': { axis: 'left2', min: undefined, max: undefined },
    'stage_denso': { axis: 'right1', min: undefined, max: undefined },
    'seismos_metric_eff': { axis: 'right2', min: undefined, max: undefined }
  });
  // New axis-first configuration
  const [axisSettings, setAxisSettings] = useState<{
    [axis: string]: { 
      channels: string[]; 
      min: number | undefined; 
      max: number | undefined;
    };
  }>({
    left1: { channels: ['treat_press'], min: undefined, max: undefined },
    left2: { channels: ['clean_rate', 'slurry_rate'], min: undefined, max: undefined },
    right1: { channels: ['stage_denso'], min: undefined, max: undefined },
    right2: { channels: ['seismos_metric_eff'], min: undefined, max: undefined }
  });
  const [templateName, setTemplateName] = useState<string>('');
  
  // Modals
  const [showManageTemplates, setShowManageTemplates] = useState<boolean>(false);
  const [showAxisConfig, setShowAxisConfig] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [showTreatmentModal, setShowTreatmentModal] = useState<boolean>(false);
  const [selectedStage, setSelectedStage] = useState<SelectedStage | null>(null);
  const [showViewTreatmentButton, setShowViewTreatmentButton] = useState<boolean>(false);
  const [viewTreatmentButtonPosition, setViewTreatmentButtonPosition] = useState<{x: number, y: number}>({x: 0, y: 0});
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickStage, setLastClickStage] = useState<SelectedStage | null>(null);
  const [isRealTimeStreaming, setIsRealTimeStreaming] = useState<boolean>(false);
  const [streamingError, setStreamingError] = useState<string | null>(null);
  const [stageState, setStageState] = useState<string | null>(null);
  const [currentWell, setCurrentWell] = useState<Well | null>(null);
  const [currentStage, setCurrentStage] = useState<Stage | null>(null);
  const [treatmentSchedule, setTreatmentSchedule] = useState<TreatmentStep[]>([]);
  const [currentStep, setCurrentStep] = useState<number | null>(null);
  const [realTimeData, setRealTimeData] = useState<any>(null);
  const [realTimeChemData, setRealTimeChemData] = useState<any[]>([]);
  const clientRef = useRef<mqtt.MqttClient | null>(null);
  // Stage number currently being streamed (drives new-stage detection across the state topic)
  const streamingStageRef = useRef<number | null>(null);
  // Van + well api the stream is currently bound to (the state topic is the
  // source of truth: whatever is executing on the pad is what we stream)
  const streamingVanRef = useRef<string | null>(null);
  const streamingApiRef = useRef<string | null>(null);
  // Mirror of stageState for the MQTT handler closure (state reads are stale there)
  const stageStateRef = useRef<string | null>(null);
  // Normalized well apis belonging to OUR pad. The broker carries every van in
  // the fleet, so the state wildcard delivers other pads' messages (and test
  // rigs) - anything outside this set must be ignored.
  const padWellApisRef = useRef<Set<string>>(new Set());

  // Live telemetry key → { friendlyName, uom } from {van}/scoreboard/metadata
  const scoreboardMetaRef = useRef<Record<string, { friendlyName: string; uom?: string }>>({});

  // A stage is editable only while actively executing (streaming + state "executing").
  // A completed/held or non-streaming stage is read-only.
  const canEditStage = stageState === 'executing';

  // If the stage stops actively executing while an edit is open, force-exit the edit so
  // no one can save changes against a completed/paused stage. Functional updates return
  // the same reference when already empty to avoid re-render loops.
  useEffect(() => {
    if (canEditStage) return;
    if (hasEditLock && currentStage) releaseEditLock(currentStage.id);
    if (hasChemEditLock) releaseChemEditLock();
    setEditedTreatmentSteps(prev => (Object.keys(prev).length ? {} : prev));
    setEditedChemLoadings(prev => (Object.keys(prev).length ? {} : prev));
    setEditedChemGroupRatios(prev => (Object.keys(prev).length ? {} : prev));
    setIsEditingRatios(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditStage]);
  const [subscribedTopics, setSubscribedTopics] = useState<Set<string>>(new Set());
  const [editedTreatmentSteps, setEditedTreatmentSteps] = useState<{[stepId: number]: {type?: string, description?: string, rate?: number, conc?: number, clean_step?: number, sand_step?: number, start_conc?: number, end_conc?: number}}>({});
  const [editBySand, setEditBySand] = useState(false);
  // true = Petrix 8.0 (chemicals array present in schedule topic) — enables direct chem schedule save
  // false = Petrix 7.0.1 (no chemicals array) — falls back to posting a plain message
  const [petrixHasChemSupport, setPetrixHasChemSupport] = useState<boolean>(true);
  const [chartSeries, setChartSeries] = useState<Highcharts.SeriesOptionsType[]>([]);
  const [plotBands, setPlotBands] = useState<Highcharts.XAxisPlotBandsOptions[]>([]);
  const [plotLines, setPlotLines] = useState<Highcharts.XAxisPlotLinesOptions[]>([]);
  const [allPlotBands, setAllPlotBands] = useState<Highcharts.XAxisPlotBandsOptions[]>([]);
  const [allPlotLines, setAllPlotLines] = useState<Highcharts.XAxisPlotLinesOptions[]>([]);
  const [xAxisRange, setXAxisRange] = useState<{min?: number, max?: number}>({});
  const [chartTitle, setChartTitle] = useState<string>('Treatment Data');
  const lockHeartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lockLostFiredRef = useRef<boolean>(false);
  const [hasEditLock, setHasEditLock] = useState<boolean>(false);
  const [isEditingRatios, setIsEditingRatios] = useState<boolean>(false);
  const [editedRatios, setEditedRatios] = useState<{[chemName: string]: number}>({});
  const [refreshPresent, setRefreshPresent] = useState<boolean>(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState<boolean>(false);
  // ── Chemical schedule state for Next Steps card ──────────────────────────────
  const [nextStepsSubTab, setNextStepsSubTab]         = useState<'treatment' | 'chemicals'>('treatment');
  const [nextChemSchedule, setNextChemSchedule]       = useState<any | null>(null);
  const [nextChemLoading, setNextChemLoading]         = useState<boolean>(false);
  const [editedChemLoadings, setEditedChemLoadings]   = useState<Record<string, number>>({});
  const [hasChemEditLock, setHasChemEditLock]         = useState<boolean>(false);
  const [isSavingChem, setIsSavingChem]               = useState<boolean>(false);
  const [chemicalChanges, setChemicalChanges]         = useState<{creator: string, timestamp: string | number, comment: string}[]>([]);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  // Raw chemicals per step from the MQTT schedule topic — source of truth for group data
  const [rawScheduleChemicals, setRawScheduleChemicals] = useState<Record<number, {name: string, group: string, ratio: number}[]>>({});
  // Per-group ratio edits for multi-group chemicals, keyed by "step::chemName"
  const [editedChemGroupRatios, setEditedChemGroupRatios] = useState<Record<string, {group: string, ratio: number}[]>>({});
  // Group ratio modal state
  const [chemGroupModal, setChemGroupModal] = useState<{step: number, chemName: string, groups: {group: string, ratio: number}[]} | null>(null);
  
  // Change tracking state
  const [originalTreatmentSchedule, setOriginalTreatmentSchedule] = useState<TreatmentStep[]>([]);
  const [treatmentChanges, setTreatmentChanges] = useState<{creator: string, timestamp: string, comment: string}[]>([]);
  const [showScorecardModal, setShowScorecardModal] = useState(false);
  const [scorecardLevel,     setScorecardLevel]     = useState<ScorecardLevel>('stage');
  const [scorecardView,      setScorecardView]      = useState<'events' | 'rates'>('events');
  const [scorecardStageId,   setScorecardStageId]   = useState<number | null>(null);
  const [scorecardWellId,    setScorecardWellId]     = useState<number | null>(null);
  const [seismosWells,  setSeismosWells]  = useState<{ id: number; well_api: string; well_name: string }[]>([]);
  const [liveSeismosMetrics, setLiveSeismosMetrics] = useState<any[]>([]);
  const [momentumWells, setMomentumWells] = useState<{ id: number; well_api: string; well_name: string }[]>([]);
  const roles: string[] = JSON.parse(localStorage.getItem("roles") || "[]");
  const isViewer = roles.includes("Viewer");

  // Fetch treatment changes from backend
  const fetchTreatmentChanges = async (stage_id) => {
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${backendUrl}/stages/${stage_id}/treatment_changes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTreatmentChanges(response.data);
    } catch (error) {
      console.error('Error fetching treatment changes:', error);
    }
  };

  // Helper function to generate change descriptions
  const generateChangeDescription = (currentTreatments: TreatmentStep[], originalTreatments: TreatmentStep[]): string => {
    // Collect per-step field changes first, then collapse consecutive steps with
    // identical change sets into a range (e.g. cascading rate across steps 4-8
    // reads as "Steps 4-8: Rate: 80 -> 90" instead of five separate lines).
    const perStepChanges: { step: number; parts: string[] }[] = [];

    // Track field changes for existing steps - only show changes for steps that were actually edited
    currentTreatments.forEach(current => {
      const original = originalTreatments.find(orig => orig.id === current.id);

      // Only process if this step exists in editedTreatmentSteps AND it's an existing step
      if (original && editedTreatmentSteps[current.id]) {
        const editedStep = editedTreatmentSteps[current.id];
        const fieldChanges: string[] = [];
        
        // Check each field for changes, but only if it was actually edited
        if (editedStep.type !== undefined && current.type !== original.type) {
          fieldChanges.push(`Type: ${original.type} -> ${current.type}`);
        }
        if (editedStep.description !== undefined && current.description !== original.description) {
          fieldChanges.push(`Description: ${original.description} -> ${current.description}`);
        }
        if (editedStep.rate !== undefined && current.rate !== original.rate) {
          fieldChanges.push(`Rate: ${original.rate} -> ${current.rate}`);
        }
        
        // Handle concentration changes
        const hasStartEndConc = editedStep.start_conc !== undefined || editedStep.end_conc !== undefined;
        
        if (hasStartEndConc) {
          // Sand Ramp / Rate Drop-Sand Ramp step with separate start/end concentrations
          if (editedStep.start_conc !== undefined) {
            const originalStartConc = (original as any).start_conc ?? original.conc;
            if (editedStep.start_conc !== originalStartConc) {
              fieldChanges.push(`Target Start Conc: ${originalStartConc} -> ${editedStep.start_conc}`);
            }
          }
          if (editedStep.end_conc !== undefined) {
            const originalEndConc = (original as any).end_conc ?? original.conc;
            if (editedStep.end_conc !== originalEndConc) {
              fieldChanges.push(`Target End Conc: ${originalEndConc} -> ${editedStep.end_conc}`);
            }
          }
        } else if (editedStep.conc !== undefined && current.conc !== original.conc) {
          // Regular step with single concentration (show as both start and end)
          fieldChanges.push(`Target Start Conc: ${original.conc} -> ${current.conc}`);
          fieldChanges.push(`Target End Conc: ${original.conc} -> ${current.conc}`);
        }
        
        if (editedStep.clean_step !== undefined && current.clean_step !== original.clean_step) {
          fieldChanges.push(`Clean Step: ${original.clean_step} -> ${current.clean_step}`);
        }
        
        // In editBySand mode only sand_step is written to editedTreatmentSteps — clean_step is
        // never set — so we must detect the change via sand_step and report it as Sand Step.
        if (editedStep.sand_step !== undefined && editedStep.clean_step === undefined) {
          const originalSand = Math.round((original.clean_step || 0) * 42 * ((original.conc) || 0));
          const newSand      = Math.round(editedStep.sand_step as number);
          if (newSand !== originalSand) {
            fieldChanges.push(`Sand Step: ${originalSand} -> ${newSand}`);
          }
        }
        
        if (fieldChanges.length > 0) {
          perStepChanges.push({ step: current.step, parts: fieldChanges });
        }
      }
    });

    perStepChanges.sort((a, b) => a.step - b.step);

    const groups: { start: number; end: number; parts: string[] }[] = [];
    for (const entry of perStepChanges) {
      const last = groups[groups.length - 1];
      const sig = [...entry.parts].sort().join('|');
      const lastSig = last ? [...last.parts].sort().join('|') : '';
      if (last && last.end + 1 === entry.step && sig === lastSig) {
        last.end = entry.step;
      } else {
        groups.push({ start: entry.step, end: entry.step, parts: entry.parts });
      }
    }

    return groups
      .map(g => {
        const label = g.start === g.end ? `Step ${g.start}` : `Steps ${g.start}-${g.end}`;
        return `${label}: ${g.parts.join(', ')}`;
      })
      .join(', ');
  };

  // Lock mechanism functions
  const fetchChemicalChanges = async (stageId: number | null | undefined) => {
    if (!stageId) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const resp = await axios.get(`${backendUrl}/stages/${stageId}/chemical_changes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChemicalChanges(resp.data);
    } catch (err) {
      console.error('Error fetching chemical changes:', err);
    }
  };

  const requestEditLock = async (stageId: number): Promise<boolean> => {
    try {
      // Check if treatment schedule is loaded before allowing edit
      if (treatmentSchedule.length === 0) {
        toast.error('Please wait for the treatment schedule to load before editing');
        return false;
      }

      // Check if current step has been determined from real-time data
      if (currentStep === null) {
        toast.error('Please wait for the current step to be determined before editing');
        return false;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found');
        return false;
      }

      // Extract the van_id from the MQTT subscription topic or use fallback
      let vanId = "";
      if (subscribedTopics.size > 0) {
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) {
            const parts = topic.split('/');
            if (parts.length >= 3) {
              vanId = parts[1];
              break;
            }
          }
        }
      }
      
      if (!vanId) {
        vanId = localStorage.getItem('currentVanId') || "DV1017";
      }

      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/request_edit_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.lockAcquired) {
        setHasEditLock(true);
        // Store original schedule when editing starts
        setOriginalTreatmentSchedule([...treatmentSchedule]);
        // Start heartbeat to keep lock alive
        startLockHeartbeat(stageId);
        toast.success(response.data.message || "Edit lock acquired");
        return true;
      } else {
        toast.error(response.data.message || "Could not acquire edit lock");
        return false;
      }
    } catch (error) {
      console.error('Error requesting edit lock:', error);
      toast.error('Failed to request edit lock');
      return false;
    }
  };

  const releaseEditLock = async (stageId: number): Promise<void> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Extract the van_id from the MQTT subscription topic or use fallback
      let vanId = "";
      if (subscribedTopics.size > 0) {
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) {
            const parts = topic.split('/');
            if (parts.length >= 3) {
              vanId = parts[1];
              break;
            }
          }
        }
      }
      
      if (!vanId) {
        vanId = localStorage.getItem('currentVanId') || "DV1017";
      }

      // Treatment and chem share one broker lock. Only call release_lock when the
      // OTHER lock is not also held — otherwise just clear the treatment UI state
      // and let the remaining lock (and its heartbeat) keep the broker lock alive.
      if (!hasChemEditLock) {
        await axios.post(
          `${backendUrl}/stages/mqtt/${stageId}/release_lock`,
          { vanId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        stopLockHeartbeat();
      }

      setHasEditLock(false);
      setEditedTreatmentSteps({});  // Discard any unsaved edits
      setEditBySand(false);         // Reset toggle back to "Editing by Clean Step"
      toast.success("Edit lock released");
    } catch (error) {
      console.error('Error releasing edit lock:', error);
      toast.error('Failed to release edit lock');
    }
  };

  const startLockHeartbeat = (stageId: number) => {
    // Clear any existing heartbeat — ref is always current, no stale closure
    if (lockHeartbeatIntervalRef.current) {
      clearInterval(lockHeartbeatIntervalRef.current);
      lockHeartbeatIntervalRef.current = null;
    }
    lockLostFiredRef.current = false;

    // Start new heartbeat every 15 seconds.
    // Treatment and chem share the same broker lock, so the heartbeat fires
    // as long as EITHER lock is held by this user.
    const interval = setInterval(async () => {
      const eitherLockHeld = hasEditLock || hasChemEditLock;
      if (eitherLockHeld && stageId) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setHasEditLock(false);
            stopLockHeartbeat();
            return;
          }

          // Extract the van_id from the MQTT subscription topic or use fallback
          let vanId = "";
          if (subscribedTopics.size > 0) {
            for (const topic of Array.from(subscribedTopics)) {
              if (topic.includes('/petrix/data')) {
                const parts = topic.split('/');
                if (parts.length >= 3) {
                  vanId = parts[1];
                  break;
                }
              }
            }
          }
          
          if (!vanId) {
            vanId = localStorage.getItem('currentVanId') || "DV1017";
          }

          // Send heartbeat to maintain lock (not acquire new one)
          const response = await axios.post(
            `${backendUrl}/stages/mqtt/${stageId}/heartbeat_lock`,
            { vanId },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!response.data.lockMaintained) {
            // Lock was lost — clear both treatment and chem lock state
            setHasEditLock(false);
            setHasChemEditLock(false);
            setEditedTreatmentSteps({});
            setEditBySand(false);
            setEditedChemLoadings({});
            stopLockHeartbeat();
            if (!lockLostFiredRef.current) {
              lockLostFiredRef.current = true;
              toast.error("Edit lock was lost");
            }
          }
        } catch (error) {
          console.error('Error sending lock heartbeat:', error);
          setHasEditLock(false);
          setHasChemEditLock(false);
          setEditedTreatmentSteps({});
          setEditBySand(false);
          setEditedChemLoadings({});
          stopLockHeartbeat();
        }
      } else {
        // Neither lock is held — stop heartbeat
        stopLockHeartbeat();
      }
    }, 15000);

    lockHeartbeatIntervalRef.current = interval;
  };

  const stopLockHeartbeat = () => {
    if (lockHeartbeatIntervalRef.current) {
      clearInterval(lockHeartbeatIntervalRef.current);
      lockHeartbeatIntervalRef.current = null;
    }
  };

  // Handle saving ratio changes
  const handleSaveRatios = async () => {
    try {
      // Get the van_id from localStorage or MQTT topics
      let vanId = localStorage.getItem('currentVanId') || "";
      
      if (!vanId && subscribedTopics.size > 0) {
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) {
            const parts = topic.split('/');
            if (parts.length >= 3) {
              vanId = parts[1];
              break;
            }
          }
        }
      }
      
      if (!vanId) {
        toast.error("Could not determine van ID for ratio update");
        return;
      }

      // Create the message with changed ratios
      const changedChems = Object.keys(editedRatios);
      if (changedChems.length === 0) {
        toast.info("No ratio changes detected");
        setIsEditingRatios(false);
        return;
      }

      const message = changedChems.map(chem => 
        `Please update ${chem} Ratio to ${editedRatios[chem]}`
      ).join('\n');

      // Send the message to the MQTT endpoint
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found');
        return;
      }

      const response = await axios.post(
        `${backendUrl}/mqtt/${vanId}/post_message`,
        { message },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data === true) {
        toast.success("Ratio update message sent successfully");
        setEditedRatios({});
        setIsEditingRatios(false);
      } else {
        toast.error("Failed to send ratio update message");
      }
    } catch (error) {
      console.error('Error sending ratio update:', error);
      toast.error("Failed to send ratio update message");
    }
  };

  // Clean up heartbeat on unmount
  useEffect(() => {
    return () => {
      stopLockHeartbeat();
    };
  }, []);


  // Concentration modal state
  const [showConcentrationModal, setShowConcentrationModal] = useState<boolean>(false);
  const [selectedStepForConcentration, setSelectedStepForConcentration] = useState<TreatmentStep | null>(null);

  // Handle concentration click for Sand Ramp types
  const handleConcentrationClick = (step: TreatmentStep) => {
    const currentType = editedTreatmentSteps[step.id]?.type !== undefined 
      ? editedTreatmentSteps[step.id].type 
      : step.type;
    
    if (currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp') {
      setSelectedStepForConcentration(step);
      setShowConcentrationModal(true);
    }
  };

  // Handle concentration modal save
  const handleConcentrationSave = (startConc: number, endConc: number) => {
    if (selectedStepForConcentration) {
      // Calculate the average of start and end concentrations
      const averageConc = (startConc + endConc) / 2;
      
      setEditedTreatmentSteps(prev => ({
        ...prev,
        [selectedStepForConcentration.id]: {
          ...prev[selectedStepForConcentration.id],
          start_conc: startConc,
          end_conc: endConc,
          conc: averageConc // Update the main conc field to the average
        }
      }));
    }
  };

  // ── Cascade helpers ──────────────────────────────────────────────
  // Copy a value from `fromStepNum` to that step plus every later step in
  // `treatmentSchedule`. The cascade button next to each edited numeric input
  // in the Next Steps tables uses these to bulk-apply a job-design change to
  // the remainder of the stage. Edits land in the same edited-state buckets
  // the Save button already reads, so a normal Save commits them all.
  const cascadeTreatmentField = (
    fromStepNum: number,
    field: 'rate' | 'clean_step' | 'sand_step' | 'conc',
    value: number,
  ) => {
    const future = treatmentSchedule.filter(s => s.step >= fromStepNum);
    if (future.length <= 1) return;
    setEditedTreatmentSteps(prev => {
      const next = { ...prev };
      future.forEach(s => {
        next[s.id] = { ...next[s.id], [field]: value };
      });
      return next;
    });
    toast.info(`Cascaded ${field.replace('_', ' ')} to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  // Sand Ramp conc is the average of start_conc/end_conc, so cascade all three
  // together to keep derived values consistent on later steps.
  const cascadeSandRampConc = (fromStepNum: number) => {
    const fromStep = treatmentSchedule.find(s => s.step === fromStepNum);
    if (!fromStep) return;
    const edited = editedTreatmentSteps[fromStep.id] || {};
    const start = edited.start_conc ?? (fromStep as any).start_conc ?? fromStep.conc;
    const end   = edited.end_conc   ?? (fromStep as any).end_conc   ?? fromStep.conc;
    const conc  = edited.conc       ?? fromStep.conc;
    const future = treatmentSchedule.filter(s => s.step >= fromStepNum);
    if (future.length <= 1) return;
    setEditedTreatmentSteps(prev => {
      const next = { ...prev };
      future.forEach(s => {
        next[s.id] = { ...next[s.id], start_conc: start, end_conc: end, conc };
      });
      return next;
    });
    toast.info(`Cascaded conc to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  const cascadeChemRatio = (fromStepNum: number, chemName: string) => {
    const future = treatmentSchedule.filter(s => s.step >= fromStepNum);
    if (future.length <= 1) return;
    const fromKey = `${fromStepNum}::${chemName}`;
    const editedGroups = editedChemGroupRatios[fromKey];
    const editedVal = editedChemLoadings[fromKey];
    if (editedGroups) {
      setEditedChemGroupRatios(prev => {
        const next = { ...prev };
        future.forEach(s => { next[`${s.step}::${chemName}`] = editedGroups; });
        return next;
      });
    } else if (editedVal !== undefined) {
      setEditedChemLoadings(prev => {
        const next = { ...prev };
        future.forEach(s => { next[`${s.step}::${chemName}`] = editedVal; });
        return next;
      });
    } else {
      return;
    }
    toast.info(`Cascaded ${chemName} ratio to ${future.length} steps`, {
      position: 'top-right', autoClose: 2000,
    });
  };

  // Small "C" button rendered next to an edited cell. Visibility is decided
  // by the caller — render only when an edit exists AND there are later
  // steps to cascade into.
  const renderCascadeBtn = (onClick: () => void, title = 'Cascade this value to current + all future steps') => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        marginLeft: '4px',
        width: '18px',
        height: '18px',
        borderRadius: '50%',
        border: `1px solid ${theme === 'dark' ? '#8CC540' : '#5a9620'}`,
        backgroundColor: theme === 'dark' ? '#2d4a18' : '#e8f5d8',
        color: theme === 'dark' ? '#a4d36a' : '#3d6611',
        fontSize: '0.7rem',
        fontWeight: 700,
        lineHeight: 1,
        padding: 0,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    >
      C
    </button>
  );

  // Helper function to get user-friendly name from channel name
  const getFriendlyChannelName = (channelName: string): string => {
    if (channelName.startsWith(SEISMOS_METRIC_PREFIX)) {
      const field = channelName.slice(SEISMOS_METRIC_PREFIX.length);
      return seismosMetricNameDictionary[field] || channelName;
    }
    return TREATMENT_CHANNEL_LABELS[channelName] || channelName;
  };

  function getYAxisIndex(axisName: string | undefined): number {
    const axisMap: { [key: string]: number } = {
      left1: 0,
      left2: 1,
      right1: 2,
      right2: 3,
    };
    return axisName && axisMap[axisName] !== undefined ? axisMap[axisName] : 0;
  }

  // Configure Y axes based on current axis configuration
  const configureYAxes = (): Highcharts.YAxisOptions[] => {
    console.log('configureYAxes called with:', { axisSettings, axisConfig });
    
    // Start with base axis configuration
    const axes = [
      { // left1 (index 0)
        title: { text: 'Left Axis 1', style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } },
        opposite: false,
        min: axisSettings.left1?.min ?? undefined,
        max: axisSettings.left1?.max ?? undefined,
        endOnTick: false,
        startOnTick: false,
        gridLineColor: theme === 'dark' ? '#484E54' : '#e6e6e6',
        labels: { style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } }
      },
      { // left2 (index 1)
        title: { text: 'Left Axis 2', style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } },
        opposite: false,
        min: axisSettings.left2?.min ?? undefined,
        max: axisSettings.left2?.max ?? undefined,
        endOnTick: false,
        startOnTick: false,
        gridLineColor: theme === 'dark' ? '#484E54' : '#e6e6e6',
        labels: { style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } }
      },
      { // right1 (index 2)
        title: { text: 'Right Axis 1', style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } },
        opposite: true,
        min: axisSettings.right1?.min ?? undefined,
        max: axisSettings.right1?.max ?? undefined,
        endOnTick: false,
        startOnTick: false,
        gridLineColor: theme === 'dark' ? '#484E54' : '#e6e6e6',
        labels: { style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } }
      },
      { // right2 (index 3)
        title: { text: 'Right Axis 2', style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } },
        opposite: true,
        min: axisSettings.right2?.min,
        max: axisSettings.right2?.max,
        endOnTick: false,
        startOnTick: false,
        gridLineColor: theme === 'dark' ? '#484E54' : '#e6e6e6',
        labels: { style: { color: theme === 'dark' ? '#e5e5e5' : '#333333' } }
      }
    ];

    // Update axis titles based on assigned channels
    const axisNames = ['left1', 'left2', 'right1', 'right2'];
    axisNames.forEach((axisName, index) => {
      const channelNames = axisSettings[axisName]?.channels?.map(ch => getFriendlyChannelName(ch)) || [];
      if (channelNames.length > 0) {
        axes[index].title.text = channelNames.join(', ');
      }
    });

    console.log('Returning axes:', axes);
    return axes;
  };

    // Handle chart click event to show "View Treatment" button or open treatment modal on double-click
  const handleChartClick = (event: any) => {
    // Hide the button first, but only if not in real-time streaming mode
    if (!isRealTimeStreaming) {
      setShowViewTreatmentButton(false);
    } else if (selectedStage) {
      // In real-time streaming mode, ensure the button stays visible if we have a selected stage
      setShowViewTreatmentButton(true);
    }
    
    console.log('Chart clicked:', event);
    
    // Get the x-coordinate (timestamp) of the click
    const xValue = event.xAxis && event.xAxis[0] ? event.xAxis[0].value : null;
    
    if (xValue) {
      console.log('Click timestamp:', new Date(xValue).toISOString());
      
      // Find which stage this timestamp falls into
      const clickedStage = stages.find(stage => {
        const startTs = stage.start_ts ? normalizeTimestamp(stage.start_ts) : null;
        const endTs = stage.end_ts ? normalizeTimestamp(stage.end_ts) : null;
        
        if (startTs && endTs) {
          console.log(`Stage ${stage.id} (${stage.stage_number}): ${new Date(startTs).toISOString()} - ${new Date(endTs).toISOString()}`);
          // Check if the click timestamp is within this stage's timeframe
          return xValue >= startTs && xValue <= endTs;
        }
        return false;
      });
      
      if (clickedStage) {
        console.log('Found clicked stage:', clickedStage);
        const well = wells.find(w => w.id === clickedStage.well_id);
        
        if (well) {
          console.log('Found well:', well);
          // Create the selected stage object
          const selectedStageObj = {
            stageId: clickedStage.id,
            wellId: well.id,
            stageNumber: clickedStage.stage_number
          };
          
          // Set the selected stage
          setSelectedStage(selectedStageObj);
          
          // Check if this is a double click (within 300ms of last click on the same stage)
          const now = Date.now();
          const isDoubleClick = now - lastClickTime < 300 && 
                               lastClickStage && 
                               lastClickStage.stageId === selectedStageObj.stageId;
          
          if (isDoubleClick) {
            // Double click - open the treatment modal directly
            setShowTreatmentModal(true);
            console.log('Double click detected - opening treatment modal');
          } else {
            // Single click - show the button
            // Set the position for the "View Treatment" button
            // Use the event coordinates
            setViewTreatmentButtonPosition({
              x: event.chartX || 450, // Default to center if chartX is not available
              y: event.chartY || 300  // Default to middle if chartY is not available
            });
            
            // Show the "View Treatment" button
            setShowViewTreatmentButton(true);
            
            console.log('Stage clicked:', clickedStage.id, 'Well:', well.id, 'Stage number:', clickedStage.stage_number);
            console.log('Button position:', viewTreatmentButtonPosition);
          }
          
          // Update last click time and stage for double-click detection
          setLastClickTime(now);
          setLastClickStage(selectedStageObj);
        }
      } else {
        console.log('No stage found at this timestamp');
      }
    } else {
      console.log('No valid x-value found in click event');
    }
  };

  const chartOptions = useMemo((): Highcharts.Options => {
    console.log('Computing chart options with current state');
    
    const isDarkTheme = theme === 'dark';
    
    // Define colors based on theme
    const backgroundColor = isDarkTheme ? '#303335' : '#ffffff';
    const textColor = isDarkTheme ? '#e5e5e5' : '#333333';
    const gridLineColor = isDarkTheme ? '#5C646B' : '#e6e6e6';
    
    // Series colors that work well in both themes
    const seriesColors = [
      '#4361ee', '#10b981', '#f72585', '#4cc9f0', '#f59e0b', '#7209b7'
    ];
    
    return {
      title: { 
        text: chartTitle,
        style: { color: textColor, fontWeight: '600' }
      },
      xAxis: { 
        type: 'datetime',
        title: { 
          text: 'Time',
          style: { color: textColor }
        },
        labels: { style: { color: textColor } },
        lineColor: gridLineColor,
        tickColor: gridLineColor,
        gridLineColor: gridLineColor,
        plotLines: plotLines,
        plotBands: plotBands,
        min: xAxisRange.min,
        max: xAxisRange.max,
        events: {
          afterSetExtremes: function(e: any) {
            // Only filter if we have all plotBands/plotLines and we're not in streaming mode
            if (allPlotBands.length === 0 || isRealTimeStreaming) return;
            
            const min = e.min;
            const max = e.max;
            
            // If min and max are undefined (reset zoom), show all bands and lines
            if (min === undefined || max === undefined) {
              setPlotBands(allPlotBands);
              setPlotLines(allPlotLines);
              return;
            }
            
            // Filter plotBands to only show those within the visible range
            const filteredBands = allPlotBands.filter((band: any) => {
              return band.to >= min && band.from <= max;
            });
            
            // Filter plotLines to only show those within the visible range
            const filteredLines = allPlotLines.filter((line: any) => {
              return line.value >= min && line.value <= max;
            });
            
            // Update the state with filtered bands and lines
            setPlotBands(filteredBands);
            setPlotLines(filteredLines);
          }
        }
      },
      yAxis: configureYAxes(), // Always uses current axisSettings
      series: chartSeries, // Always uses current series data
      credits: { enabled: false },
      tooltip: {
        shared: true,
        xDateFormat: '%Y-%m-%d %H:%M:%S',
        valueDecimals: 2,
        backgroundColor: isDarkTheme ? '#303335' : '#ffffff',
        borderColor: gridLineColor,
        style: { color: textColor }
      },
      chart: { 
        zoomType: 'x',
        height: 500,
        backgroundColor: backgroundColor,
        borderRadius: 8,
        style: { fontFamily: 'Inter, Space Grotesk, sans-serif' },
        events: {
          click: handleChartClick,
          plotBandClick: function(e: any) {
            console.log('Plot band clicked directly:', e);
            
            if (e.id && e.id.startsWith('stage-')) {
              const stageId = parseInt(e.id.replace('stage-', ''));
              const stage = stages.find(s => s.id === stageId);
              
              if (stage) {
                const well = wells.find(w => w.id === stage.well_id);
                
                if (well) {
                  setSelectedStage({
                    stageId: stage.id,
                    wellId: well.id,
                    stageNumber: stage.stage_number
                  });
                  
                  setViewTreatmentButtonPosition({
                    x: e.chartX || 450,
                    y: e.chartY || 300
                  });
                  
                  setShowViewTreatmentButton(true);
                }
              }
            }
          }
        }
      } as Highcharts.ChartOptions,
      legend: {
        enabled: true,
        align: 'center' as Highcharts.AlignValue,
        verticalAlign: 'top' as Highcharts.VerticalAlignValue,
        layout: 'horizontal',
        itemStyle: { color: textColor },
        itemHoverStyle: { color: seriesColors[0] }
      },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
              radius: 2,
              symbol: 'circle'
            },
            states: {
              hover: {
                enabled: false
              }
            },
            turboThreshold: 5000,
            dataGrouping: {
              enabled: true,
              approximation: 'average',
              groupPixelWidth: 2
            },
            lineWidth: 1.5
          }
        },
      boost: {
        enabled: true,
        seriesThreshold: 1,
        allowForce: true,
        debug: {
          timeRendering: false,
          timeSeriesProcessing: false,
          timeSetup: false
        }
      },
      colors: seriesColors
    };
  }, [
    theme, 
    axisSettings, 
    selectedChannels, 
    chartSeries, 
    plotBands, 
    plotLines, 
    xAxisRange, 
    chartTitle,
    stages,
    wells
  ]); 
  
  // Helper function to normalize timestamp (used in multiple places)
  // Database timestamps are always in epoch UTC milliseconds (e.g., "1751911357213")
  const normalizeTimestamp = (timestamp: number): number => {
    // Ensure timestamp is in milliseconds (database timestamps are already in ms)
    // But MQTT data might come in seconds, so we need to check
    const timestampInMs = ensureMs(timestamp);
    
    // Convert from UTC to local time
    const utcDate = new Date(timestampInMs);
    const timezoneOffsetMs = utcDate.getTimezoneOffset() * 60 * 1000;
    const localTimestamp = timestampInMs - timezoneOffsetMs;
    
    // Round to nearest second for consistent alignment between treatment and chemical data
    return Math.round(localTimestamp / 1000) * 1000;
  };

  // Handle "View Treatment" button click
  const handleViewTreatmentClick = async () => {
    if (selectedStage) {
      if (hasEditLock){
        await releaseEditLock(selectedStage.stageId);
      }
      setShowTreatmentModal(true);
      // Don't hide the button when real-time streaming is active
      if (!isRealTimeStreaming) {
        setShowViewTreatmentButton(false);
      }
    }
  };

  const refreshTreatmentSchedule = async (optimisticUpdate?: any[]) => {
    if (optimisticUpdate) {
      // Use the optimistic update immediately for instant UI feedback
      console.log('Applying optimistic update to treatment schedule:', optimisticUpdate);
      const sortedOptimisticUpdate = [...optimisticUpdate].sort((a, b) => a.step - b.step);
      setTreatmentSchedule(sortedOptimisticUpdate);
    }
    
    // For schedule updates, we need to refresh the treatment schedule if we have a current stage
    // This can happen both in real-time streaming mode and when viewing treatment modals
    const stageToRefresh = currentStage || selectedStage;
    if (!stageToRefresh) {
      console.log('No current stage or selected stage to refresh schedule for');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Small delay to allow Python container to process the MQTT message
      await new Promise(resolve => setTimeout(resolve, 1000));

      const stageId = currentStage ? currentStage.id : selectedStage?.stageId;
      if (!stageId) return;

      const scheduleResponse = await axios.get(`${backendUrl}/stages/${stageId}/actual_treatment_schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const scheduleData = Array.isArray(scheduleResponse.data) 
        ? scheduleResponse.data 
        : [scheduleResponse.data];
      
      const sortedSchedule = [...scheduleData].sort((a, b) => a.step - b.step);
      setTreatmentSchedule(sortedSchedule);
      
      console.log('Treatment schedule refreshed from database:', sortedSchedule);
    } catch (err) {
      console.error('Error refreshing treatment schedule:', err);
    }
  };

  useEffect(() => {
    if (chartRef.current?.chart) {
      const chart = chartRef.current.chart;
      
      // Update yAxis if needed
      const newYAxes = configureYAxes();
      newYAxes.forEach((axisOptions, index) => {
        if (chart.yAxis[index]) {
          chart.yAxis[index].update(axisOptions, false);
        }
      });
      
      chart.redraw();
    }
  }, [chartOptions]);

  // Clean up MQTT client on unmount
  useEffect(() => {
    console.log(7)
    return () => {
      if (clientRef.current) {
        clientRef.current.end();
      }
    };
  }, []);

  // Fetch pad information to get pad name
  const fetchPadInfo = async (padId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token not found');
        return null;
      }

      const response = await axios.get(`${backendUrl}/pads/${padId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching pad info:', error);
      return null;
    }
  };

  // Fetch chemical channels for a specific pad
  const fetchChemChannels = async (padId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token not found');
        return [];
      }

      const response = await axios.get(`${backendUrl}/pads/${padId}/chem_channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      return response.data || [];
    } catch (error) {
      console.error('Error fetching chemical channels:', error);
      return [];
    }
  };

  // 1. Initialize everything once on mount
  useEffect(() => {
    console.log(1)
    const channelOptions = TREATMENT_CHANNELS.map(ch => ({ 
      value: ch, 
      label: getFriendlyChannelName(ch) 
    }));
    
    const allInitialChannels = [...channelOptions, ...buildSeismosMetricChannels()];
    setChannels(allInitialChannels);
    
    // Set default selected channels immediately
    // NOTE: sourced from allInitialChannels (not channelOptions) so the seismos
    // metric default resolves with its seismos_metric marker intact.
    const DEFAULT_CHANNELS = [
      'treat_press', 'clean_rate', 'slurry_rate', 'stage_denso', 'seismos_metric_eff',
    ];
    const defaultSelected = allInitialChannels.filter(ch => 
      DEFAULT_CHANNELS.includes(ch.value)
    );
    
    setSelectedChannels(defaultSelected);
    fetchTemplates();
  }, []); // Run once on mount

  // 3. Fetch pad info and chemical channels when pad is selected
  useEffect(() => {
    if (selectedPadId) {
      // Fetch pad information to get pad name
      fetchPadInfo(selectedPadId).then(padInfo => {
        if (padInfo && padInfo.pad_name) {
          setPadName(padInfo.pad_name);
        }
      });

      // Fetch chemical channels
      fetchChemChannels(selectedPadId).then(chemChannelData => {
        const chemChannelOptions = chemChannelData.map((ch: any) => ({
          value: ch.value,
          label: ch.label,
          chem_name: ch.chem_name,
          metric: ch.metric
        }));

        axios.get(`${backendUrl}/pads/${selectedPadId}/momentum/wells`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
          .then(res => {
            const wells: { id: number; well_api: string; well_name: string }[] = res.data || [];
            setMomentumWells(wells);
        
            // Add momentum channels to flat channels list for axis config / rules engine
            if (wells.length > 0) {
              setChannels(prev => {
                const existingValues = new Set(prev.map((c: any) => c.value));
                const toAdd: ChannelOption[] = wells
                  .filter(w => !existingValues.has(`momentum_well_${w.id}`))
                  .map(w => ({
                    value:            `momentum_well_${w.id}`,
                    label:            `${w.well_name} Pressure`,
                    momentum_well_id: w.id,
                  }));
                return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
              });
            }
          })
          .catch(err => console.error('Error fetching momentum wells:', err));

          // Scoreboard channels (named by metadata friendlyName)
          axios.get(`${backendUrl}/pads/${selectedPadId}/scoreboard_channels`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
            .then(res => {
              const sb: ChannelOption[] = res.data || [];
              if (sb.length > 0) {
                setChannels(prev => {
                  const existing = new Set(prev.map(c => c.value));
                  const toAdd = sb.filter(c => !existing.has(c.value));
                  return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
                });
              }
            })
            .catch(err => console.error('Error fetching scoreboard channels:', err));

          // Fetch Seismos offset wells and add as selectable pressure channels
          axios.get(`${backendUrl}/pads/${selectedPadId}/seismos/wells`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
            .then(res => {
              const sWells: { id: number; well_api: string; well_name: string }[] = res.data || [];
              setSeismosWells(sWells);
              if (sWells.length > 0) {
                setChannels(prev => {
                  const existingValues = new Set(prev.map((c: any) => c.value));
                  const toAdd: ChannelOption[] = sWells
                    .filter(w => !existingValues.has(`seismos_well_${w.id}`))
                    .map(w => ({
                      value:           `seismos_well_${w.id}`,
                      label:           `${w.well_name} Pressure`,
                      seismos_well_id: w.id,
                    }));
                  return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
                });
              }
            })
            .catch(err => console.error('Error fetching seismos wells:', err));
                
        // Combine treatment and chemical channels
        const treatmentChannels = TREATMENT_CHANNELS.map(ch => ({ 
          value: ch, 
          label: getFriendlyChannelName(ch) 
        }));
        
        const allChannels = [
          ...treatmentChannels,
          ...chemChannelOptions,
          ...buildSeismosMetricChannels(),
        ];
        setChannels(allChannels);
        
        // Update axis settings to include chemical channels in default assignments
        setAxisSettings(prev => {
          const newSettings = { ...prev };
          
          // Add chemical channels to appropriate axes (distribute them across available axes)
          chemChannelOptions.forEach((chemChannel, index) => {
            const axisNames = ['left1', 'left2', 'right1', 'right2'];
            const targetAxis = axisNames[index % axisNames.length];
            
            // Add to axis config for backward compatibility
            setAxisConfig(prevConfig => ({
              ...prevConfig,
              [chemChannel.value]: {
                axis: targetAxis,
                min: undefined,
                max: undefined
              }
            }));
          });
          
          return newSettings;
        });
      });
    }
  }, [selectedPadId]);

  // 2. Fetch data when ready (but not while streaming)
  useEffect(() => {
    console.log('running 1')
    if (isRealTimeStreaming) return; // avoid heavy reloads during live streaming
    console.log('running 2')
    console.log(refreshPresent)
    if (selectedPadId && selectedChannels.length > 0 && !refreshPresent) {
      console.log('running 3')
      fetchPadData(selectedPadId);
    }
  }, [selectedPadId, selectedChannels, isRealTimeStreaming]);

  // 2a. If channels/templates change while streaming, reset and restart cleanly
  useEffect(() => {
    if (!isRealTimeStreaming) return;
    console.log('running 2')
    setRefreshPresent(true)

    // Stop streaming (clears chart, unsubscribes, resets state, and calls fetchPadData)
    stopStreaming(false);

    // Give the disconnect + fetch a short beat, then restart
    const t = setTimeout(() => {
      handleStartStreaming();
    }, 600);

    return () => clearTimeout(t);
  }, [selectedChannels]);

  // 2b. Live Seismos metrics ride the 30s poll SeismosDataComponent already runs.
  // Events are stage-scoped and capped ~100, so replace series data outright —
  // idempotent, so no dedupe against whatever loadStageHistory already drew.
  useEffect(() => {
    if (!isRealTimeStreaming) return;
    const chart = chartRef.current?.chart;
    if (!chart) return;

    selectedChannels.filter(c => !!c.seismos_metric).forEach(ch => {
      const series = chart.get(ch.value);
      if (!series) return;
      const field = ch.seismos_metric as string;
      const points = liveSeismosMetrics
        .map(e => [seismosEventMs(e), seismosMetricValue(e, field)] as [number | null, number | null])
        .filter(([x, y]) => x != null && y != null)
        .map(([x, y]) => [normalizeTimestamp(x as number), y as number] as [number, number])
        .sort((a, b) => a[0] - b[0]);
      (series as any).setData(points, false);
    });

    chart.redraw();
  }, [liveSeismosMetrics, selectedChannels, isRealTimeStreaming]);

  // Find the current stage in progress when streaming starts
  const findCurrentStage = async () => {
    if (!selectedPadId) {
      setStreamingError('No pad ID provided');
      return;
    }

    try {
      setStreamingError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        setStreamingError('Authentication token not found');
        return;
      }

      // Fetch wells for this pad
      const wellsResponse = await axios.get(`${backendUrl}/pads/${selectedPadId}/wells`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Extract wells data from the response
      let wellsData: Well[] = [];
      
      if (wellsResponse.data && Array.isArray(wellsResponse.data)) {
        wellsData = wellsResponse.data;
      } else if (wellsResponse.data && wellsResponse.data.data && Array.isArray(wellsResponse.data.data)) {
        wellsData = wellsResponse.data.data;
      } else {
        console.error('Unexpected wells response format:', wellsResponse);
        setStreamingError('Failed to fetch wells data');
        return;
      }
      
      // Store the wells data for later use
      console.log('Fetched wells for pad:', wellsData);
      padWellApisRef.current = new Set(
        wellsData.map((w: Well) => String(w.api || '').replace(/-00-00$/, ''))
      );

      // Fetch stages for each well and find the one in progress
      for (const well of wellsData) {
        const stagesResponse = await axios.get(`${backendUrl}/wells/${well.id}/stages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const stages: Stage[] = stagesResponse.data;
        
        // Find a stage that has start_ts but no end_ts (in progress)
        const inProgressStage = stages.find(stage => stage.start_ts && !stage.end_ts);
        
        if (inProgressStage) {
          setCurrentWell(well);
          setCurrentStage(inProgressStage);
          
        try {
          // Fetch treatment schedule for this stage
          const scheduleResponse = await axios.get(
            `${backendUrl}/stages/${inProgressStage.id}/design_treatment_schedule/latest`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );

                    // Check if the response is an array or a single object
          const scheduleData = Array.isArray(scheduleResponse.data) 
            ? scheduleResponse.data 
            : [scheduleResponse.data]; // Convert single object to array if needed
          
          // Sort by step number
          const sortedSchedule = [...scheduleData].sort((a, b) => a.step - b.step);
          setTreatmentSchedule(sortedSchedule);

          // Fetch treatment changes and chem schedule for this stage
          fetchTreatmentChanges(inProgressStage.id);
          fetchNextChemSchedule(inProgressStage.id);
          
          // Set current step to the first step initially
          setCurrentStep(sortedSchedule.length > 0 ? sortedSchedule[0].step : null);
          
          return { well, stage: inProgressStage, schedule: sortedSchedule };

          // Use scheduleResponse.data as normal...
        } catch (error: any) {
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            toast.error("ProPilot Scheduler not loaded - Please load and try again.");
            return null
          } else {
            console.error("Error fetching treatment schedule:", error);
            toast.error("Failed to load treatment schedule.");
            return null
          }
        }
        }
      }
      
      setStreamingError('No stage currently in progress');
      toast.error('No stage streaming. Please try again when the stage is started.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return null;
    } catch (err) {
      console.error('Error finding current stage:', err);
      setStreamingError('Failed to find current stage in progress');
      return null;
    }
  };

  // Start real-time streaming
  const handleStartStreaming = async () => {
    try {
      // Set real-time streaming flag to true at the beginning
      setIsRealTimeStreaming(true);
      
      // Find the current stage in progress
      const result = await findCurrentStage();
      console.log('Current stage result:', result);
      
      if (result === null || result === undefined) {
        console.log('Showing toast notification for null result');
        setIsRealTimeStreaming(false); // Reset flag if no result
        return; // Return early if no result
      }
      
      const { well, stage } = result;

      // Seed streaming lifecycle state (badge shows immediately; lock happens on first state msg)
      streamingVanRef.current = null;
      streamingApiRef.current = well.api;
      streamingStageRef.current = Number(stage.stage_number);
      setStageState('executing');
      stageStateRef.current = 'executing';

      // Immediately set selectedStage when streaming starts
      setSelectedStage({
        stageId: stage.id,
        wellId: well.id,
        stageNumber: stage.stage_number
      });
      setShowViewTreatmentButton(true);
      
      // Initialize real-time data points with empty arrays for selected channels only
      const initialDataPoints: {[key: string]: [number, number][]} = {};
      selectedChannels.forEach(channel => {
        initialDataPoints[channel.value] = [];
      });
      
      // Fetch historical treatment data for this stage (shared loader; also
      // used by rebindStage on stage transitions)
      await loadStageHistory(result?.well?.pad_id ?? selectedPadId, stage);
      
      // WebSocket URL for MQTT broker
      const brokerUrl = process.env.REACT_APP_BROKER_URL;
      const brokerUser = process.env.REACT_APP_BROKER_USER;
      const brokerPass = process.env.REACT_APP_BROKER_PASS;
      
      // MQTT connection options
      const options = {
        username: brokerUser,
        password: brokerPass,
        keepalive: 60,
        reconnectPeriod: 1000,
      };
      
      // Connect to MQTT broker
      const client = mqtt.connect(brokerUrl, options);
      clientRef.current = client;
      
      client.on('connect', () => {
        console.log('MQTT connected successfully to broker:', brokerUrl);
        
        // Only subscribe to the state topic with wildcard to discover wells
        const stateBaseTopic = 'propilot/+/petrix/state';
        console.log('Subscribing to state base topic:', stateBaseTopic);
        client.subscribe(stateBaseTopic, (err) => {
          if (err) {
            console.error('Subscribe error:', err);
          } else {
            console.log('Successfully subscribed to state base topic:', stateBaseTopic);
            setSubscribedTopics(prev => new Set(prev).add(stateBaseTopic));
          }
        });
        
        // Log the current well from findCurrentStage result
        if (well) {
          console.log('Current well from database:', well.well_name);
          console.log('Current stage:', stage);
        } else {
          console.log('No current well set, will use first well with data');
        }
      });
      
      client.on('message', (topic, payload) => {
        try {
          const payloadStr = payload.toString();
          const parsed = JSON.parse(payloadStr);
          
          // Extract well identifier from topic
          const topicParts = topic.split('/');
          if (topicParts.length < 3) {
            console.warn('Invalid topic format:', topic);
            return;
          }
          
          const wellIdentifier = topicParts[1]; // e.g., "FTCV-1799" or "DV1017"
          
          // Handle state messages
          if (topic.endsWith('/state')) {
            handleStateMessage(topic, wellIdentifier, parsed, client);
          }
          
          // Handle data messages
          if (topic.endsWith('/data')) {
            handleDataMessage(topic, wellIdentifier, parsed);
          }
          
          // Handle chemical data messages
          if (topic.endsWith('/chem')) {
            handleChemDataMessage(topic, wellIdentifier, parsed);
          }

          if (topic.endsWith('/scoreboard/metadata')) {
            handleScoreboardMetadataMessage(parsed);
          }
          if (topic.endsWith('/scoreboard/telemetry')) {
            handleScoreboardTelemetryMessage(parsed);
          }

          // Handle Momentum well pressure messages
          if (topic.endsWith('/momentum/wellPress')) {
            handleMomentumWellPressMessage(parsed);
          }

          // Handle Seismos well pressure messages
          if (topic.endsWith('/seismos/wellPress')) {
            handleSeismosWellPressMessage(parsed);
          }
          
          // Handle schedule update messages
          if (topic.endsWith('/petrix/schedule')) {
            console.log('Received schedule message on topic:', topic);
            handleScheduleMessage(topic, wellIdentifier, parsed);
          }
          
        } catch (error) {
          console.error('Error processing MQTT message:', error);
        }
      });
      
      // ── Streaming lifecycle ──────────────────────────────────────────────
      // One rule: whatever well/stage the state topic says is `executing` is
      // what we stream. bindToWell is the single bind path - it covers the
      // initial hookup, same-well stage changes, and hops to a different well.
      // NOTE: assumes one well executes at a time (zipper); SimulFrac needs a
      // different UI and would flap between wells here.
      const VAN_TOPIC_SUFFIXES = ['petrix/data', 'petrix/chem', 'petrix/schedule', 'momentum/wellPress', 'seismos/wellPress'];

      // Resolve a Well object on this pad by api (shared by bind + announce)
      const resolvePadWell = async (wellApi: string): Promise<Well | null> => {
        try {
          const token = localStorage.getItem('token');
          if (!token || !selectedPadId) return null;
          const res = await axios.get(`${backendUrl}/pads/${selectedPadId}/wells`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const wellsData: Well[] = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          const norm = (api: string) => String(api || '').replace(/-00-00$/, '');
          padWellApisRef.current = new Set(wellsData.map(w => norm(w.api)));
          return wellsData.find(w => norm(w.api) === norm(wellApi)) || null;
        } catch (e) {
          console.error('resolvePadWell failed', e);
          return null;
        }
      };

      const bindToWell = (vanId: string, wellApi: string, stageNumber: number | null, client: mqtt.MqttClient, freshStage: boolean) => {
        const prevVan = streamingVanRef.current;
        console.log(`Binding stream: api=${wellApi} stage=${stageNumber} freshStage=${freshStage} (van ${prevVan ?? 'none'} -> ${vanId})`);

        // Swap van-specific subscriptions when the van changes
        // (the state wildcard stays subscribed for the life of the connection)
        if (prevVan !== vanId) {
          if (prevVan) {
            VAN_TOPIC_SUFFIXES.forEach(sfx => client.unsubscribe(`propilot/${prevVan}/${sfx}`));
            SCOREBOARD_SUFFIXES.forEach(sfx => client.unsubscribe(`${prevVan}/scoreboard/${sfx}`));
          }
          scoreboardMetaRef.current = {};
          SCOREBOARD_SUFFIXES.forEach(sfx => {
            const t = `${vanId}/scoreboard/${sfx}`;
            client.subscribe(t, (err) => {
              if (err) console.error('Subscribe error:', t, err);
              else setSubscribedTopics(prev => new Set(prev).add(t));
            });
          });
          VAN_TOPIC_SUFFIXES.forEach(sfx => {
            const t = `propilot/${vanId}/${sfx}`;
            client.subscribe(t, (err) => {
              if (err) console.error('Subscribe error:', t, err);
              else setSubscribedTopics(prev => new Set(prev).add(t));
            });
          });
          localStorage.setItem('currentVanId', vanId);
          localStorage.setItem('mqttTopic', `propilot/${vanId}/petrix/data`);
        }
        streamingVanRef.current = vanId;
        streamingApiRef.current = wellApi;
        streamingStageRef.current = stageNumber;
        setIsRealTimeStreaming(true);
        setRefreshPresent(false);

        if (!freshStage) return; // initial hookup to the already-loaded stage: keep the backfilled chart

        // New stage (or new well): wipe stage-scoped state, then resolve the
        // Well object for the badge and rebind stage id / schedule / changes.
        // Clear at the STATE level so a later chartOptions recompute can't
        // restore stale points (chartSeries feeds chartOptions); the instance
        // wipe gives instant visual feedback.
        setChartSeries(prev => prev.map((s: any) => ({ ...s, data: [] })));
        resetChartForNewStage();
        setTreatmentChanges([]);
        setChemicalChanges([]);
        if (stageNumber !== null) {
          setCurrentStage(prev => (prev ? { ...prev, stage_number: stageNumber } : prev));
        }
        (async () => {
          const boundWell = await resolvePadWell(wellApi);
          if (!boundWell) {
            console.error('bindToWell: no pad well matches api', wellApi);
            return;
          }
          setCurrentWell(boundWell);
          if (stageNumber !== null) {
            setTimeout(() => rebindStage(boundWell, stageNumber), 1500);
          }
        })();
      };

      // State message handler
      const handleStateMessage = (topic, wellIdentifier, parsed, client) => {
        console.log('Received state message:', { topic, wellIdentifier, wellApi: parsed.wellApi, state: parsed.state, wbs: parsed.wbs });
        if (!parsed.wellApi) return;

        const norm = (api: string) => String(api || '').replace(/-00-00$/, '');
        // Scope to OUR pad: ignore state from every other pad and test rig on
        // the fleet-wide broker. Without this, the first `executing` seen can
        // bind us to another pad's well (the root cause of the chart wipes).
        if (padWellApisRef.current.size > 0 && !padWellApisRef.current.has(norm(parsed.wellApi))) {
          return;
        }
        const stageMatch = /S(\d+)$/.exec(parsed.wbs || '');   // same stage parse as the poster-worker
        const incomingStage = stageMatch ? parseInt(stageMatch[1], 10) : null;
        const isBoundWell = norm(parsed.wellApi) === norm(streamingApiRef.current || '');

        if (parsed.state === 'executing') {
          // First bind after start: adopt the message as the source of truth and
          // NEVER wipe - the chart already holds the freshly loaded history. The
          // DB-seeded refs are only hints; comparing them against MQTT values to
          // gate a destructive wipe is what cost us the historical data (any
          // type/format mismatch read as a "new stage"). After the first bind,
          // both sides of the comparison come from parsed messages, so a
          // freshStage=true is a genuine stage/well change.
          const firstBind = streamingVanRef.current === null;
          const freshStage = !firstBind && (!isBoundWell || incomingStage !== streamingStageRef.current);
          if (freshStage) {
            console.warn('Fresh stage detected - wiping chart:', {
              boundApi: streamingApiRef.current, incomingApi: parsed.wellApi,
              boundStage: streamingStageRef.current, incomingStage,
            });
          }
          if (firstBind || freshStage || streamingVanRef.current !== wellIdentifier) {
            bindToWell(wellIdentifier, parsed.wellApi, incomingStage, client, freshStage);
          }
          setStageState('executing');
          stageStateRef.current = 'executing';
        } else if (isBoundWell) {
          // complete / configured / unconfigured for the bound well drive the
          // badge and the read-only gating (canEditStage).
          setStageState(parsed.state);
          stageStateRef.current = parsed.state;
          // Show the upcoming stage number as soon as it's configured. Display
          // only: streamingStageRef stays on the old stage so the next
          // `executing` still registers as a fresh stage (chart wipe + rebind).
          if (incomingStage !== null && incomingStage !== streamingStageRef.current) {
            setCurrentStage(prev => (prev ? { ...prev, stage_number: incomingStage } : prev));
          }
        } else if (parsed.state === 'configured' && stageStateRef.current !== 'executing') {
          // The pad staged up a DIFFERENT well while ours is idle: re-point the
          // binding identity + badge now; topics/chart swap on its `executing`.
          // Ignored while our well is executing - zipper ops configure the next
          // well mid-stage and we must not hop away from live pumping.
          console.log(`Next up: ${parsed.wellApi} stage ${incomingStage} (configured)`);
          streamingApiRef.current = parsed.wellApi;
          streamingStageRef.current = null; // forces a fresh bind on its `executing`
          setStageState('configured');
          stageStateRef.current = 'configured';
          if (incomingStage !== null) {
            setCurrentStage(prev => (prev ? { ...prev, stage_number: incomingStage } : prev));
          }
          resolvePadWell(parsed.wellApi).then(w => { if (w) setCurrentWell(w); });
        }
      };
    
    // Data message handler
    const handleDataMessage = (topic, wellIdentifier, parsed) => {
      
      // Store the original data for display
      setRealTimeData(parsed);
      
      // Map PetrixData format to TreatmentData format
      const timestampMs = Date.now();
      
      // Create a mapped object that converts from PetrixData format to TreatmentData format
      const mappedData = {
        id: Math.floor(Math.random() * 10000) + 1, // Generate a random ID
        pad_id: selectedPadId || 0,
        timestamp: timestampMs, // Use milliseconds to match database format
        treat_press: parsed.stp, // Map stp to treat_press
        clean_rate: parsed.cleanRate, // Map cleanRate to clean_rate
        slurry_rate: parsed.slurryRate, // Map slurryRate to slurry_rate
        stage_denso: parsed.stageDensity, // Map stageDensity to stage_denso
        elapsed_time: parsed.elapsedTime, // Map elapsedTime to elapsed_time
        step_clean: parsed.stepClean, // Map stepClean to step_clean
        step_slurry: parsed.stepSlurry, // Map stepSlurry to step_slurry
        step_sand: parsed.stepSand, // Map stepSand to step_sand
        step: parsed.step, // Include step field
        net_pressure:        parsed.netPressure,
        stage_slurry:        parsed.stageSlurry,
        clean_rate_calc:     parsed.cleanRateCalculated,
        stage_sand:          parsed.stageSand,
        friction_loss_press: parsed.frictionLossPressure,
        stp_b:               parsed.stpB,
        hydrostatic_press:   parsed.hydrostaticPressure,
        stage_clean:         parsed.stageClean,
        bhp:                 parsed.bhp,
      };
      
      // Update current step if available
      if (parsed.step !== undefined && parsed.step !== null) {
        setCurrentStep(parsed.step);
      }
      
      // Call handleRealTimeData with the mapped data
      handleRealTimeData(mappedData);
    };
    
    // Chemical data message handler
    const handleChemDataMessage = (topic, wellIdentifier, parsed) => {
      
      // The parsed data should be an array of chemical objects
      // Format: [{"name": "Bionic Man", "rate": 4.2, "ratio": 1.0, "total": 372.54}, ...]
      if (Array.isArray(parsed)) {
        const timestampMs = Date.now();
        
        // Store the chemical data for display in the Real-Time Treatment/Chem Values component
        setRealTimeChemData(parsed);

        // Dynamically merge any new chemicals from the live /chem topic into the
        // channels list so they immediately appear in the rules dropdown, even if
        // they haven't been persisted to the DB yet.
        // Uses the chem name as-is (spaces preserved) to match the value format
        // returned by the /pads/{padId}/chem_channels backend endpoint.
        setChannels(prev => {
          const existingValues = new Set(prev.map((c: any) => c.value));
          const toAdd: any[] = [];
          parsed.forEach((chemItem: any) => {
            if (!chemItem.name) return;
            const name = chemItem.name;
            [
              { suffix: '_ratio', label: `${name} Ratio`,  metric: 'ratio' },
              { suffix: '_rate',  label: `${name} Rate`,   metric: 'rate'  },
              { suffix: '_total', label: `${name} Total`,  metric: 'total' },
            ].forEach(({ suffix, label, metric }) => {
              const value = `${name}${suffix}`;
              if (!existingValues.has(value)) {
                toAdd.push({ value, label, chem_name: name, metric });
                existingValues.add(value);
              }
            });
          });
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
        
        // Process each chemical in the array
        parsed.forEach(chemItem => {
          if (chemItem.name && typeof chemItem.rate === 'number' && 
              typeof chemItem.ratio === 'number' && typeof chemItem.total === 'number') {
            
            // Direct update to the chart if chart reference is available (for performance)
            if (chartRef.current && chartRef.current.chart) {
              const chart = chartRef.current.chart;
              const normalizedTimestamp = normalizeTimestamp(timestampMs);
              
              // Add new points directly to the chemical series only if they exist
              const rateChannel = `${chemItem.name}_rate`;
              const ratioChannel = `${chemItem.name}_ratio`;
              const totalChannel = `${chemItem.name}_total`;
              
              const rateSeries = chart.get(rateChannel);
              if (rateSeries && selectedChannels.some(ch => ch.value === rateChannel)) {
                addStreamPoint(rateSeries, normalizedTimestamp, chemItem.rate);
              }
              
              const ratioSeries = chart.get(ratioChannel);
              if (ratioSeries && selectedChannels.some(ch => ch.value === ratioChannel)) {
                addStreamPoint(ratioSeries, normalizedTimestamp, chemItem.ratio);
              }
              
              const totalSeries = chart.get(totalChannel);
              if (totalSeries && selectedChannels.some(ch => ch.value === totalChannel)) {
                addStreamPoint(totalSeries, normalizedTimestamp, chemItem.total);
              }
            }
          }
        });
      }
    };

    // Scoreboard metadata: telemetry key → friendlyName. Also makes new channels
    // selectable immediately, before the worker has written any rows.
    const handleScoreboardMetadataMessage = (parsed: any) => {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      scoreboardMetaRef.current = parsed;
      setChannels(prev => {
        const existing = new Set(prev.map(c => c.value));
        const toAdd: ChannelOption[] = [];
        Object.values(parsed).forEach((m: any) => {
          const name = m?.friendlyName;
          if (!name) return;
          const value = `scoreboard:${name}`;
          if (existing.has(value)) return;
          existing.add(value);
          toAdd.push({
            value,
            label: m.uom ? `${name} (${m.uom})` : name,
            scoreboard_channel: name,
            uom: m.uom || '',
          });
        });
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    };

    // Scoreboard telemetry: plot by friendlyName; the raw key is never a channel.
    const handleScoreboardTelemetryMessage = (parsed: any) => {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      const chart = chartRef.current?.chart;
      if (!chart) return;
      const meta = scoreboardMetaRef.current;
      const x = normalizeTimestamp(Date.now());
      Object.entries(parsed).forEach(([key, raw]) => {
        const name = meta[key]?.friendlyName;
        if (!name || raw == null) return;
        const value = `scoreboard:${name}`;
        if (!selectedChannels.some(ch => ch.value === value)) return;
        const series = chart.get(value);
        if (series) addStreamPoint(series as any, x, Number(raw));
      });
    };

    // Momentum well pressure message handler
    const handleMomentumWellPressMessage = (parsed: any) => {
      if (!Array.isArray(parsed)) return;
      const normalize = (s: string) => s.replace(/\D/g, '').slice(0, 10);
      parsed.forEach((item: any) => {
        const rawApi = String(item.wellAPI ?? '').trim();
        const press  = item.press;
        if (!rawApi || press == null) return;
        const matched = momentumWells.find(w => normalize(w.well_api) === normalize(rawApi));
        if (!matched) return;
        const channelValue = `momentum_well_${matched.id}`;
        if (!selectedChannels.some(ch => ch.value === channelValue)) return;
        if (chartRef.current?.chart) {
          const series = chartRef.current.chart.get(channelValue);
          if (series) {
            addStreamPoint(series as any, normalizeTimestamp(Date.now()), Number(press));
          }
        }
      });
    };

      // Seismos well pressure message handler
      const handleSeismosWellPressMessage = (parsed: any) => {
        if (!Array.isArray(parsed)) return;
        const normalize = (s: string) => String(s).replace(/\D/g, '').slice(0, 10);
        parsed.forEach((item: any) => {
          const rawApi = String(item.wellAPI ?? '').trim();
          const press  = item.press;
          if (!rawApi || press == null) return;
          const matched = seismosWells.find(w => normalize(w.well_api) === normalize(rawApi));
          if (!matched) return;
          const channelValue = `seismos_well_${matched.id}`;
          if (!selectedChannels.some(ch => ch.value === channelValue)) return;
          if (chartRef.current?.chart) {
            const series = chartRef.current.chart.get(channelValue);
            if (series) {
              addStreamPoint(series as any, normalizeTimestamp(Date.now()), Number(press));
            }
          }
        });
      };

      // Schedule message handler
      const handleScheduleMessage = (topic, wellIdentifier, parsed) => {
        console.log('Received schedule update via MQTT:', { topic, wellIdentifier, payload: parsed });
        
        if (!Array.isArray(parsed) || parsed.length === 0) {
          // Fallback: fetch from the latest design schedule endpoint
          console.log('MQTT payload invalid, falling back to API fetch');
          setTimeout(() => { refreshTreatmentSchedule(); }, 1000);
          return;
        }

        // Detect Petrix version from the chemicals array presence and update state.
        // This drives handleSaveChemSchedule — Petrix 7.0.1 can't receive chem updates
        // programmatically so we fall back to posting a plain message instead.
        // Detect Petrix version: 8.0 sends a "chemicals" key on every step (may be an empty
        // array when no chemicals are assigned). 7.0.1 omits the key entirely.
        // Check for key presence — not array length — so an empty chemicals: [] still
        // correctly identifies Petrix 8.0.
        const hasPetrix8Chemicals = parsed.some(
          (step: any) => Object.prototype.hasOwnProperty.call(step, 'chemicals') && Array.isArray(step.chemicals)
        );
        setPetrixHasChemSupport(hasPetrix8Chemicals);
        console.log(`Petrix version detected: ${hasPetrix8Chemicals ? '8.0 (chemicals supported)' : '7.0.1 (no chemicals array)'}`);

        // Always update the treatment schedule regardless of Petrix version
        const rawChems: Record<number, {name: string, group: string, ratio: number}[]> = {};
        parsed.forEach((step: any) => {
          if (Array.isArray(step.chemicals)) {
            rawChems[step.step] = step.chemicals.map((c: any) => ({
              name:  (c.name  || ''),
              group: (c.group || ''),
              ratio: typeof c.ratio === 'number' ? c.ratio : 0,
            }));
          }
        });
        setRawScheduleChemicals(rawChems);

        // The new MQTT broadcast is authoritative — an unsupervised rule (or
        // any external change) has shifted the schedule baseline, so any
        // pending edits are stale. Drop them so the fresh values render
        // instead of being masked by the user's prior edit (rate, conc,
        // clean/sand step on the treatment side; ratios on the chem side).
        setEditedChemLoadings({});
        setEditedChemGroupRatios({});
        setEditedTreatmentSteps({});

        const transformedSchedule = parsed.map(step => ({
          id: step.originalStep || step.step,
          stage_id: currentStage?.id || 0,
          step: step.step,
          type: step.type,
          description: step.description || '',
          rate: step.targetRate,
          conc: step.targetStartConc !== step.targetEndConc 
            ? (step.targetStartConc + step.targetEndConc) / 2
            : step.targetStartConc,
          clean_step: step.clean,
          clean_actual: 0,
          slurry_step: 0,
          slurry_actual: 0,
          sand_step: (() => {
            const concVal = step.targetStartConc !== step.targetEndConc
              ? (step.targetStartConc + step.targetEndConc) / 2
              : step.targetStartConc;
            return Math.round((step.clean || 0) * 42 * concVal);
          })(),
          sand_actual: 0,
          start_conc: step.targetStartConc,
          end_conc: step.targetEndConc
        }));
        
        console.log('Updating treatment schedule from MQTT:', transformedSchedule);
        setTreatmentSchedule(transformedSchedule);
      };
      
      client.on('error', (error) => {
        console.error('MQTT connection error:', error);
      });
      
      client.on('close', () => {
        console.log('MQTT connection closed');
      });
      
    } catch (error) {
      console.error('Error starting streaming:', error);
    }
  };

  const stopStreaming = (refreshData = true) => {
    if (clientRef.current) {
      // Remove handlers first so nothing fires during teardown
      try { clientRef.current.removeAllListeners('message'); } catch {}
      try { clientRef.current.removeAllListeners(); } catch {}

      // Unsubscribe (optional once you're ending, but fine to keep)
      subscribedTopics.forEach(topic => {
        console.log('Unsubscribing from topic:', topic);
        clientRef.current?.unsubscribe(topic);
      });

      console.log('Ending MQTT connection');
      clientRef.current.end(true);  // force disconnect
      clientRef.current = null;
      setSubscribedTopics(new Set());
    }

    // Keep the rest of your resets as-is (isRealTimeStreaming, series, plotBands, etc.)
    setIsRealTimeStreaming(false);
    setStageState(null);
    stageStateRef.current = null;
    streamingVanRef.current = null;
    streamingApiRef.current = null;
    streamingStageRef.current = null;
    padWellApisRef.current = new Set();
    setRealTimeData(null);
    setCurrentWell(null);
    setCurrentStage(null);
    setTreatmentSchedule([]);
    setCurrentStep(null);
    console.log('Streaming stopped and all state reset');

    setChartTitle('Treatment Data');
    setChartSeries([]);
    setPlotBands([]);
    setPlotLines([]);
    setXAxisRange({});

    // Reset date range to null so fetchPadData loads ALL data
    setFromDate(null);
    setToDate(null);

    if (selectedPadId && refreshData) {
      console.log('running 3 - Reloading treatment data graph');
      fetchPadData(selectedPadId);
    }

    setTimeout(() => {
      console.log('Ready for new connection');
    }, 500);
  };
  
  // Get the next steps in the treatment schedule
  const getNextSteps = () => {
    if (!treatmentSchedule.length || currentStep === null) {
      return [];
    }
    
    const currentStepIndex = treatmentSchedule.findIndex(step => step.step === currentStep);
    
    if (currentStepIndex === -1) {
      return treatmentSchedule.slice(0, 3); // Return first three steps if current step not found
    }
    
    // Return current step and next two steps
    return treatmentSchedule.slice(currentStepIndex, currentStepIndex + 3);
  };

  // Check if current step is visible in the rendered steps
  const isCurrentStepVisible = () => {
    if (!treatmentSchedule.length || currentStep === null) {
      return false;
    }
    
    const nextSteps = getNextSteps();
    // Only return true if the current step is the FIRST step in the rendered list
    // This ensures we're actually on the current step, not just that it's visible somewhere
    const isVisible = nextSteps.length > 0 && nextSteps[0].step === currentStep;
    
    // Add additional check: ensure we have real-time data for this step
    // This prevents editing before the step is actually being executed
    const hasRealTimeDataForCurrentStep = realTimeData && realTimeData.step === currentStep;
    
    return isVisible && hasRealTimeDataForCurrentStep;
  };

    const rateDropConstraints = useMemo(() => {
    const sorted = [...getNextSteps()].sort((a, b) => a.step - b.step);
    const map: Record<number, { rateMin: number; rateMax: number; rateOutOfRange: boolean; cleanSeconds: number; cleanOutOfRange: boolean; consecutiveRateDrop: boolean }> = {};
    let hasViolation = false;
    sorted.forEach((s, i) => {
      const type = editedTreatmentSteps[s.id]?.type ?? s.type;
      if (type !== 'Rate Drop' && type !== 'Rate Drop-Proppant' && type !== 'Rate Drop-Sand Ramp') return;
      const prevType = i > 0 ? (editedTreatmentSteps[sorted[i - 1].id]?.type ?? sorted[i - 1].type) : '';
      const consecutiveRateDrop = prevType === 'Rate Drop' || prevType === 'Rate Drop-Proppant' || prevType === 'Rate Drop-Sand Ramp';
      const prevRate = i > 0 ? (editedTreatmentSteps[sorted[i - 1].id]?.rate ?? sorted[i - 1].rate) : 0;
      const rateMin = +(prevRate - 10).toFixed(4);
      const rateMax = +(prevRate - 2).toFixed(4);
      const currentRate = (editedTreatmentSteps[s.id]?.rate ?? s.rate) / (1 + (editedTreatmentSteps[s.id]?.conc ?? s.conc) / 42);
      const rateOutOfRange = currentRate < rateMin || currentRate > rateMax;
      const cleanStep = editedTreatmentSteps[s.id]?.clean_step ?? s.clean_step;
      const cleanSeconds = currentRate > 0 ? (cleanStep / currentRate) * 60 : 0;
      const cleanOutOfRange = cleanSeconds < 5 || cleanSeconds > 90;
      if (rateOutOfRange || cleanOutOfRange || consecutiveRateDrop) hasViolation = true;
      map[s.id] = { rateMin, rateMax, rateOutOfRange, cleanSeconds, cleanOutOfRange, consecutiveRateDrop };
    });
    return { map, hasViolation };
  }, [treatmentSchedule, editedTreatmentSteps]);

  // Handle receiving real-time data
  // ── Real-time streaming: memory + stage-transition helpers ──────────────────
  // Backstop for multi-day streaming: hard cap points per series (~6h at 1 Hz).
  // The primary bound is clearing every series when a new stage starts (a stage < 5h);
  // this cap only protects against a missed reset so memory can never grow unbounded.
  const MAX_STREAM_POINTS = 21600;
  const addStreamPoint = (series: any, x: number, y: number) => {
    if (!series) return;
    const shift = series.data && series.data.length >= MAX_STREAM_POINTS;
    series.addPoint([x, y], false, shift);
  };

  // Wipe all series data when a new stage begins (keeps only the current stage on screen).
  const resetChartForNewStage = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    chart.series.forEach((s: any) => s.setData([], false));
    chart.redraw();
  };

  // Rebind stage id / schedule / step to the new stage. The poster-worker inserts the
  // stage row on the same `executing` message we react to, so retry a few times to
  // avoid the race where start_ts isn't written yet.
  // Load a stage's historical treatment data into the chart, state-level
  // (setChartSeries) so React owns the series data. Shared by stream start and
  // stage-transition rebinds - this is the original start-path code, extracted.
  const loadStageHistory = async (padIdForHistory: number | null | undefined, stage: Stage): Promise<void> => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setStreamingError('Authentication token not found');
          return;
        }
        
        // Get current timezone
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Create from date using stage's start_ts
        // Check if start_ts is already in milliseconds or needs conversion
        const startTsInMs = ensureMs(stage.start_ts);
        const fromDate = new Date(startTsInMs);
        const toDate = new Date(); // Current time
        
        console.log(`Fetching historical data from ${fromDate.toISOString()} to ${toDate.toISOString()}`);
        console.log(`Original start_ts: ${stage.start_ts}, converted to ms: ${startTsInMs}`);
        
        // Prepare channels parameter based on selected channels
        const channelParam = selectedChannels.map(ch => ch.value).join(',');
        
        if (!padIdForHistory) {
          setStreamingError('Pad ID not available for historical fetch');
          return;
        }

        const treatmentResponse = await axios.get(`${backendUrl}/pads/${padIdForHistory}/treatment_data`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            channels: channelParam,
            from: fromDate.toISOString(),
            to: toDate.toISOString(),
            timezone: userTimezone
          }
        });
        
        console.log('Historical treatment data:', treatmentResponse.data);
        
        // Process historical data
        const historicalData = treatmentResponse.data;
        
        // Initialize series data from historical data based on selected channels
        const processedData: {[key: string]: [number, number][]} = {};
        selectedChannels.forEach(channel => {
          processedData[channel.value] = [];
        });
        
        console.log('Historical data count:', historicalData.length);
        
        historicalData.forEach((item: any) => {
          if (item.timestamp) {
            
            const normalizedTimestamp = normalizeTimestamp(item.timestamp);
            
            // Process only selected channels (both treatment and chemical)
            selectedChannels.forEach(channel => {
              const channelValue = channel.value;
              let dataValue;
              
              // Check if this is a chemical channel
              if (channelValue.includes('_rate') || channelValue.includes('_ratio') || channelValue.includes('_total')) {
                // This is a chemical channel, get the value directly from the item
                dataValue = item[channelValue];
              } else {
                // This is a treatment channel, get from TreatmentData structure
                dataValue = item[channelValue as keyof TreatmentData];
              }
              
              if (dataValue !== null && dataValue !== undefined) {
                processedData[channelValue].push([normalizedTimestamp, Number(dataValue)]);
              }
            });
          }
        });
        
        // Fetch historical momentum well press data for selected momentum channels
        const selectedMomentumChannels = selectedChannels.filter(c => !!c.momentum_well_id);
          for (const momentumCh of selectedMomentumChannels) {
            try {
              const pressResponse = await axios.get(
                `${backendUrl}/pads/${padIdForHistory}/momentum/well_press`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  params: { from: fromDate.toISOString(), to: toDate.toISOString(), well_id: momentumCh.momentum_well_id }
                }
              );
              processedData[momentumCh.value] = pressResponse.data.map((row: any) => [
                normalizeTimestamp(row.timestamp), row.press,
              ]);
            } catch (err) {
              console.error(`Error fetching momentum press for ${momentumCh.label}:`, err);
              processedData[momentumCh.value] = [];
            }
          }

        // Fetch historical seismos well press data for selected seismos channels
        const selectedSeismosChannels = selectedChannels.filter(c => !!c.seismos_well_id);
        for (const seismosCh of selectedSeismosChannels) {
          try {
            const pressResponse = await axios.get(
              `${backendUrl}/pads/${padIdForHistory}/seismos/well_press`,
              {
                headers: { Authorization: `Bearer ${token}` },
                params: { from: fromDate.toISOString(), to: toDate.toISOString(), well_id: seismosCh.seismos_well_id }
              }
            );
            processedData[seismosCh.value] = pressResponse.data.map((row: any) => [
              normalizeTimestamp(row.timestamp), row.press,
            ]);
          } catch (err) {
            console.error(`Error fetching seismos press for ${seismosCh.label}:`, err);
            processedData[seismosCh.value] = [];
          }
        }

        // Log the processed data counts
        console.log('Processed historical data counts:', processedData);
        
        // Calculate appropriate min and max for x-axis to show all historical data
        let xMin: number | undefined = undefined;
        let xMax: number | undefined = undefined;
        
        // Find the earliest and latest timestamps across all selected channels
        const allTimestamps: number[] = [];
        Object.keys(processedData).forEach(key => {
          if (processedData[key] && processedData[key].length > 0) {
            processedData[key].forEach(point => {
              allTimestamps.push(point[0]);
            });
          }
        });
        
        if (allTimestamps.length > 0) {
          xMin = Math.min(...allTimestamps);
          xMax = Math.max(...allTimestamps) + (10 * 1000); // Add 10 seconds to ensure latest point is visible
          console.log(`Setting initial chart range: ${new Date(xMin).toISOString()} to ${new Date(xMax).toISOString()}`);
        }
        
        // Initialize series with historical data
        const initialSeries: Highcharts.SeriesOptionsType[] = selectedChannels.map((channel, index) => {
        const channelValue = channel.value;
        const axisName = axisConfig[channelValue]?.axis;
        const yAxisIndex = getYAxisIndex(axisName);

        return {
          type: 'line',
          id: channelValue,
          name: channel.label || getFriendlyChannelName(channelValue),
          data: processedData[channelValue] || [],
          yAxis: yAxisIndex,
          zIndex: index + 1,
          lineWidth: 1,
          color: (() => {
            if (CHANNEL_COLORS[channelValue]) return CHANNEL_COLORS[channelValue];
            if (channel.momentum_well_id) {
              const idx = momentumWells.findIndex(w => w.id === channel.momentum_well_id);
              return MOMENTUM_WELL_COLORS[idx >= 0 ? idx % MOMENTUM_WELL_COLORS.length : 0];
            }
            if (channel.seismos_well_id) {
              const idx = seismosWells.findIndex(w => w.id === channel.seismos_well_id);
              return SEISMOS_WELL_COLORS[idx >= 0 ? idx % SEISMOS_WELL_COLORS.length : 0];
            }
            return undefined;
          })(),
          states: { hover: { enabled: false } },
          marker: { enabled: false, radius: 2 },
          turboThreshold: 0,
          animation: false,
          enableMouseTracking: true,
          boostThreshold: 1000,
          dataGrouping: { enabled: true, approximation: 'average', groupPixelWidth: 2 }
        };
      });
        
        // Update chart state with historical data
        setChartTitle('Real-Time Treatment Data');
        setChartSeries(initialSeries);
        setPlotBands([]);
        setPlotLines([]);
        setXAxisRange({ min: xMin, max: xMax });
        
      } catch (err) {
        console.error('Error fetching historical treatment data:', err);
        setStreamingError('Failed to fetch historical treatment data');
        
        // Initialize with empty series as fallback
        const emptySeries: Highcharts.SeriesOptionsType[] = selectedChannels.map((channel, index) => {
          const channelValue = channel.value;
          const axisName = axisConfig[channelValue]?.axis;
          const yAxisIndex = getYAxisIndex(axisName);
          
          return {
            type: 'line',
            id: channelValue,
            name: channel.label || getFriendlyChannelName(channelValue),
            data: [],
            yAxis: yAxisIndex,
            zIndex: index + 1,
            lineWidth: 2,
            states: { hover: { enabled: false } },
            marker: {
              enabled: true,
              radius: 3
            },
            turboThreshold: 0
          };
        });
        
        // Update chart state with empty series
        setChartTitle('Real-Time Treatment Data');
        setChartSeries(emptySeries);
        setPlotBands([]);
        setPlotLines([]);
        setXAxisRange({});
      }
  };

  const rebindStage = async (wellObj: Well, stageNumber: number, attempt = 0) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${backendUrl}/wells/${wellObj.id}/stages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const stages: Stage[] = res.data || [];
      const match =
        stages.find(s => Number(s.stage_number) === stageNumber && s.start_ts && !s.end_ts) ||
        stages.find(s => Number(s.stage_number) === stageNumber);
      if (!match) {
        if (attempt < 3) setTimeout(() => rebindStage(wellObj, stageNumber, attempt + 1), 1500);
        return;
      }
      setCurrentStage(match);
      setSelectedStage({ stageId: match.id, wellId: wellObj.id, stageNumber: match.stage_number });
      setShowViewTreatmentButton(true);
      // Load the new stage's own change logs + chem schedule so nothing from the prior stage lingers.
      fetchTreatmentChanges(match.id);
      fetchChemicalChanges(match.id);
      fetchNextChemSchedule(match.id);
      // Reload the chart from this stage's start_ts (state-level, same path as
      // stream start) - replaces the handful of live points buffered meanwhile.
      loadStageHistory(selectedPadId, match);
      try {
        const sched = await axios.get(
          `${backendUrl}/stages/${match.id}/design_treatment_schedule/latest`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const arr = Array.isArray(sched.data) ? sched.data : [sched.data];
        const sorted = [...arr].sort((a: any, b: any) => a.step - b.step);
        setTreatmentSchedule(sorted);
        setCurrentStep(sorted.length > 0 ? sorted[0].step : null);
      } catch (e) {
        console.error('rebindStage: schedule fetch failed', e);
      }
    } catch (e) {
      console.error('rebindStage failed', e);
    }
  };

  const handleRealTimeData = (data: any) => {
    
    // Ensure we have a complete data object in the same format as treatment_data endpoint
    let completeData = { ...data };
    
    // If timestamp is missing, use current time in milliseconds to match database format
    if (completeData.timestamp === undefined) {
      completeData.timestamp = Date.now(); // Use milliseconds to match database format
    }
    
    // If id is missing, generate a placeholder
    if (completeData.id === undefined) {
      completeData.id = Math.floor(Math.random() * 10000) + 1;
    }
    
    // If pad_id is missing, use the selected pad ID or a default
    if (completeData.pad_id === undefined) {
      completeData.pad_id = selectedPadId || 0;
    }
    
    // Ensure all required fields exist with default values if needed
    const requiredFields = [
      'treat_press', 'clean_rate', 'slurry_rate', 'stage_denso',
      'step_clean', 'step_slurry', 'step_sand', 'elapsed_time'
    ];
    
    requiredFields.forEach(field => {
      if (completeData[field] === undefined || isNaN(completeData[field])) {
        completeData[field] = null; // Use null instead of 0 to avoid plotting zeros
      }
    });
    
    // Normalize the timestamp for chart display
    const now = normalizeTimestamp(completeData.timestamp);
    
    // Check if any of the data values are valid numbers
    const hasValidData = 
      (completeData.treat_press !== undefined && completeData.treat_press !== null && !isNaN(completeData.treat_press)) ||
      (completeData.clean_rate !== undefined && completeData.clean_rate !== null && !isNaN(completeData.clean_rate)) ||
      (completeData.slurry_rate !== undefined && completeData.slurry_rate !== null && !isNaN(completeData.slurry_rate)) ||
      (completeData.stage_denso !== undefined && completeData.stage_denso !== null && !isNaN(completeData.stage_denso)) ||
      (completeData.step_clean !== undefined && completeData.step_clean !== null && !isNaN(completeData.step_clean)) ||
      (completeData.step_slurry !== undefined && completeData.step_slurry !== null && !isNaN(completeData.step_slurry)) ||
      (completeData.step_sand !== undefined && completeData.step_sand !== null && !isNaN(completeData.step_sand));
    
    if (!hasValidData) {
      console.warn('No valid numerical data found in the received message');
      return; // Skip processing if no valid data
    }
    
    // Direct update to the chart if chart reference is available (for performance)
    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      
      // Add new points directly to the series
      if (completeData.treat_press !== undefined && completeData.treat_press !== null && !isNaN(completeData.treat_press)) {
        const series = chart.get('treat_press');
        if (series) {
          addStreamPoint(series, now, completeData.treat_press);
        }
      }
      
      if (completeData.clean_rate !== undefined && completeData.clean_rate !== null && !isNaN(completeData.clean_rate)) {
        const series = chart.get('clean_rate');
        if (series) {
          addStreamPoint(series, now, completeData.clean_rate);
        }
      }
      
      if (completeData.slurry_rate !== undefined && completeData.slurry_rate !== null && !isNaN(completeData.slurry_rate)) {
        const series = chart.get('slurry_rate');
        if (series) {
          addStreamPoint(series, now, completeData.slurry_rate);
        }
      }
      
      if (completeData.stage_denso !== undefined && completeData.stage_denso !== null && !isNaN(completeData.stage_denso)) {
        const series = chart.get('stage_denso');
        if (series) {
          addStreamPoint(series, now, completeData.stage_denso);
        }
      }
      
      if (completeData.step_clean !== undefined && completeData.step_clean !== null && !isNaN(completeData.step_clean)) {
        const series = chart.get('step_clean');
        if (series) {
          addStreamPoint(series, now, completeData.step_clean);
        }
      }
      
      if (completeData.step_slurry !== undefined && completeData.step_slurry !== null && !isNaN(completeData.step_slurry)) {
        const series = chart.get('step_slurry');
        if (series) {
          addStreamPoint(series, now, completeData.step_slurry);
        }
      }
      
      if (completeData.step_sand !== undefined && completeData.step_sand !== null && !isNaN(completeData.step_sand)) {
        const series = chart.get('step_sand');
        if (series) {
          addStreamPoint(series, now, completeData.step_sand);
        }
      }
      
      // Update x-axis extremes to show all data
      // First, find the earliest timestamp in all series
      const allPoints = [
        ...Array.from(chart.series as any[]).flatMap((s: any) => Array.from(s.points || []).map((p: any) => p.x as number)),
        now
      ];
      
      // Only update extremes if we have enough data points
      if (allPoints.length > 1) {
        const earliestTimestamp = Math.min(...allPoints);
        const latestTimestamp = now + (10 * 1000); // Add 10 seconds to ensure latest point is visible
        
        // Set extremes to show all data
        chart.xAxis[0].setExtremes(earliestTimestamp, latestTimestamp);
      } else {
        // Fallback to showing last 5 minutes if we don't have enough data
        const xMax = now + (10 * 1000); // Add 10 seconds to ensure latest point is visible
        const xMin = now - (5 * 60 * 1000); // 5 minutes in milliseconds
        chart.xAxis[0].setExtremes(xMin, xMax);
      }
      
      // Redraw the chart once after all updates
      chart.redraw();
    }
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found.');
        return;
      }
      const resp = await axios.get<Template[]>(`${backendUrl}/templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const chartTemplates = resp.data.filter((t) => 
        t.widget_type?.toUpperCase() === 'CHART'
      );
      setTemplates(chartTemplates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates.');
    }
  };
  
  // Handle delete template
  const handleDeleteTemplate = async (templateId: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found.');
        return;
      }
      
      // Call the delete endpoint
      await axios.delete(`${backendUrl}/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Refresh templates list
      fetchTemplates();
      
      toast.success('Template deleted successfully.');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template.');
    }
  };

// Fetch treatment data, wells, and stages for a pad
  const fetchPadData = async (padId: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Get current timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Prepare channels parameter
      const channelParam = selectedChannels.length > 0 
        ? selectedChannels.map(ch => ch.value).join(',') 
        : undefined;

      // Fetch wells for this pad
      const wellsResponse = await axios.get(`${backendUrl}/pads/${padId}/wells`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let wellsData: Well[] = [];
      if (wellsResponse.data && Array.isArray(wellsResponse.data)) {
        wellsData = wellsResponse.data;
      } else if (wellsResponse.data && wellsResponse.data.data && Array.isArray(wellsResponse.data.data)) {
        wellsData = wellsResponse.data.data;
      } else {
        console.error('Unexpected wells response format:', wellsResponse);
      }
      setWells(wellsData);

      // Fetch stages for each well
      const allStages: Stage[] = [];
      for (const well of wellsData) {
        const stagesResponse = await axios.get(`${backendUrl}/wells/${well.id}/stages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        allStages.push(...stagesResponse.data);
      }
      setStages(allStages);

      // Determine date window (only on initial load — respect user-set range otherwise)
      let dateParams: { from?: string; to?: string } = {};
      if (fromDate && toDate) {
        dateParams = {
          from: fromDate.toISOString(),
          to: toDate.toISOString()
        };
      } else if (allStages.length > 0) {
        const stageTimes = allStages
          .map(s => s.end_ts ?? s.start_ts)
          .filter(Boolean)
          .map(ensureMs);

        const now = Date.now();
        const twoDays = 2 * 24 * 60 * 60 * 1000;

        const latestStageTs = stageTimes.length > 0 ? Math.max(...stageTimes) : now;

        const windowTo = now - latestStageTs > twoDays ? new Date(latestStageTs) : new Date(now);
        const windowFrom = new Date(windowTo.getTime() - twoDays);

        setFromDate(windowFrom);
        setToDate(windowTo);
        dateParams = {
          from: windowFrom.toISOString(),
          to: windowTo.toISOString()
        };
      }

      // Fetch treatment data with the resolved window
      const treatmentResponse = await axios.get(`${backendUrl}/pads/${padId}/treatment_data`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          channels: channelParam,
          timezone: userTimezone,
          ...dateParams
        }
      });

      console.log(treatmentResponse.data)

      // Seismos metric events for the pad (sparse; plotted on their own event time)
      let seismosEvents: any[] = [];
      if (selectedChannels.some(c => c.seismos_metric)) {
        try {
          const seismosResponse = await axios.get(`${backendUrl}/seismos/events/pad/${padId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          seismosEvents = seismosResponse.data?.events || [];
        } catch (err) {
          console.error('Error fetching seismos events:', err);
        }
      }

      // Prepare chart data
      prepareChartData(treatmentResponse.data, allStages, wellsData, seismosEvents);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pad data:', err);
      setError('Failed to fetch pad data. Please try again later.');
      setLoading(false);
    }
  };

  // Prepare chart data from treatment data and stages
  const prepareChartData = (treatmentData: TreatmentData[], stages: Stage[], wellsData: Well[] = [], seismosEvents: any[] = []) => {
    // Hide the "View Treatment" button when preparing new chart data
    setShowViewTreatmentButton(false);
    
    if (treatmentData.length === 0) {
      setError('No treatment data available for this pad');
      return;
    }
    
    // Helper function to ensure timestamp is in milliseconds and convert from UTC to local time
    const normalizeTimestamp = (timestamp: number): number => {
      const timestampInMs = ensureMs(timestamp);
      const utcDate = new Date(timestampInMs);
      const timezoneOffsetMs = utcDate.getTimezoneOffset() * 60 * 1000;
      const localTimestamp = timestampInMs - timezoneOffsetMs;
      return localTimestamp;
    };

    // Sort treatment data by timestamp
    const sortedData = [...treatmentData].sort((a, b) => a.timestamp - b.timestamp);

    // Prepare series data based on selectedChannels ONLY
    const seriesData: { [key: string]: [number, number][] } = {};
    
    // Initialize arrays only for selected channels
    selectedChannels.forEach(channel => {
      seriesData[channel.value] = [];
    });

    sortedData.forEach(item => {
      if (item.timestamp) {
        const normalizedTimestamp = normalizeTimestamp(item.timestamp);
        
        // Only process data for selected channels
        selectedChannels.forEach(channel => {
          const channelValue = channel.value;
          const dataValue = item[channelValue as keyof TreatmentData];
          
          if (dataValue !== null && dataValue !== undefined) {
            seriesData[channelValue].push([normalizedTimestamp, Number(dataValue)]);
          }
        });
      }
    });

    // Seismos metric events are sparse and event-timed, so they bypass the 1Hz
    // treatment grid and plot on their own timestamps.
    const selectedSeismosMetrics = selectedChannels.filter(c => !!c.seismos_metric);
    if (selectedSeismosMetrics.length > 0) {
      seismosEvents.forEach(e => {
        const ms = seismosEventMs(e);
        if (ms == null) return;
        const x = normalizeTimestamp(ms);
        selectedSeismosMetrics.forEach(ch => {
          const val = seismosMetricValue(e, ch.seismos_metric as string);
          if (val != null) seriesData[ch.value].push([x, val]);
        });
      });
      selectedSeismosMetrics.forEach(ch => seriesData[ch.value].sort((a, b) => a[0] - b[0]));
    }

    // Create plot lines and bands for stages
    const newPlotLines: Highcharts.XAxisPlotLinesOptions[] = [];
    const newPlotBands: Highcharts.XAxisPlotBandsOptions[] = [];
    
    // Group stages by well
    const stagesByWell: { [key: number]: Stage[] } = {};
    stages.forEach(stage => {
      if (!stagesByWell[stage.well_id]) {
        stagesByWell[stage.well_id] = [];
      }
      stagesByWell[stage.well_id].push(stage);
    });
    
    // Get theme-based colors
    const isDarkTheme = theme === 'dark';
    const plotBandColor = isDarkTheme ? '#5C646B' : '#5C646B';
    const plotLineColor = isDarkTheme ? '#484E54' : '#888888';
    const labelTextColor = isDarkTheme ? '#ffffff' : '#333333';
    
    // Process all stages with the wells data
    Object.entries(stagesByWell).forEach(([wellId, wellStages]) => {
      const wellIdNum = parseInt(wellId);
      const well = wellsData.find(w => w.id === wellIdNum);
      
      wellStages.forEach(stage => {
        const stageNum = stage.stage_number;
        
        let wellNumber;
        if (well && well.well_name) {
          const matches = well.well_name.match(/\b(?:[A-Z]+\d+[A-Z]*|\d+[A-Z]+)\b/g);
          if (matches) {
            wellNumber = matches[matches.length - 1];
          } else {
            // Fallback: grab last word token (handles "Unit 4", "SA B 1", "USW A 1")
            const tokens = well.well_name.trim().split(/\s+/);
            wellNumber = tokens[tokens.length - 1];
          }
        } else {
          wellNumber = `#${wellIdNum}`;
        }
        
        if (stage.start_ts && stage.end_ts) {
          const localStartTs = normalizeTimestamp(stage.start_ts);
          const localEndTs = normalizeTimestamp(stage.end_ts);
          
          newPlotBands.push({
            from: localStartTs,
            to: localEndTs,
            color: plotBandColor,
            zIndex: 0,
            id: `stage-${stage.id}`,
            label: {
              text: `${stageNum}<br>${wellNumber}`,
              useHTML: true,
              style: {
                color: labelTextColor,
                fontWeight: 'bold',
                fontSize: '12px',
                textShadow: isDarkTheme ? '0px 0px 2px rgba(0,0,0,0.8)' : 'none',
                cursor: 'pointer'
              },
              align: 'center',
              verticalAlign: 'top',
              y: 15
            },
            events: {
              click: function(e: any) {
                console.log('Plot band clicked:', stage.id);
                
                if (well) {
                  setSelectedStage({
                    stageId: stage.id,
                    wellId: well.id,
                    stageNumber: stage.stage_number
                  });
                  
                  setViewTreatmentButtonPosition({
                    x: e.chartX || 450,
                    y: e.chartY || 300
                  });
                  
                  setShowViewTreatmentButton(true);
                }
              }
            }
          });
          
          newPlotLines.push({
            color: plotLineColor,
            width: 1,
            value: localStartTs,
            zIndex: 1
          });
          
          newPlotLines.push({
            color: plotLineColor,
            width: 1,
            value: localEndTs,
            zIndex: 1
          });
        } else if (stage.start_ts) {
          const localStartTs = normalizeTimestamp(stage.start_ts);
          
          newPlotLines.push({
            color: plotLineColor,
            width: 1,
            value: localStartTs,
            zIndex: 1,
            label: {
              text: `${stageNum}<br>${wellNumber}`,
              useHTML: true,
              style: {
                color: labelTextColor,
                fontWeight: 'bold',
                fontSize: '12px',
                textShadow: isDarkTheme ? '0px 0px 2px rgba(0,0,0,0.8)' : 'none'
              },
              align: 'center',
              y: 15
            }
          });
        }
      });
    });

    // Adjust the date range for timezone to match the chart data
    const adjustTimestamp = (date: Date | null): number | undefined => {
      if (!date) return undefined;
      const timestamp = date.getTime();
      const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
      return timestamp - timezoneOffsetMs;
    };
    
    // Create series ONLY for selected channels with proper axis assignment
  const newSeries: Highcharts.SeriesOptionsType[] = selectedChannels.map((channel, index) => {
    const channelValue = channel.value;
    const yAxisIndex = getYAxisIndex(axisConfig[channelValue]?.axis);
    return {
      type: 'line',
      id: channelValue,
      name: channel.label || getFriendlyChannelName(channelValue),
      data: seriesData[channelValue] || [],
      yAxis: yAxisIndex,
      zIndex: index + 1,
      lineWidth: 1,
      color: CHANNEL_COLORS[channelValue],   // <-- add this
      states: { hover: { enabled: false } },
      // Seismos metrics are sparse events — invisible without markers.
      marker: channel.seismos_metric
        ? { enabled: true, symbol: 'circle', radius: 4 }
        : { enabled: false, symbol: 'circle' },
      turboThreshold: 0,
      boostThreshold: 5000
    };
  });

    // Update state instead of setChartOptions
    setChartSeries(newSeries);
    
    // Store the complete sets for zoom filtering
    setAllPlotBands(newPlotBands);
    setAllPlotLines(newPlotLines);
    
    // Initially show all bands and lines (no filter)
    setPlotBands(newPlotBands);
    setPlotLines(newPlotLines);
    
    setXAxisRange({
      min: sortedData.length > 0 ? normalizeTimestamp(sortedData[0].timestamp) : adjustTimestamp(fromDate),
      max: sortedData.length > 0 ? normalizeTimestamp(sortedData[sortedData.length - 1].timestamp) : adjustTimestamp(toDate),
    });
    setChartTitle('Treatment Data');
  };

  // Handle date range change
  // Double-click a stage band on the chart to load just that stage.
  // Chart x-space is normalizeTimestamp(ts), so match there; the pickers take
  // the raw ts, which renders as the same wall clock and sends correct UTC.
  const handleChartDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const chart = chartRef.current?.chart;
    if (!chart || isRealTimeStreaming) return;
    const rect = chart.container.getBoundingClientRect();
    const x = chart.xAxis[0].toValue(e.clientX - rect.left);
    const stage = stages.find(st => st.start_ts && st.end_ts &&
      x >= normalizeTimestamp(st.start_ts) && x <= normalizeTimestamp(st.end_ts));
    if (!stage) return;
    setFromDate(new Date(ensureMs(stage.start_ts)));
    setToDate(new Date(ensureMs(stage.end_ts)));
    setIsStageView(true);
    setPendingRangeLoad(true);
  };

  // fetchPadData closes over fromDate/toDate, so fire on the next render
  // once the picker state has actually committed.
  useEffect(() => {
    if (!pendingRangeLoad) return;
    setPendingRangeLoad(false);
    if (selectedPadId) fetchPadData(selectedPadId);
  }, [pendingRangeLoad, fromDate, toDate, selectedPadId]);

  // Back to the whole job: clearing the range makes fetchPadData fall into
  // its own default-window branch, so there is nothing to store or restore.
  const handleShowFullJob = () => {
    setFromDate(null);
    setToDate(null);
    setIsStageView(false);
    setPendingRangeLoad(true);
  };

  // PRC Plot: print the live chart SVG plus the Seismos table. Highcharts runs
  // in non-styled mode so the SVG carries its own colors and needs no page CSS.
  const handleOpenPrcPlot = () => {
    const chart = chartRef.current?.chart;
    if (!chart) { toast.error('Chart is not ready yet.'); return; }
    // On screen the chart is ~1900x500 (3.8:1), which scales to a short strip
    // on paper with unreadable labels. Re-render at 16:9 for the capture so it
    // fills a landscape page, then restore. Synchronous — never painted.
    // 2:1 keeps the chart + table on a single landscape sheet (see 16:9 = overflow).
    const prevW = chart.chartWidth;
    const prevH = chart.chartHeight;
    // Stage shading reads as muddy grey on a light background, and its stage
    // label is redundant with the title, so drop the bands for the capture.
    const axis = chart.xAxis[0];
    const bands = plotBands.filter(b => b.id);
    let captured = '';
    try {
      bands.forEach(b => axis.removePlotBand(b.id as string));
      chart.setSize(1600, 800, false);
      captured = chart.container.querySelector('svg')?.outerHTML || '';
    } finally {
      chart.setSize(prevW, prevH, false);
      bands.forEach(b => axis.addPlotBand(b));
      chart.reflow();
    }
    if (!captured) { toast.error('Chart is not ready yet.'); return; }
    // viewBox + meet lets the SVG letterbox inside whatever box the flex
    // layout gives it, at any paper size, without distorting.
    const svg = captured.replace('<svg ', '<svg viewBox="0 0 1600 800" preserveAspectRatio="xMidYMid meet" ');
    // Clone before stripping so the on-screen table keeps its issue rows and
    // hover popovers. Rate Drop Issues / Well Issues are a screen-only detail —
    // dropping them on paper buys the chart the height instead.
    const srcTable = document.getElementById('seismos-prc-table');
    let table = '<p class="prc-none">No Seismos events for this stage</p>';
    if (srcTable) {
      const clone = srcTable.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('td.seismos-issue-cell')
           .forEach(td => td.closest('tr')?.remove());
      // The <table> carries an inline style={{fontSize:'0.9rem',lineHeight:1.5}}
      // for the on-screen card. Inline wins over the popup's stylesheet, so
      // strip it here or the print CSS below is silently ignored — that's what
      // was rendering the table at 14.4px and clipping the parameter labels.
      clone.querySelector('table')?.removeAttribute('style');
      table = clone.outerHTML;
    }
    // The popup is about:blank, so a bundler-relative asset path has no base
    // to resolve against — make it absolute against the app origin.
    const logoUrl = new URL(seismos, window.location.href).href;
    const prcWell = wells.find(w => w.id === selectedStage?.wellId);
    const prcTitle = ['PRC Plot', prcWell?.well_name, selectedStage?.stageNumber != null
      ? `Stage ${selectedStage.stageNumber}` : null].filter(Boolean).join(' — ');
    // Reuse the theme pairs already used throughout this component so the
    // export matches the on-screen chart, which is theme-aware already.
    const c = theme === 'dark'
      ? { bg: '#231F20', fg: '#e5e5e5', border: '#5C646B', head: '#303335', accent: '#F9C430' }
      : { bg: '#ffffff', fg: '#333333', border: '#e6e6e6', head: '#f8f9fa', accent: '#5a4000' };
    const win = window.open('', '_blank');
    if (!win) { toast.error('Popup blocked \u2014 allow popups to export the PRC plot.'); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>${prcTitle}</title><style>
      *{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
      html,body{margin:0}
      /* One page, guaranteed: the body is exactly one page tall and clips.
         In print, 100vh resolves to the page box, so this holds on Letter and
         A4 alike without naming a paper size. Everything but the chart is
         flex:none, so the chart absorbs whatever height is left over — no
         magic inch value to re-tune when the table gains or loses a row. */
      body{background:${c.bg};color:${c.fg};font-family:Arial,Helvetica,sans-serif;
           padding:3mm 5mm;height:100vh;overflow:hidden;display:flex;flex-direction:column}
      h3{flex:none;color:${c.accent};margin:0 0 2px;font-size:10px}
      svg{flex:1 1 0;min-height:0;width:100%;height:auto;display:block}
      img#prc-logo{flex:none;height:15px;margin:2px auto 0}
      /* !important as a guard: the source table is inline-styled, and any new
         inline style added to it on screen would otherwise silently override
         the print sizing again. */
      table{flex:none;width:100%;border-collapse:collapse;table-layout:fixed;margin-top:2px;
            font-size:9px!important;line-height:1.25!important}
      th,td{border:1px solid ${c.border};padding:0 3px;text-align:left;color:${c.fg};white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      thead th{background:${c.head};color:${c.accent}}
      /* 106px holds the longest label, "Hydraulic Diameter (in)" at 98px
         including padding, measured at 9px Arial. The ~8px of slack is the
         margin for a wider font substitution — re-measure before trimming it,
         and re-measure if the font-size above changes. */
      th:first-child,td:first-child{width:106px}
      .prc-none{flex:none;text-align:center;font-size:9px;margin:3px 0 0;color:${c.fg};opacity:.7}
      /* margin:0 removes the page margin and suppresses Chrome's header/footer.
         Orientation only — naming a paper size fights the user's Letter/A4 default. */
      @page{size:landscape;margin:0}
    </style></head><body><h3>${prcTitle}</h3>${svg}
      <img id="prc-logo" src="${logoUrl}" alt="Seismos">${table}
      <script>var p=false;function go(){if(p)return;p=true;window.focus();window.print();}window.onload=go;setTimeout(go,3000);<\/script>
    </body></html>`);
    win.document.close();
  };

  const handleDateRangeChange = () => {
    if (fromDate && toDate) {
      // If we have a selected pad, fetch new data with the date range
      if (selectedPadId) {
        console.log('running 4')
        fetchPadData(selectedPadId);
      } else {
        // If no pad is selected, just update the x-axis range
        const adjustTimestamp = (date: Date): number => {
          const timestamp = date.getTime();
          const timezoneOffsetMs = date.getTimezoneOffset() * 60 * 1000;
          return timestamp - timezoneOffsetMs;
        };
        
        // UPDATE: Use setXAxisRange instead of setChartOptions
        setXAxisRange({
          min: adjustTimestamp(fromDate),
          max: adjustTimestamp(toDate)
        });
      }
    }
  };
    
  // Custom styles for date picker to match theme
  const datePickerCustomStyles = {
    className: `form-control ${theme === 'dark' ? 'bg-dark text-light border-secondary' : ''}`,
    wrapperClassName: 'w-100',
    popperClassName: theme === 'dark' ? 'react-datepicker-dark' : '',
    dateFormat: "MMM d, yyyy h:mm aa"
  };

  // Handle changes to axis settings (min/max values)
  const handleAxisSettingChange = (axis: string, field: string, value: number | undefined) => {
    console.log('handleAxisSettingChange called:', axis, field, value);
    
    setAxisSettings(prev => {
      const updatedSettings = {
        ...prev,
        [axis]: {
          ...prev[axis],
          [field]: value
        }
      };
      
      const min = updatedSettings[axis].min;
      const max = updatedSettings[axis].max;
      
      if (min !== undefined && max !== undefined && max < min) {
        toast.error(`For axis "${axis}", max value cannot be lower than min value.`);
        return prev;
      }
      
      console.log('Updated axis settings:', updatedSettings);
      
      // The useEffect above will handle the chart update automatically
      return updatedSettings;
    });
  };

  // Original channel-based axis change function (kept for backward compatibility)
  const handleChannelAxisAssignment = (axis: string, selectedChannelValues: string[]) => {
    console.log(`Assigning channels to ${axis}:`, selectedChannelValues);
    
    // Update axis settings with new channel assignments
    setAxisSettings(prev => {
      // Create a copy of the current settings
      const updatedSettings = { ...prev };
      
      // First, remove the selected channels from all axes
      Object.keys(updatedSettings).forEach(axisKey => {
        updatedSettings[axisKey].channels = updatedSettings[axisKey].channels.filter(
          ch => !selectedChannelValues.includes(ch)
        );
      });
      
      // Then add the selected channels to the target axis
      updatedSettings[axis].channels = selectedChannelValues;
      
      console.log(`Updated axis settings for ${axis}:`, updatedSettings[axis].channels);
      return updatedSettings;
    });
    
    // Update the channel-to-axis mapping
    setAxisConfig(prev => {
      const updatedConfig = { ...prev };
      
      // First, remove any channel mappings for channels that are no longer selected
      const previouslyAssignedChannels = Object.keys(prev).filter(channel => 
        prev[channel]?.axis === axis
      );
      
      previouslyAssignedChannels.forEach(channel => {
        if (!selectedChannelValues.includes(channel)) {
          // If this channel was previously assigned to this axis but is no longer selected,
          // remove its axis assignment
          delete updatedConfig[channel];
        }
      });
      
      // Then update the axis for each selected channel
      selectedChannelValues.forEach(channel => {
        updatedConfig[channel] = {
          ...(updatedConfig[channel] || { min: undefined, max: undefined }),
          axis: axis
        };
      });
      
      console.log('Updated axisConfig:', updatedConfig);
      return updatedConfig;
    });
    
    setTimeout(() => {
      console.log('Updating series after channel reassignment');
      
      setChartSeries(prevSeries => {
        return prevSeries.map((series: any) => {
          const seriesId = series.id;
          if (seriesId) {
            // Find which axis this channel is now assigned to
            let newAxisName = 'left1'; // Default
            
            // Check axisSettings first
            Object.keys(axisSettings).forEach(axisKey => {
              if (axisSettings[axisKey].channels.includes(seriesId)) {
                newAxisName = axisKey;
              }
            });
            
            // Check if this channel was just reassigned
            if (selectedChannelValues.includes(seriesId)) {
              newAxisName = axis;
            }
            
            const newYAxisIndex = getYAxisIndex(newAxisName);
            console.log(`Reassigning series ${seriesId} to ${newAxisName} (index ${newYAxisIndex})`);
            
            return {
              ...series,
              yAxis: newYAxisIndex
            };
          }
          return series;
        });
      });
      
      // Force chart redraw
      if (chartRef.current && chartRef.current.chart) {
        chartRef.current.chart.redraw(true);
      }
    }, 100);
  };

  const handleEditTemplate = async (template: Template) => {
    const newTemplateName = prompt('Enter new template name:', template.template_name);
    if (!newTemplateName) {
      toast.warning('Template name cannot be empty.');
      return;
    }

    const configuration = selectedChannels.map((ch) => ({
      channel: ch.value,
      display_position: axisConfig[ch.value]?.axis || 'left1',
      min_value:
        typeof axisConfig[ch.value]?.min === 'number'
          ? axisConfig[ch.value]?.min
          : null,
      max_value:
        typeof axisConfig[ch.value]?.max === 'number'
          ? axisConfig[ch.value]?.max
          : null,
      axis: axisConfig[ch.value]?.axis
        ? parseInt(axisConfig[ch.value]?.axis.replace(/\D/g, ''))
        : null,
    }));

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found.');
        return;
      }
      await axios.put(
        `${backendUrl}/templates/${template.id}`,
        {
          template_name: newTemplateName,
          channels: configuration,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('Template updated successfully.');
      fetchTemplates();
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template.');
    }
  };
  
  const handleTemplateSelect = (template: any) => {
    console.log('handleTemplateSelect called with template:', template);
    
    if (template) {
      setSelectedTemplate(template);
      const fullTemplate = templates.find(t => t.id === template.id);
      if (fullTemplate) {
        applyTemplate(fullTemplate);
      }
    } else {
      setSelectedTemplate(null);
    }
    setShowSettingsModal(false);
  };

  const applyTemplate = (template: Template) => {
    console.log('Applying template:', template);
    
    // Resolve against the known channel list so markers (chem_name,
    // momentum_well_id, seismos_well_id, seismos_metric) survive the round-trip.
    // Templates only persist the channel key, and a bare {value,label} would
    // land the channel in the wrong group and skip its data-loading path.
    const selected = template.channels.map((ch) => {
      const known = channels.find(c => c.value === ch.channel);
      return known || {
        value: ch.channel,
        label: getFriendlyChannelName(ch.channel),
      };
    });
    setSelectedChannels(selected);

    // Update axisConfig for backward compatibility
    const axisConf: {
      [channel: string]: { axis: string; min: number | undefined; max: number | undefined };
    } = {};
    template.channels.forEach((ch) => {
      axisConf[ch.channel] = {
        axis: ch.display_position,
        min: ch.min_value !== null ? ch.min_value : undefined,
        max: ch.max_value !== null ? ch.max_value : undefined,
      };
    });

    // Validate min/max
    Object.entries(axisConf).forEach(([channel, config]) => {
      if (config.min !== undefined && config.max !== undefined && 
          typeof config.min === 'number' && typeof config.max === 'number' && 
          config.max < config.min) {
        toast.error(`For channel "${channel}", max < min.`);
        axisConf[channel].max = config.min;
      }
    });

    setAxisConfig(axisConf);

    // ALSO update axisSettings directly
    const newAxisSettings = {
      left1: { channels: [], min: undefined, max: undefined },
      left2: { channels: [], min: undefined, max: undefined },
      right1: { channels: [], min: undefined, max: undefined },
      right2: { channels: [], min: undefined, max: undefined }
    };

    // Group channels by axis and get min/max values
    template.channels.forEach((ch) => {
      const axisName = ch.display_position as keyof typeof newAxisSettings;
      if (newAxisSettings[axisName]) {
        newAxisSettings[axisName].channels.push(ch.channel);
        // Use the first channel's min/max for the entire axis
        if (newAxisSettings[axisName].min === undefined) {
          newAxisSettings[axisName].min = ch.min_value !== null ? ch.min_value : undefined;
          newAxisSettings[axisName].max = ch.max_value !== null ? ch.max_value : undefined;
        }
      }
    });

    console.log('Setting new axis settings:', newAxisSettings);
    setAxisSettings(newAxisSettings);
  };

  // Handle save schedule
  const requestChemEditLock = async (): Promise<boolean> => {
    if (!currentStage) return false;
    const stageId = currentStage.id;
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Authentication token not found'); return false; }
      let vanId = '';
      if (subscribedTopics.size > 0) {
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) { const parts = topic.split('/'); if (parts.length >= 3) { vanId = parts[1]; break; } }
        }
      }
      if (!vanId) vanId = localStorage.getItem('currentVanId') || 'DV1017';
      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${stageId}/request_edit_lock`,
        { vanId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.lockAcquired) {
        setHasChemEditLock(true);
        startLockHeartbeat(stageId);
        toast.success(response.data.message || 'Chemical edit lock acquired');
        return true;
      } else {
        toast.error(response.data.message || 'Could not acquire chemical edit lock');
        return false;
      }
    } catch (error) {
      toast.error('Failed to request chemical edit lock');
      return false;
    }
  };

  const releaseChemEditLock = async (): Promise<void> => {
    if (!currentStage) return;
    const stageId = currentStage.id;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      let vanId = '';
      if (subscribedTopics.size > 0) {
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) { const parts = topic.split('/'); if (parts.length >= 3) { vanId = parts[1]; break; } }
        }
      }
      if (!vanId) vanId = localStorage.getItem('currentVanId') || 'DV1017';

      // Treatment and chem share one broker lock. Only call release_lock when the
      // OTHER lock is not also held — otherwise just clear the chem UI state
      // and let the remaining lock (and its heartbeat) keep the broker lock alive.
      if (!hasEditLock) {
        await axios.post(
          `${backendUrl}/stages/mqtt/${stageId}/release_lock`,
          { vanId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        stopLockHeartbeat();
      }
    } catch (error) {
      console.error('Error releasing chem edit lock:', error);
    }
    setHasChemEditLock(false);
    setEditedChemLoadings({});
    setEditedChemGroupRatios({});
  };

  const fetchNextChemSchedule = async (stageId: number | null | undefined) => {
    if (!stageId) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      setNextChemLoading(true);
      const resp = await axios.get(`${backendUrl}/stages/${stageId}/chem_schedule_pivoted`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNextChemSchedule(resp.data);
    } catch (err) {
      console.error('Error fetching chem schedule:', err);
    } finally {
      setNextChemLoading(false);
    }
  };

  const handleSaveChemSchedule = async () => {
    if (!currentStage) return;
    const token = localStorage.getItem('token');
    if (!token) { toast.error('Authentication token not found'); return; }

    let vanId = '';
    if (subscribedTopics.size > 0) {
      for (const topic of Array.from(subscribedTopics)) {
        if (topic.includes('/petrix/data')) {
          const parts = topic.split('/');
          if (parts.length >= 3) { vanId = parts[1]; break; }
        }
      }
    }
    if (!vanId) vanId = localStorage.getItem('currentVanId') || '';
    if (!vanId) { toast.error('Could not determine Van ID'); return; }

    setIsSavingChem(true);
    try {
      // Build change description covering both single-group (editedChemLoadings)
      // and multi-group (editedChemGroupRatios) edits, with before → after values.
      // Build per-step change map: { stepNum -> string[] of "ChemLabel: old -> new gpt" }
      const changesByStep: Record<number, string[]> = {};
      // Strip trailing zeros: 1.000 -> "1", 2.500 -> "2.5", 0.170 -> "0.17"
      const fmtVal = (v: number) => parseFloat(v.toFixed(3)).toString();
      const addChange = (step: number, label: string, oldVal: number, newVal: number) => {
        if (oldVal === newVal) return; // skip unchanged
        if (!changesByStep[step]) changesByStep[step] = [];
        changesByStep[step].push(`${label}: ${fmtVal(oldVal)} -> ${fmtVal(newVal)} gpt`);
      };

      Object.entries(editedChemLoadings).forEach(([key, newVal]) => {
        const [stepStr, name] = key.split('::');
        const step = Number(stepStr);
        const chemStep = nextChemSchedule?.steps?.find((s: any) => s.step === step);
        const oldVal = chemStep?.chems?.[name]?.design
          ?? (rawScheduleChemicals[step] || []).find(c => c.name === name)?.ratio
          ?? 0;
        addChange(step, name, oldVal, newVal);
      });

      Object.entries(editedChemGroupRatios).forEach(([key, groups]) => {
        const [stepStr, name] = key.split('::');
        const step = Number(stepStr);
        const origGroups = (rawScheduleChemicals[step] || []).filter(c => c.name === name);
        groups.forEach(g => {
          const oldVal = origGroups.find(o => o.group === g.group)?.ratio ?? 0;
          const label = g.group ? `${name} (${g.group})` : name;
          addChange(step, label, oldVal, g.ratio); // addChange skips if unchanged
        });
      });

      // Format: collapse consecutive steps with identical change sets into a
      // range so a cascade across many steps reads as one block, e.g.
      // "Steps 4-8:\n  AG-1 (Backup): 5 -> 4 gpt\n  FR: 7 -> 5 gpt"
      const sortedChemEntries = Object.entries(changesByStep)
        .map(([step, parts]) => ({ step: Number(step), parts }))
        .sort((a, b) => a.step - b.step);
      const chemGroups: { start: number; end: number; parts: string[] }[] = [];
      for (const entry of sortedChemEntries) {
        const last = chemGroups[chemGroups.length - 1];
        const sig = [...entry.parts].sort().join('|');
        const lastSig = last ? [...last.parts].sort().join('|') : '';
        if (last && last.end + 1 === entry.step && sig === lastSig) {
          last.end = entry.step;
        } else {
          chemGroups.push({ start: entry.step, end: entry.step, parts: entry.parts });
        }
      }
      const changedChems = chemGroups
        .map(g => {
          const label = g.start === g.end ? `Step ${g.start}` : `Steps ${g.start}-${g.end}`;
          return `${label}:\n  ${g.parts.join('\n  ')}`;
        })
        .join('\n');

      // ── Petrix 7.0.1 ── No chemical array support — post as a plain message instead
      if (!petrixHasChemSupport) {
        const message = `Chemical loading update requested:\n${
          Object.entries(editedChemLoadings).map(([key, val]) => {
            const [step, name] = key.split('::');
            return `Step ${step} - ${name}: ${val.toFixed(3)} gpt`;
          }).join('\n')
        }`;

        const resp = await axios.post(
          `${backendUrl}/mqtt/${vanId}/post_message`,
          { message },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (resp.data === true) {
          setEditedChemLoadings({});
          setEditedChemGroupRatios({});
          fetchChemicalChanges(currentStage.id);
          toast.success(`Chemical update sent as message to field operator (Petrix 7.0.1 mode): ${changedChems}`, {
            position: 'top-right', autoClose: 5000,
          });
        } else {
          toast.error('Failed to send chemical update message', { position: 'top-right' });
        }
        return;
      }

      // ── Petrix 8.0 ── Full chemical array support — send via actual_treatment_schedule
      // Every step in rawScheduleChemicals now includes all chemicals+groups, so we just
      // use it directly as the base and overlay any edits on top.

      const scheduleWithChems = treatmentSchedule.map(t => {
        const editedStep = editedTreatmentSteps[t.id] || {};
        const rate   = editedStep.rate   ?? t.rate;
        const conc   = editedStep.conc   ?? t.conc;
        const clean  = editedStep.clean_step ?? t.clean_step;
        const type   = editedStep.type   ?? t.type;
        const desc   = editedStep.description ?? (t.description || '');
        let targetStartConc = conc, targetEndConc = conc;
        if (type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp') {
          targetStartConc = (t as any).start_conc ?? conc;
          targetEndConc   = (t as any).end_conc   ?? conc;
        }
        const origStep = (t as any).originalStep ?? (t.id > 1000000000000 ? 0 : t.step);

        // Build chemicals array from rawScheduleChemicals (complete per step).
        // Group chems by name to apply edits correctly.
        const stepRawChems = rawScheduleChemicals[t.step] || [];
        const chemicals: {name: string, group: string, ratio: number}[] = [];

        if (stepRawChems.length > 0) {
          const chemsByName: Record<string, {name: string, group: string, ratio: number}[]> = {};
          stepRawChems.forEach(c => {
            if (!chemsByName[c.name]) chemsByName[c.name] = [];
            chemsByName[c.name].push(c);
          });

          Object.entries(chemsByName).forEach(([chemName, groups]) => {
            const k = `${t.step}::${chemName}`;
            if (groups.length > 1) {
              // Multi-group: use edited group ratios if available, else originals from schedule
              const editedGroups = editedChemGroupRatios[k];
              const source = editedGroups ?? groups.map(g => ({ group: g.group, ratio: g.ratio }));
              source.forEach(g => chemicals.push({ name: chemName, group: g.group, ratio: g.ratio }));
            } else {
              // Single-group: use inline edit or original ratio
              const loading = editedChemLoadings[k] !== undefined
                ? editedChemLoadings[k]
                : groups[0].ratio;
              chemicals.push({ name: chemName, group: groups[0].group, ratio: loading });
            }
          });
        } else {
          // No schedule topic data — fall back to DB chem schedule
          const chemStep = nextChemSchedule?.steps?.find((s: any) => s.step === t.step);
          nextChemSchedule?.chem_names
            ?.filter((name: string) => {
              const entry = chemStep?.chems?.[name];
              return (entry?.design != null) || (`${t.step}::${name}` in editedChemLoadings);
            })
            ?.forEach((name: string) => {
              const k = `${t.step}::${name}`;
              const loading = editedChemLoadings[k] !== undefined
                ? editedChemLoadings[k]
                : (chemStep?.chems?.[name]?.design ?? 0);
              chemicals.push({ name, group: '', ratio: loading });
            });
        }

        // Append any chemicals from the /chem live topic that were edited but are
        // not yet in the chemicals array (i.e. they appear in the chem topic but not
        // in the schedule topic for this step). Assume group "" per Petrix convention.
        const includedNames = new Set(chemicals.map(c => c.name));
        realTimeChemData.forEach((lc: any) => {
          const k = `${t.step}::${lc.name}`;
          if (!includedNames.has(lc.name) && editedChemLoadings[k] !== undefined) {
            chemicals.push({ name: lc.name, group: '', ratio: editedChemLoadings[k] });
            includedNames.add(lc.name);
          }
        });

        return {
          step: t.step, type, description: desc,
          targetRate: rate, targetStartConc, targetEndConc,
          clean, trueDensity: 0, originalStep: origStep,
          chemicals,
        };
      });

      const resp = await axios.post(
        `${backendUrl}/stages/mqtt/${currentStage.id}/actual_treatment_schedule`,
        {
          vanId,
          schedule: scheduleWithChems,
          changes: `Chemical loadings updated: ${changedChems}`,
          is_chem_change: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (resp.data?.success) {
        // Optimistic update: apply edits to nextChemSchedule (DB-sourced display)
        if (nextChemSchedule) {
          setNextChemSchedule({
            ...nextChemSchedule,
            steps: nextChemSchedule.steps.map((row: any) => ({
              ...row,
              chems: Object.fromEntries(
                nextChemSchedule.chem_names.map((name: string) => {
                  const k = `${row.step}::${name}`;
                  return [name, {
                    ...row.chems[name],
                    design: editedChemLoadings[k] !== undefined
                      ? editedChemLoadings[k]
                      : (row.chems[name]?.design ?? null),
                  }];
                })
              ),
            })),
          });
        }

        // rawScheduleChemicals is updated automatically when Petrix echoes the saved
        // schedule back on the MQTT schedule topic — no manual update needed here.

        setEditedChemLoadings({});
        setEditedChemGroupRatios({});
        // Keep lock open — user can continue editing (mirrors treatment behaviour)
        fetchChemicalChanges(currentStage.id);
        fetchNextChemSchedule(currentStage.id);  // refresh after chem save
        toast.success(`Success - Chemical loadings updated: ${changedChems}`, {
          position: 'top-right', autoClose: 5000,
        });
      } else {
        toast.error(`Update rejected: ${resp.data?.message}`, { position: 'top-right' });
      }
    } catch (err: any) {
      toast.error(`Error - ${err.response?.data?.message || err.message || 'Failed to save'}`, {
        position: 'top-right',
      });
    } finally {
      setIsSavingChem(false);
    }
  };

  const handleSaveSchedule = async () => {
    // Check if there are any changes to the treatment schedule
    if (Object.keys(editedTreatmentSteps).length === 0) {
      toast.info("No changes detected", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    // Set loading state
    setIsSavingSchedule(true);

    // Store original schedule before making changes (for revert on failure)
    const originalScheduleBackup = [...treatmentSchedule];

    try {
      // Extract the van_id from the MQTT subscription topic
      // The topic format is "propilot/{vanId}/petrix/data"
      let vanId = "";
      
      // Check if we have any subscribed topics
      if (subscribedTopics.size > 0) {
        // Find a topic that matches the pattern
        for (const topic of Array.from(subscribedTopics)) {
          if (topic.includes('/petrix/data')) {
            // Extract the van_id from the topic
            const parts = topic.split('/');
            if (parts.length >= 3) {
              vanId = parts[1]; // The van_id is the second part of the topic
              console.log('Extracted vanId from MQTT topic:', vanId);
              break;
            }
          }
        }
      }
      
      // Fallback to localStorage if still no vanId
      if (!vanId) {
        vanId = localStorage.getItem('currentVanId') || "";
      }
      
      if (!vanId) {
        toast.error("Could not determine van ID", {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }
      
      console.log('Using vanId for treatment schedule update:', vanId);

      // Create a copy of the current treatment schedule with the edited values
      const updatedSchedule = treatmentSchedule.map(step => {
        // Get the edited values or use the original values
        const editedStep = editedTreatmentSteps[step.id] || {};
        const rate = editedStep.rate !== undefined ? editedStep.rate : step.rate;
        const conc = editedStep.conc !== undefined ? editedStep.conc : step.conc;
        const type = editedStep.type !== undefined ? editedStep.type : step.type;
        const description = editedStep.description !== undefined ? editedStep.description : (step.description || "");

        // When editing by sand step, back-calculate clean from the edited sand_step.
        // editedStep.clean_step is undefined in that mode — using it would send the stale original value.
        const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
        const clean = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
          ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
          : (editedStep.clean_step !== undefined ? editedStep.clean_step : step.clean_step);
        
        // Resolve targetStartConc and targetEndConc.
        // Only Sand Ramp steps have a split start/end conc — Proppant always has matching values.
        // Priority: explicitly edited value > original step value > conc fallback.
        let targetStartConc: number;
        let targetEndConc: number;

        if (type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp') {
          targetStartConc = editedStep.start_conc !== undefined
            ? editedStep.start_conc
            : ((step as any).start_conc !== undefined ? (step as any).start_conc : conc);
          targetEndConc = editedStep.end_conc !== undefined
            ? editedStep.end_conc
            : ((step as any).end_conc !== undefined ? (step as any).end_conc : conc);
        } else {
          targetStartConc = conc;
          targetEndConc = conc;
        }
        
        // Transform to the required format
        // Determine originalStep based on whether this is a new step or existing step
        let originalStep: string | number = 0;
        
        // Check if this step has an originalStep property already (from database)
        if ((step as any).originalStep !== undefined) {
          originalStep = (step as any).originalStep;
        } else {
          // For steps that don't have originalStep in database, determine based on ID
          // If the ID is a timestamp (indicating it was added in this session), set originalStep to 0
          // Otherwise, use the current step number as the original
          if (step.id > 1000000000000) { // Timestamp-based ID (new step)
            originalStep = 0;
          } else {
            originalStep = step.step; // Existing step from database
          }
        }
        
        return {
          step: step.step,
          type: type,
          description: description,
          targetRate: rate,
          targetStartConc: targetStartConc,
          targetEndConc: targetEndConc,
          clean: clean,
          trueDensity: conc, // Keep original conc for trueDensity
          originalStep: originalStep,
          // Carry existing chemicals from the schedule topic unchanged -
          // treatment tab edits never touch chemicals, so we must preserve them
          // or Petrix will receive chemicals: [] and wipe the chem array.
          chemicals: (rawScheduleChemicals[step.step] || []).map(c => ({
            name: c.name, group: c.group, ratio: c.ratio,
          })),
        };
      });

      // Get the token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found');
        return;
      }

      // Generate changes description before sending
      const currentScheduleWithChanges = treatmentSchedule.map(step => {
        const editedStep = editedTreatmentSteps[step.id];
        if (editedStep) {
          const type = editedStep.type !== undefined ? editedStep.type : step.type;
          const conc = editedStep.conc !== undefined ? editedStep.conc : step.conc;
          const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
          const effectiveCleanStep = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
            ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
            : (editedStep.clean_step !== undefined ? editedStep.clean_step : step.clean_step);
          return {
            ...step,
            rate: editedStep.rate !== undefined ? editedStep.rate : step.rate,
            conc,
            clean_step: effectiveCleanStep,
            type,
            description: editedStep.description !== undefined ? editedStep.description : step.description
          };
        }
        return step;
      });
      
      const changesMessage = generateChangeDescription(currentScheduleWithChanges, originalTreatmentSchedule);

      // Send the request to the backend
      const response = await axios.post(
        `${backendUrl}/stages/mqtt/${currentStage?.id}/actual_treatment_schedule`,
        {
          vanId: vanId,
          schedule: updatedSchedule,
          changes: changesMessage || "No changes detected"
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Check the response - backend returns {success: boolean, message: string}
      if (response.data && response.data.success === true) {
        // Generate changes message for successful update
        const currentScheduleWithChanges = treatmentSchedule.map(step => {
          const editedStep = editedTreatmentSteps[step.id];
          if (editedStep) {
            const type = editedStep.type !== undefined ? editedStep.type : step.type;
            const conc = editedStep.conc !== undefined ? editedStep.conc : step.conc;
            const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
            const effectiveCleanStep = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
              ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
              : (editedStep.clean_step !== undefined ? editedStep.clean_step : step.clean_step);
            return {
              ...step,
              rate: editedStep.rate !== undefined ? editedStep.rate : step.rate,
              conc,
              clean_step: effectiveCleanStep,
              type,
              description: editedStep.description !== undefined ? editedStep.description : step.description
            };
          }
          return step;
        });
        
        const changesMessage = generateChangeDescription(currentScheduleWithChanges, originalTreatmentSchedule);
        
        toast.success(`Treatment schedule successfully updated. Changes: ${changesMessage || 'No changes detected'}`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        fetchTreatmentChanges(currentStage?.id)
        fetchChemicalChanges(currentStage?.id)
        fetchNextChemSchedule(currentStage?.id)
        
        // Update the treatmentSchedule state with the new values, including start_conc and end_conc
        const updatedSchedule = treatmentSchedule.map(step => {
          const editedStep = editedTreatmentSteps[step.id];
          if (editedStep) {
            const type = editedStep.type !== undefined ? editedStep.type : step.type;
            const conc = editedStep.conc !== undefined ? editedStep.conc : step.conc;
            const isProppantOrSandRamp = type === 'Proppant' || type === 'Rate Drop-Proppant' || type === 'Sand Ramp' || type === 'Rate Drop-Sand Ramp';
            const effectiveCleanStep = (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
              ? (conc > 0 ? editedStep.sand_step / (42 * conc) : 0)
              : (editedStep.clean_step !== undefined ? editedStep.clean_step : step.clean_step);
            const updatedStep = {
              ...step,
              rate: editedStep.rate !== undefined ? editedStep.rate : step.rate,
              conc,
              clean_step: effectiveCleanStep,
              // Keep sand_step in sync — use the edited value directly in sand mode,
              // otherwise re-derive from the effective clean_step so the table always
              // shows the correct value after editedTreatmentSteps is cleared.
              sand_step: (editBySand && isProppantOrSandRamp && editedStep.sand_step !== undefined)
                ? Math.round(editedStep.sand_step)
                : Math.round(effectiveCleanStep * 42 * conc),
              type,
              description: editedStep.description !== undefined ? editedStep.description : step.description
            };
            
            // Preserve start_conc and end_conc — use explicitly edited value if present,
            // otherwise keep whatever was already on the step (already in ...step spread).
            // This ensures Sand Ramp steps never lose their original spread after save.
            if (editedStep.start_conc !== undefined) {
              (updatedStep as any).start_conc = editedStep.start_conc;
            } else if ((step as any).start_conc !== undefined) {
              (updatedStep as any).start_conc = (step as any).start_conc;
            }
            if (editedStep.end_conc !== undefined) {
              (updatedStep as any).end_conc = editedStep.end_conc;
            } else if ((step as any).end_conc !== undefined) {
              (updatedStep as any).end_conc = (step as any).end_conc;
            }
            
            return updatedStep;
          }
          return step;
        });
        
        setTreatmentSchedule(updatedSchedule);
        
        // Update the original schedule to the new successfully saved values
        setOriginalTreatmentSchedule([...updatedSchedule]);
        
        // Clear the edited steps after successful update
        setEditedTreatmentSteps({});
      } else {
        // Revert to original schedule on failure response
        console.log('Reverting to original schedule due to failure response');
        setTreatmentSchedule(originalScheduleBackup);
        setEditedTreatmentSteps({});
        
        toast.error("Treatment schedule failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error('Error updating treatment schedule:', error);
      
      // Revert to original schedule on failure
      console.log('Reverting to original schedule due to failure');
      setTreatmentSchedule(originalScheduleBackup);
      setEditedTreatmentSteps({});
      
      toast.error("Treatment schedule failed. Changes reverted.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      // Always reset loading state
      setIsSavingSchedule(false);
    }
  };

  // Handle save template
  const handleSaveTemplate = async () => {
    if (!templateName) {
      toast.warning('Template name cannot be empty.');
      return;
    }
    if (selectedChannels.length === 0) {
      toast.warning('No channels selected for the template.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication token not found.');
        return;
      }
      
      // Create template channels using the axis-first configuration
      const templateChannels = selectedChannels.map(ch => {
        // Find which axis this channel is assigned to
        let axisPosition = 'left1'; // Default
        
        // Check all axes to find where this channel is assigned
        Object.keys(axisSettings).forEach(axis => {
          if (axisSettings[axis].channels.includes(ch.value)) {
            axisPosition = axis;
          }
        });
        
        // Extract axis number from the position string (e.g., 'left1' -> 1, 'right2' -> 2)
        const axisNumber = parseInt(axisPosition.match(/\d+/)?.[0] || '1');
        
        // Get min/max values from the axis settings
        const axisMin = axisSettings[axisPosition].min;
        const axisMax = axisSettings[axisPosition].max;
        
        return {
          channel: ch.value,
          display_position: axisPosition,
          min_value: axisMin !== undefined ? axisMin : null,
          max_value: axisMax !== undefined ? axisMax : null,
          axis: axisNumber // Include the numeric axis value (1, 2, 3, or 4)
        };
      });
      
      await axios.post(
        `${backendUrl}/templates`,
        {
          template_name: templateName,
          device_type: 'TREATMENT',
          widget_type: 'CHART',
          channels: templateChannels
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Template saved successfully.');
      fetchTemplates();
      setShowManageTemplates(false);
      setTemplateName('');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template.');
    }
  };

  // Cumulative running totals for treatment schedule table
  const slurryTotals: { [stepId: number]: number } = {};
  const sandTotals: { [stepId: number]: number } = {};
  let slurryRunning = 0;
  let sandRunning = 0;
  [...treatmentSchedule].sort((a, b) => a.step - b.step).forEach(s => {
    slurryRunning += Number(editedTreatmentSteps[s.id]?.slurry_step ?? s.slurry_step ?? 0);
    sandRunning += Number(editedTreatmentSteps[s.id]?.sand_step ?? s.sand_step ?? 0);
    slurryTotals[s.id] = slurryRunning;
    sandTotals[s.id] = sandRunning;
  });

  const channelGroupOptions = useMemo(() => {
    const treatmentChannels = channels.filter(
      c => !c.chem_name && !c.momentum_well_id && !c.seismos_well_id && !c.seismos_metric && !c.scoreboard_channel
    );
    const scoreboardChannels = channels
      .filter(c => !!c.scoreboard_channel)
      .sort((a, b) => a.label.localeCompare(b.label));
    const chemChannels       = channels.filter(c => !!c.chem_name);
    const momentumChannels   = channels.filter(c => !!c.momentum_well_id);
    const seismosChannels    = channels.filter(c => !!c.seismos_well_id);
    const seismosMetricChans = channels.filter(c => !!c.seismos_metric);

    const groups: { label: string; options: ChannelOption[] }[] = [
      { label: 'Treatment', options: treatmentChannels },
    ];
    if (chemChannels.length > 0) {
      groups.push({ label: 'Chemicals', options: chemChannels });
    }
    if (momentumChannels.length > 0) {
      groups.push({ label: 'Momentum', options: momentumChannels });
    }
    // One Seismos header: per-event metrics first, offset-well pressures below.
    const allSeismos = [...seismosMetricChans, ...seismosChannels];
    if (allSeismos.length > 0) {
      groups.push({ label: 'Seismos', options: allSeismos });
    }
    if (scoreboardChannels.length > 0) {
      groups.push({ label: 'Scoreboard', options: scoreboardChannels });
    }
    return groups;
  }, [channels]);

  // ── Shared react-select styles (theme-aware) ──────────────────────────────
  const reactSelectStyles = useMemo(() => ({
    option: (provided: any, state: any) => ({
      ...provided,
      color: state.isSelected ? '#ffffff' : theme === 'dark' ? '#ffffff' : '#333333',
      backgroundColor: state.isSelected
        ? '#4361ee'
        : state.isFocused
          ? theme === 'dark' ? '#3d4144' : '#f0f0f0'
          : theme === 'dark' ? '#303335' : '#ffffff',
      fontWeight: 500,
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
      border: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`,
    }),
    menuList: (provided: any) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#3d4144' : '#e6e6e6',
      border: `1px solid ${theme === 'dark' ? '#5c646b' : '#ccc'}`,
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: theme === 'dark' ? '#ffffff' : '#333333',
      fontWeight: 500,
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: theme === 'dark' ? '#aaa' : '#666',
      ':hover': { backgroundColor: '#f72585', color: '#fff' },
    }),
    control: (provided: any) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
      borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6',
    }),
    input: (provided: any) => ({
      ...provided,
      color: theme === 'dark' ? '#ffffff' : '#333333',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: theme === 'dark' ? '#8b9cb7' : '#9ca3af',
    }),
    groupHeading: (provided: any) => ({
      ...provided,
      padding: '4px 12px 0',
      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
    }),
  }), [theme]);

  return (
    <section className={`${styles.section} ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}>
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme={theme} />
      
      {/* Full Screen Loading Overlay */}
      {isSavingSchedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)'
        }}>
          <Spinner
            animation="border"
            variant="light"
            style={{ 
              width: '4rem', 
              height: '4rem',
              marginBottom: '1rem'
            }}
          />
          <h4 style={{ 
            color: 'white',
            fontWeight: '600',
            textAlign: 'center',
            margin: 0
          }}>
            Waiting for field approval
          </h4>
          <p style={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            textAlign: 'center',
            margin: '0.5rem 0 0 0',
            fontSize: '1rem'
          }}>
            Please wait while your changes are being processed...
          </p>
        </div>
      )}
      
      <div className={styles.profile}>
        {/* Header Component */}
        <Header 
        title={
          isRealTimeStreaming && currentWell && currentStage
            ? `Treatment Data Graph - ${currentWell.well_name} - Stage ${currentStage.stage_number}`
            : padName 
              ? `Treatment Data Graph - ${padName}` 
              : "Treatment Data Graph"
        }
          setShowSidebar={setShowSidebar} 
          showSidebar={showSidebar} 
        />

        <div className={styles.container}>
          {/* Sidebar */}
          <div
            className={styles.sidebarStyle}
            style={{
              width: showSidebar ? '240px' : '0',
              transition: 'width 0.3s',
            }}
          >
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className={styles.mainContentStyle}>
            {/* Top Controls Row - Back Button, Chart Settings, View Treatment, Edit Ratios, and Date Pickers */}
            <div className="d-flex flex-wrap align-items-center mb-4 gap-3">
              {/* Back Button */}
              <Button 
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => navigate('/pad-data')}
                className="d-flex align-items-center"
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  padding: '0.5rem 1rem',
                  border: `1px solid ${theme === 'dark' ? '#5C646B' : '#e6e6e6'}`
                }}
              >
                <span style={{ marginRight: '0.5rem' }}>&larr;</span> Back to Pad Data
              </Button>
              
              {/* Chart Settings Button */}
              <Button
                variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
                onClick={() => setShowSettingsModal(true)}
                className="d-flex align-items-center"
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  padding: '0.5rem 1rem',
                  border: `1px solid ${theme === 'dark' ? '#5C646B' : '#e6e6e6'}`
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-gear me-2" viewBox="0 0 16 16">
                  <path d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492zM5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0z"/>
                  <path d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52l-.094-.319zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115l.094-.319z"/>
                </svg>
                Chart Settings
              </Button>
              
              {/* View Treatment Button */}
              <Button 
                onClick={() => setShowRulesModal(true)}
                style={{
                    backgroundColor: '#8CC540',
                    borderColor: '#8CC540'}}>
                Manage Rules
              </Button>

              {/* Reports Button */}
              <Button
                onClick={() => setShowReportsModal(true)}
                style={{
                    backgroundColor: '#8CC540',
                    borderColor: '#8CC540'}}>
                Reports
              </Button>

              {/* View Treatment Button */}
              {selectedStage && showViewTreatmentButton && (
                <Button
                  variant="primary"
                  onClick={handleViewTreatmentClick}
                  className="d-flex align-items-center"
                  style={{
                    backgroundColor: '#8CC540',
                    borderColor: '#8CC540',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    fontWeight: 'bold'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-eye me-2" viewBox="0 0 16 16">
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                  </svg>
                  View Treatment
                </Button>
              )}
              
              {/* Date Range Pickers - Smaller and Inline */}
              <div className="d-flex align-items-center gap-2 ms-auto">
                <div style={{ width: '180px' }}>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date: Date | null) => setFromDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    placeholderText="From Date"
                    {...datePickerCustomStyles}
                  />
                </div>
                <div style={{ width: '180px' }}>
                  <DatePicker
                    selected={toDate}
                    onChange={(date: Date | null) => setToDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    placeholderText="To Date"
                    {...datePickerCustomStyles}
                  />
                </div>
                <Button 
                  variant="primary"
                  onClick={handleDateRangeChange}
                  size="sm"
                  style={{
                    backgroundColor: theme === 'dark' ? '#8CC540' : '#8CC540',
                    borderColor: theme === 'dark' ? '#8CC540' : '#8CC540',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
            

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger" style={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                {error}
              </div>
            )}

            {/* Chart Container */}
            <div className="card shadow-sm" style={{ 
              backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
              borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div className="card-body p-0">
                {/* Loading State */}
                {loading ? (
                  <div className="text-center p-5" style={{ 
                    minHeight: '500px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: theme === 'dark' ? '#e5e5e5' : '#333333'
                  }}>
                    <Spinner 
                      animation="border" 
                      variant={theme === 'dark' ? 'light' : 'primary'}
                      style={{ width: '3rem', height: '3rem' }}
                    />
                    <p className="mt-4" style={{ 
                      fontSize: '1rem',
                      fontWeight: '500'
                    }}>
                      Loading treatment data...
                    </p>
                  </div>
                ) : (
                  /* Chart */
                  <div
                    onDoubleClick={handleChartDoubleClick}
                    style={{ 
                    height: '500px', 
                    width: '100%',
                    padding: '1rem',
                    position: 'relative'
                  }}>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                    ref={chartRef}
                  />
                    
                    
                    {/* Real-Time Streaming Button */}
                    <div 
                      style={{
                        position: 'absolute',
                        right: '20px',
                        top: '20px',
                        zIndex: 1000
                      }}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        {isStageView && !isRealTimeStreaming && (
                          <Button
                            size="sm"
                            onClick={handleShowFullJob}
                            style={{
                              backgroundColor: '#5C646B',
                              borderColor: '#5C646B',
                              fontSize: '0.75rem',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            ← Full Job
                          </Button>
                        )}
                        {isRealTimeStreaming && currentWell && currentStage && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: stageState === "executing" ? "#8CC540" : "#F0A73E",
                              color: "#fff",
                              borderRadius: "8px",
                              padding: "5px 10px",
                              fontSize: "0.8rem",
                              fontWeight: "bold",
                              whiteSpace: "nowrap",
                              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                                opacity: stageState === "executing" ? 1 : 0.6,
                              }}
                            />
                            {`${currentWell.well_name} \u00B7 Stage ${currentStage.stage_number} \u2014 ${
                              stageState === "executing" ? "In Progress" : stageState === "complete" ? "Complete" : stageState === "configured" ? "Configured" : "Standby"
                            }`}
                          </span>
                        )}
                        <Button
                          variant={isRealTimeStreaming ? "danger" : "primary"}
                          style={{
                            backgroundColor: isRealTimeStreaming ? "#FF5F4D" : "#8CC540",
                            borderColor: isRealTimeStreaming ? "#FF5F4D" : "#8CC540",
                            borderRadius: "8px",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                            fontWeight: "bold",
                          }}
                          size="sm"
                          onClick={isRealTimeStreaming ? () => stopStreaming() : handleStartStreaming}
                        >
                          {isRealTimeStreaming ? "Stop Streaming" : "Real-Time Streaming"}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Streaming Error Message */}
                    {streamingError && (
                      <Alert variant="danger" className="mt-3">
                        {streamingError}
                      </Alert>
                    )}
                    
                    {/* Real-time Data Display - Positioned above the chart */}
                    {isRealTimeStreaming && realTimeData && (
                      <div style={{ 
                        marginBottom: '15px',
                        zIndex: 1000,
                        backgroundColor: theme === 'dark' ? '#303335' : 'rgba(255, 255, 255, 0.9)',
                        padding: '10px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }}>
                        <Card 
                          className="mb-0" 
                          bg={theme === 'dark' ? 'dark' : 'light'}
                          text={theme === 'dark' ? 'white' : 'dark'}
                          style={{ 
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <Card.Header as="h5" className="d-flex justify-content-between align-items-center py-2">
                            <span>Real-Time Treatment Data</span>
                            <span>
                              {currentWell && currentStage && (
                                `Well: ${currentWell.well_name} - Stage: ${currentStage.stage_number}`
                              )}
                            </span>
                          </Card.Header>
                          <Card.Body className="py-2">
                            <div className="d-flex flex-wrap justify-content-between">
                              <div className="mb-1 me-3">
                                <strong>Step:</strong> {realTimeData.step || 'N/A'}
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Step:</strong> {realTimeData?.step || 'N/A'}
                              </div>
                              <div className="mb-1 me-3">
                                <strong>STP:</strong> {realTimeData?.stp?.toFixed(2) || 'N/A'} psi
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Clean Rate:</strong> {realTimeData?.cleanRate?.toFixed(2) || 'N/A'} bpm
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Slurry Rate:</strong> {realTimeData?.slurryRate?.toFixed(2) || 'N/A'} bpm
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Stage Density:</strong> {realTimeData?.stageDensity?.toFixed(2) || 'N/A'} ppg
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Elapsed Time:</strong> {realTimeData?.elapsedTime?.toFixed(2)/1000 || 'N/A'} min
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Step Clean:</strong> {realTimeData?.stepClean?.toFixed(2) || 'N/A'} bbl
                              </div>
                              <div className="mb-1 me-3">
                                <strong>Step Slurry:</strong> {realTimeData?.stepSlurry?.toFixed(2) || 'N/A'} bbl
                              </div>
                              <div className="mb-1">
                                <strong>Step Sand:</strong> {realTimeData?.stepSand?.toFixed(2) || 'N/A'} lbs
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                    
                  </div>
                )}
              </div>
            </div>
            
            {/* Real-Time Treatment/Chem Values - Only shown when Real-Time Streaming is active */}
            {isRealTimeStreaming && realTimeData && (
              <div className="card shadow-sm mt-2" style={{ 
                backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
                borderRadius: '8px'
                /* overflow:hidden removed — it clips the horizontal scrollbar on the chem table */
              }}>
                <div className="card-body py-2 px-3">
                  <h5 className="card-title mb-3" style={{ 
                    color: theme === 'dark' ? '#e5e5e5' : '#333333',
                    textAlign: 'center',
                    width: '100%'
                  }}>
                    Real-Time Treatment/Chem Values
                  </h5>
                  
                  {/* Treatment Data Cards */}
                  <div className="d-flex flex-wrap justify-content-between p-2 mb-3">
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Treating Pressure (STP):</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#cf2727ff' : '#cf2727ff' }}>{realTimeData?.stp?.toFixed(0) || 'N/A'} psi</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Clean Rate:</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#3E76FE' : '#3E76FE' }}>{realTimeData?.cleanRate?.toFixed(1) || 'N/A'} bpm</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Slurry Rate:</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#8CC540' : '#8CC540' }}>{realTimeData?.slurryRate?.toFixed(1) || 'N/A'} bpm</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '200px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Stage Density:</strong> <span className="float-end" style={{ color: theme === 'dark' ? 'brown' : 'brown' }}>{realTimeData?.stageDensity?.toFixed(2) || 'N/A'} ppg</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Step Clean:</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#3e31ecff' : '#3e31ecff' }}>{realTimeData?.stepClean?.toFixed(0) || 'N/A'} bbl</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Step Slurry:</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#5a960cff' : '#5a960cff' }}>{realTimeData?.stepSlurry?.toFixed(0) || 'N/A'} bbl</span>
                    </div>
                    <div className="mb-2 me-3 p-2" style={{
                      backgroundColor: theme === 'dark' ? '#303335' : '#f8f9fa',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333',
                      border: '1px solid #5C646B',
                      borderRadius: '6px',
                      minWidth: '180px',
                      boxShadow: theme === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <strong>Step Sand:</strong> <span className="float-end" style={{ color: theme === 'dark' ? '#f9c844' : '#f9c844' }}>{realTimeData?.stepSand?.toFixed(0) || 'N/A'} lbs</span>
                    </div>
                  </div>
                  

                  {/* Chemical Schedule - Next Steps: shown only while the stage is actively executing */}
                  {canEditStage && nextChemSchedule && realTimeChemData.length > 0 && (() => {
                    const cs = nextChemSchedule;
                    if (nextChemLoading) return (
                      <div className="text-center p-3">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2" style={{ color: theme === 'dark' ? '#e5e5e5' : '#333' }}>Loading chemicals...</span>
                      </div>
                    );

                                    const isDark      = theme === 'dark';
                    const nextSteps3  = getNextSteps(); // current + next 2

                    // liveChemMap: keyed by plain name from the /chem topic
                    const liveChemMap: Record<string, { ratio: number | null; total: number | null }> = {};
                    realTimeChemData.forEach((item: any) => {
                      if (item.name) {
                        liveChemMap[item.name] = {
                          ratio: typeof item.ratio === 'number' ? item.ratio : null,
                          total: typeof item.total === 'number' ? item.total : null,
                        };
                      }
                    });

                    // Column headers = union of live /chem topic AND the schedule's
                    // chemicals list. Petrix's /chem topic only includes chemicals
                    // that are actively pumping (or have streamed at least once),
                    // so a chemical in the design schedule whose pump hasn't started
                    // yet (zero rate, early in the stage) would otherwise be hidden.
                    // The schedule topic carries the authoritative list of chemicals
                    // for the stage, so we anchor on /chem order (preserves
                    // current display ordering for users) and append any
                    // schedule-only names at the end.
                    const liveChemNames: string[] = realTimeChemData
                      .filter((item: any) => item.name)
                      .map((item: any) => item.name as string);
                    const seenChemNames = new Set<string>(liveChemNames);
                    const effectiveChemNames: string[] = [...liveChemNames];
                    Object.values(rawScheduleChemicals).forEach(chems => {
                      chems.forEach(c => {
                        if (c.name && !seenChemNames.has(c.name)) {
                          seenChemNames.add(c.name);
                          effectiveChemNames.push(c.name);
                        }
                      });
                    });

                    const getLiveEntry = (chemName: string) => liveChemMap[chemName] ?? null;

                    // Since the schedule topic now includes all chemicals + groups on every step,
                    // multi-group detection is simply: does rawScheduleChemicals for ANY step have
                    // more than one entry with the same name?
                    const multiGroupChems = new Set<string>();
                    Object.values(rawScheduleChemicals).forEach(chems => {
                      const countByName: Record<string, number> = {};
                      chems.forEach(c => { countByName[c.name] = (countByName[c.name] || 0) + 1; });
                      Object.entries(countByName).forEach(([name, count]) => {
                        if (count > 1) multiGroupChems.add(name);
                      });
                    });
                    // No master-group seeding needed — every step already has all groups.

                    const totalGals = (loading: number | null, cleanBbls: number) =>
                      loading == null ? null : (loading * cleanBbls * 42) / 1000;
                    const fmt = (v: number | null) => v == null ? '—' : v.toFixed(3);

                    const colsPerChem = 5;
                    // Fixed pixel widths — table grows beyond container when there are many chemicals,
                    // and the table-responsive wrapper handles the horizontal scrollbar.
                    const fixedColWidths = { stepNum: 48, type: 90, cleanVol: 88 };
                    const chemColWidths  = { designRatio: 78, actualRatio: 78, totalDesign: 82, totalActual: 82, variance: 72 };

                    const thS: React.CSSProperties = {
                      padding: '5px 6px', fontWeight: 600, whiteSpace: 'nowrap',
                      backgroundColor: isDark ? '#3d4144' : '#f3f4f6',
                      color: isDark ? '#e5e5e5' : '#303335',
                      border: `1px solid ${isDark ? '#5C646B' : '#dee2e6'}`,
                    };
                    const nameThStyle: React.CSSProperties = {
                      ...thS, textAlign: 'center',
                      backgroundColor: isDark ? '#5a5a00' : '#fef9c3',
                      color: isDark ? '#ffe066' : '#5a4000', fontWeight: 700,
                    };

                    return (
                      <>
                        {/* Title bar: Chemical Schedule - Next Steps | Chemical Changes */}
                        <div className="d-flex justify-content-center align-items-center mt-3 mb-2">
                          <h5 className="mb-0" style={{ color: isDark ? '#e5e5e5' : '#333333' }}>
                            Chemical Schedule - Next Steps
                          </h5>
                          <span className="mx-3" style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant={isDark ? 'outline-light' : 'outline-secondary'}
                              id="dropdown-rt-chem-changes"
                              size="sm"
                            >
                              Chemical Changes ({chemicalChanges.length})
                            </Dropdown.Toggle>
                            <Dropdown.Menu
                              className={isDark ? 'dropdown-menu-dark' : ''}
                              style={{ minWidth: '600px', maxWidth: '800px', maxHeight: '400px', overflowY: 'auto', fontSize: '0.9em', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                            >
                              {chemicalChanges.length > 0 ? (
                                [...chemicalChanges]
                                  .sort((a, b) => {
                                    const ta = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : a.timestamp as unknown as number;
                                    const tb = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : b.timestamp as unknown as number;
                                    return tb - ta;
                                  })
                                  .map((change, index) => (
                                    <div key={index} className="px-4 py-3" style={{ borderBottom: index < chemicalChanges.length - 1 ? `1px solid ${isDark ? '#3a4564' : '#e6e6e6'}` : 'none', backgroundColor: isDark ? '#303335' : '#ffffff' }}>
                                      <div className="d-flex justify-content-between align-items-start mb-2">
                                        <span style={{ fontWeight: 'bold', fontSize: '1em', color: isDark ? '#4361ee' : '#3a0ca3' }}>{change.creator}</span>
                                        <span style={{ fontSize: '0.85em', color: isDark ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                          {(() => { const ts = typeof change.timestamp === 'string' ? parseInt(change.timestamp) : change.timestamp as unknown as number; return new Date(ensureMs(ts)).toLocaleString(); })()}
                                        </span>
                                      </div>
                                      <p style={{ margin: 0, fontSize: '0.9em', color: isDark ? '#e5e5e5' : '#333333', lineHeight: '1.4', wordWrap: 'break-word' }}>{change.comment}</p>
                                    </div>
                                  ))
                              ) : (
                                <div className="px-4 py-3 text-center" style={{ color: isDark ? '#9ca3af' : '#6b7280', fontStyle: 'italic' }}>No chemical changes recorded for this stage</div>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>

                        {/* Table: current + next 2 steps only */}
                        <div className="table-responsive" style={{ overflowX: 'auto' }}>
                          <table
                            className="table"
                            style={{ width: 'max-content', minWidth: '100%', tableLayout: 'auto', borderCollapse: 'collapse', fontSize: '0.8rem', color: isDark ? '#e5e5e5' : '#303335', backgroundColor: isDark ? '#303335' : '#fff' }}
                          >
                            <colgroup>
                              <col style={{ minWidth: `${fixedColWidths.stepNum}px` }} />
                              <col style={{ minWidth: `${fixedColWidths.type}px` }} />
                              <col style={{ minWidth: `${fixedColWidths.cleanVol}px` }} />
                              {effectiveChemNames.map((name: string) => (
                                <React.Fragment key={name}>
                                  <col style={{ minWidth: `${chemColWidths.designRatio}px` }} />
                                  <col style={{ minWidth: `${chemColWidths.actualRatio}px` }} />
                                  <col style={{ minWidth: `${chemColWidths.totalDesign}px` }} />
                                  <col style={{ minWidth: `${chemColWidths.totalActual}px` }} />
                                  <col style={{ minWidth: `${chemColWidths.variance}px` }} />
                                </React.Fragment>
                              ))}
                            </colgroup>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                              <tr>
                                <th colSpan={3} style={thS}>Step</th>
                                {effectiveChemNames.map((name: string) => (
                                  <th key={name} colSpan={5} style={nameThStyle}>{name}</th>
                                ))}
                              </tr>
                              <tr>
                                <th style={thS}>Step #</th>
                                <th style={thS}>Type</th>
                                <th style={{ ...thS, textAlign: 'right' }}>Clean Vol<br/>(gals)</th>
                                {effectiveChemNames.map((name: string) => (
                                  <React.Fragment key={name}>
                                    <th style={{ ...thS, textAlign: 'center' }}>Design Ratio<br/>(gpt)</th>
                                    <th style={{ ...thS, textAlign: 'center' }}>Actual Ratio<br/>(gpt)</th>
                                    <th style={{ ...thS, textAlign: 'right'  }}>Total Design<br/>(gal)</th>
                                    <th style={{ ...thS, textAlign: 'right'  }}>Total Actual<br/>(gal)</th>
                                    <th style={{ ...thS, textAlign: 'center' }}>Variance<br/>(%)</th>
                                  </React.Fragment>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {/* Always render from treatmentSchedule (current + next 2 steps).
                                  cs.steps is used only as a design-value lookup; it may be empty
                                  for Petrix 7.0.1 where no chemical schedule is posted. */}
                              {getNextSteps().map((step, rowIdx) => {
                                  const row       = cs.steps.find((s: any) => s.step === step.step);
                                  const isActive  = currentStep === step.step;
                                  const isFutureOrCurrent = currentStep === null || step.step >= currentStep;
                                  const canEdit   = hasChemEditLock && isFutureOrCurrent;
                                  const rowBg  = rowIdx % 2 === 0 ? (isDark ? '#303335' : '#ffffff') : (isDark ? '#383b3e' : '#f9fafb');
                                  const cellBg = isActive ? (isDark ? '#f9c844' : '#fef08a') : rowBg;
                                  const cellBase: React.CSSProperties = {
                                    padding: '4px 6px', whiteSpace: 'nowrap',
                                    border: `1px solid ${isDark ? '#5C646B' : '#dee2e6'}`,
                                    backgroundColor: cellBg, color: isDark ? '#e5e5e5' : '#303335',
                                  };
                                  return (
                                    <tr key={step.step} style={{ backgroundColor: cellBg }}>
                                      <td style={{ ...cellBase, textAlign: 'center' }}>{step.step}</td>
                                      <td style={cellBase}>{step.type}</td>
                                      <td style={{ ...cellBase, textAlign: 'right' }}>{Math.round((step.clean_step || 0) * 42).toLocaleString()}</td>
                                      {effectiveChemNames.map((name: string) => {
                                        const entry     = row?.chems?.[name];
                                        const cellKey   = `${row?.step ?? step.step}::${name}`;
                                        const editedVal = editedChemLoadings[cellKey];
                                        const editedGroups = editedChemGroupRatios[cellKey];

                                        // Column-level: use modal for all rows if ANY step has >1 group
                                        const isMultiGroup = multiGroupChems.has(name);
                                        // Groups for this specific row's step
                                        const stepGroups = (rawScheduleChemicals[row?.step ?? step.step] || []).filter(c => c.name === name);

                                        // Design value priority:
                                        //   1. Edited group ratios sum (modal edit in progress)
                                        //   2. Inline edited value (edit in progress)
                                        //   3. rawScheduleChemicals (MQTT schedule echo — always current,
                                        //      updated immediately when Petrix accepts any change including
                                        //      unsupervised rule fires)
                                        //   4. DB design value (fallback when no schedule topic received yet)
                                        const rawRatio = stepGroups.length > 1
                                          ? stepGroups.reduce((s, g) => s + g.ratio, 0)
                                          : stepGroups[0]?.ratio ?? null;
                                        const designVal = editedGroups
                                          ? editedGroups.reduce((s, g) => s + g.ratio, 0)
                                          : editedVal !== undefined
                                            ? editedVal
                                            : rawRatio !== null
                                              ? rawRatio
                                              : (entry?.design ?? null);
                                        const liveEntry = row?.step === currentStep ? getLiveEntry(name) : null;
                                        const liveRatio = liveEntry?.ratio ?? null;
                                        const liveTotal = liveEntry?.total ?? null;
                                        const actualVal = liveRatio !== null ? liveRatio : (entry?.actual ?? null);
                                        const liveStepClean = (row?.step === currentStep && realTimeData?.stepClean != null)
                                          ? realTimeData.stepClean : null;
                                        const td = liveStepClean != null && designVal != null
                                          ? (designVal * liveStepClean * 42) / 1000
                                          : totalGals(designVal, row?.clean_step);
                                        const ta = (row?.step === currentStep && liveTotal !== null)
                                          ? liveTotal
                                          : totalGals(actualVal, row?.clean_step);
                                        const variance = (designVal != null && designVal !== 0 && actualVal != null)
                                          ? Math.abs((designVal - actualVal) / designVal) * 100
                                          : null;
                                        const varColor  = variance == null ? (isDark ? '#e5e5e5' : '#303335')
                                          : variance <= 5 ? '#16a34a' : variance <= 15 ? '#d97706' : '#dc2626';
                                        const fromStepNum = row?.step ?? step.step;
                                        const hasLaterSteps = treatmentSchedule.some(s => s.step > fromStepNum);
                                        const groupsChanged = editedGroups !== undefined &&
                                          JSON.stringify(editedGroups) !== JSON.stringify(stepGroups.map(g => ({ group: g.group, ratio: g.ratio })));
                                        const singleChanged = !isMultiGroup &&
                                          editedVal !== undefined &&
                                          editedVal !== (rawRatio !== null ? rawRatio : (entry?.design ?? null));
                                        const showChemCascade = canEdit && hasLaterSteps && (groupsChanged || singleChanged);
                                        return (
                                          <React.Fragment key={name}>
                                            <td style={{ ...cellBase, textAlign: 'center' }}>
                                              {canEdit ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                  {isMultiGroup ? (
                                                    // Multi-group: clickable cell — opens group modal
                                                    <span
                                                      onClick={() => {
                                                        // Every step now has all groups in rawScheduleChemicals,
                                                        // so just use stepGroups directly. Overlay any prior edits.
                                                        const groups = editedGroups
                                                          ?? stepGroups.map(g => ({ group: g.group, ratio: g.ratio }));
                                                        setChemGroupModal({ step: row?.step ?? step.step, chemName: name, groups });
                                                      }}
                                                      style={{ cursor: 'pointer' }}
                                                      title="Click to edit group ratios"
                                                    >
                                                      {fmt(designVal)}
                                                    </span>
                                                  ) : (
                                                    <input type="number" step="any"
                                                      value={designVal ?? ''}
                                                      onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                                      onChange={e => { const v = parseFloat(e.target.value); setEditedChemLoadings(prev => ({ ...prev, [cellKey]: isNaN(v) ? 0 : v })); }}
                                                      style={{ width: '68px', textAlign: 'center', border: `1px solid ${isDark ? '#a16207' : '#ca8a04'}`, borderRadius: '3px', padding: '1px 3px', fontSize: '0.78rem', backgroundColor: isDark ? '#1a1a00' : '#fffbeb', color: isDark ? '#ffe066' : '#5a4000' }}
                                                    />
                                                  )}
                                                  {showChemCascade && renderCascadeBtn(
                                                    () => cascadeChemRatio(fromStepNum, name),
                                                    `Apply this ${name} ratio to step ${fromStepNum} and all later steps`
                                                  )}
                                                </span>
                                              ) : fmt(designVal)}
                                            </td>
                                            <td style={{ ...cellBase, textAlign: 'center', color: varColor }}>{fmt(actualVal)}</td>
                                            <td style={{ ...cellBase, textAlign: 'right' }}>{fmt(td)}</td>
                                            <td style={{ ...cellBase, textAlign: 'right', color: varColor }}>{fmt(ta)}</td>
                                            <td style={{ ...cellBase, textAlign: 'center', color: varColor, fontWeight: variance != null ? 600 : undefined }}>
                                              {variance != null ? `${variance.toFixed(1)}%` : '—'}
                                            </td>
                                          </React.Fragment>
                                        );
                                      })}
                                    </tr>
                                  );
                                })}
                            </tbody>
                          </table>
                        </div>

                        {/* Edit / Save / Cancel — only for viewers with edit rights */}
                        {!isViewer && (
                          <div className="d-flex justify-content-end mt-3">
                            {!hasChemEditLock ? (
                              <Button
                                variant="primary"
                                onClick={requestChemEditLock}
                                style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s ease-in-out' }}
                                disabled={!canEditStage || !isCurrentStepVisible()}
                              >
                                Edit
                              </Button>
                            ) : (
                              <div className="d-flex gap-2">
                                <Button
                                  variant="success"
                                  onClick={handleSaveChemSchedule}
                                  style={{ backgroundColor: '#8CC540', borderColor: '#8CC540', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s ease-in-out' }}
                                  disabled={Object.keys(editedChemLoadings).length === 0 && Object.keys(editedChemGroupRatios).length === 0 || isSavingChem || !canEditStage}
                                >
                                  {isSavingChem ? (
                                    <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />Waiting for field approval</>
                                  ) : 'Save'}
                                </Button>
                                <Button
                                  variant="secondary"
                                  onClick={releaseChemEditLock}
                                  style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s ease-in-out' }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
            
            {/* Treatment Schedule Steps Component - shown only while the stage is actively executing */}
            {canEditStage && (
              <>
              {/* Full-screen overlay for chem save — same as treatment save */}
              {isSavingChem && (
                <div style={{
                  position: 'fixed',
                  top: 0, left: 0,
                  width: '100vw', height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex', flexDirection: 'column',
                  justifyContent: 'center', alignItems: 'center',
                  zIndex: 9999,
                  backdropFilter: 'blur(4px)',
                }}>
                  <Spinner
                    animation="border"
                    variant="light"
                    style={{ width: '4rem', height: '4rem', marginBottom: '1rem' }}
                  />
                  <h4 style={{ color: 'white', fontWeight: '600', textAlign: 'center', margin: 0 }}>
                    Waiting for field approval
                  </h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center', margin: '0.5rem 0 0 0', fontSize: '1rem' }}>
                    Please wait while your changes are being processed...
                  </p>
                </div>
              )}
              <div className="card shadow-sm mt-2" style={{ 
                backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                borderColor: theme === 'dark' ? '#5C646B' : '#e6e6e6',
                borderRadius: '8px',
              }}>
                <div className="card-body py-2 px-3">
                  <div className="d-flex justify-content-center align-items-center mb-3">
                    <h5 className="mb-0" style={{ 
                      color: theme === 'dark' ? '#e5e5e5' : '#333333'
                    }}>
                      Treatment Schedule - Next Steps
                    </h5>
                    {isRealTimeStreaming && (
                      <>
                        <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                        <Dropdown>
                          <Dropdown.Toggle 
                            variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'} 
                            id="dropdown-changes"
                            size="sm"
                          >
                            Treatment Changes ({treatmentChanges.length})
                          </Dropdown.Toggle>
                          <Dropdown.Menu 
                            className={theme === 'dark' ? 'dropdown-menu-dark' : ''} 
                            style={{ 
                              minWidth: '600px', 
                              maxWidth: '800px', 
                              maxHeight: '400px', 
                              overflowY: 'auto',
                              fontSize: '0.9em',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {(() => {
                              const changesList = treatmentChanges;
                              return changesList.length > 0 ? (
                                [...changesList]
                                  .sort((a, b) => {
                                    const ta = typeof a.timestamp === 'string' ? parseInt(a.timestamp) : a.timestamp as unknown as number;
                                    const tb = typeof b.timestamp === 'string' ? parseInt(b.timestamp) : b.timestamp as unknown as number;
                                    return tb - ta;
                                  })
                                  .map((change, index) => (
                                  <div
                                    key={index}
                                    className="px-4 py-3"
                                    style={{
                                      borderBottom: index < changesList.length - 1 ? `1px solid ${theme === 'dark' ? '#3a4564' : '#e6e6e6'}` : 'none',
                                      backgroundColor: theme === 'dark' ? '#303335' : '#ffffff'
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <span style={{ fontWeight: 'bold', fontSize: '1em', color: theme === 'dark' ? '#4361ee' : '#3a0ca3' }}>
                                        {change.creator}
                                      </span>
                                      <span style={{ fontSize: '0.85em', color: theme === 'dark' ? '#9ca3af' : '#6b7280', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                        {(() => {
                                          const ts = typeof change.timestamp === 'string' ? parseInt(change.timestamp) : change.timestamp as unknown as number;
                                          const tsMs = ensureMs(ts);
                                          return new Date(tsMs).toLocaleString();
                                        })()}
                                      </span>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.9em', color: theme === 'dark' ? '#e5e5e5' : '#333333', lineHeight: '1.4', wordWrap: 'break-word' }}>
                                      {change.comment}
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-center" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontStyle: 'italic' }}>
                                  No recent changes for this stage
                                </div>
                              );
                            })()}
                          </Dropdown.Menu>
                        </Dropdown>
                        {hasEditLock && (
                          <>
                            <span className="mx-3" style={{ color: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: '1.2em' }}>|</span>
                            <button
                              className={`btn btn-sm ${editBySand ? 'btn-warning' : 'btn-outline-secondary'}`}
                              onClick={() => {
                                if (!editBySand) {
                                  // Switching TO sand edit mode — pre-seed sand_step for any
                                  // proppant/sand ramp step that hasn't been explicitly edited yet.
                                  // This ensures the input shows the correct derived value
                                  // (clean_step * 42 * conc) rather than whatever is stored in
                                  // step.sand_step (which may be 0 when populated from MQTT).
                                  setEditedTreatmentSteps(prev => {
                                    const next = { ...prev };
                                    treatmentSchedule.forEach(s => {
                                      const currentType = prev[s.id]?.type ?? s.type;
                                      const isProppantOrSandRamp =
                                        currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                      if (isProppantOrSandRamp && prev[s.id]?.sand_step === undefined) {
                                        const conc = (prev[s.id]?.conc ?? s.conc) || 0;
                                        const cleanStep = (prev[s.id]?.clean_step ?? s.clean_step) || 0;
                                        next[s.id] = {
                                          ...(prev[s.id] || {}),
                                          sand_step: Math.round(cleanStep * 42 * conc)
                                        };
                                      }
                                    });
                                    return next;
                                  });
                                }
                                setEditBySand(prev => !prev);
                              }}
                            >
                              {editBySand ? 'Editing by Sand Step' : 'Editing by Clean Step'}
                            </button>
                          </>
                        )}
                      </>
                    )}


                  </div>
                  <div className="table-responsive">
                    <Table 
                      bordered
                      size="sm"
                      variant={theme === 'dark' ? 'dark' : 'light'}
                    >
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Progress</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Rate</th>
                          <th>Conc - (Start/End)</th>
                          <th>Clean Step</th>
                          <th>Slurry Step</th>
                          <th>Slurry Total</th>
                          <th>Sand Step</th>
                          <th>Sand Total</th>
                          <th>Time Step</th>
                        </tr>
                      </thead>
                      <tbody>
                        {treatmentSchedule.length > 0 ? (
                          (() => {
                            // Build running totals over ALL steps so numbers are correct even for mid-stage views
                            const totals: { [stepId: number]: { slurry: number; sand: number } } = {};
                            let slurryRunning = 0;
                            let sandRunning = 0;
                            [...treatmentSchedule].sort((a, b) => a.step - b.step).forEach(s => {
                              const currentType = editedTreatmentSteps[s.id]?.type ?? s.type;
                              const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                              const conc = (editedTreatmentSteps[s.id]?.conc ?? s.conc) || 0;
                              const description = (editedTreatmentSteps[s.id]?.description ?? s.description) || '';
                              const descriptionValue = getDescriptionValue(description);

                              let cleanStep: number;
                              if (editBySand && isProppantOrSandRamp && editedTreatmentSteps[s.id]?.sand_step !== undefined) {
                                const sandStep = editedTreatmentSteps[s.id].sand_step as number;
                                cleanStep = conc > 0 ? sandStep / (42 * conc) : 0;
                              } else {
                                cleanStep = (editedTreatmentSteps[s.id]?.clean_step ?? s.clean_step) || 0;
                              }

                              slurryRunning += cleanStep + (cleanStep * conc / descriptionValue);
                              sandRunning += cleanStep * 42 * conc;
                              totals[s.id] = { slurry: slurryRunning, sand: sandRunning };
                            });

                            return getNextSteps().map((step) => (
                              <tr 
                                key={step.step} 
                                className={currentStep === step.step ? 'table-active' : ''}
                              >
                                <td>{step.step}</td>
                                <td>
                                  {step.step === currentStep && realTimeData?.stepClean !== undefined && realTimeData.stepClean !== null && step.clean_step !== undefined && step.clean_step !== 0
                                    ? `${((realTimeData.stepClean / step.clean_step) * 100).toFixed(2)}%`
                                    : '0.00%'}
                                </td>
                                <td>
                                  {(() => {
                                    const currentType = editedTreatmentSteps[step.id]?.type !== undefined ? editedTreatmentSteps[step.id].type : step.type;
                                    const isValidOption = TREATMENT_TYPE_OPTIONS.includes(currentType);
                                    
                                    const dropdownOptions = isValidOption 
                                      ? TREATMENT_TYPE_OPTIONS 
                                      : [currentType, ...TREATMENT_TYPE_OPTIONS];
                                    
                                    return (
                                      <select
                                        className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                        value={currentType}
                                        onChange={(e) => {
                                          if (hasEditLock) {
                                            const newType = e.target.value;
                                            const previousType = editedTreatmentSteps[step.id]?.type !== undefined 
                                              ? editedTreatmentSteps[step.id].type 
                                              : step.type;
                                            
                                            const wasProppantOrSandRamp = previousType === 'Proppant' || previousType === 'Rate Drop-Proppant' || previousType === 'Sand Ramp' || previousType === 'Rate Drop-Sand Ramp';
                                            const isProppantOrSandRamp = newType === 'Proppant' || newType === 'Rate Drop-Proppant' || newType === 'Sand Ramp' || newType === 'Rate Drop-Sand Ramp';
                                            
                                            setEditedTreatmentSteps(prev => ({
                                              ...prev,
                                              [step.id]: {
                                                ...prev[step.id],
                                                type: newType,
                                                description: (wasProppantOrSandRamp && !isProppantOrSandRamp) 
                                                  ? '' 
                                                  : prev[step.id]?.description
                                              }
                                            }));
                                          }
                                        }}
                                        disabled={!isRealTimeStreaming || !hasEditLock}
                                        style={{
                                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                                          color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                          borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6'
                                        }}
                                      >
                                        {dropdownOptions.map((option) => (
                                          <option key={option} value={option}>
                                            {option}
                                          </option>
                                        ))}
                                      </select>
                                    );
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const currentType =
                                      editedTreatmentSteps[step.id]?.type ?? step.type;
                                    const currentDescription =
                                      editedTreatmentSteps[step.id]?.description ??
                                      step.description;
                                    const safeCurrentDescription = currentDescription || '';

                                    const showDescriptionDropdown =
                                      currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';

                                    if (showDescriptionDropdown) {
                                      const isValidOption = getDescriptionOptions().includes(
                                        safeCurrentDescription
                                      );

                                      const dropdownOptions = isValidOption
                                        ? ['', ...getDescriptionOptions()]
                                        : ['', safeCurrentDescription, ...getDescriptionOptions()];

                                      return (
                                        <select
                                          className={`form-control form-control-sm ${
                                            theme === 'dark' ? 'bg-dark text-light' : ''
                                          }`}
                                          value={safeCurrentDescription}
                                          onChange={(e) => {
                                            if (hasEditLock) {
                                              setEditedTreatmentSteps((prev) => ({
                                                ...prev,
                                                [step.id]: {
                                                  ...prev[step.id],
                                                  description: e.target.value,
                                                },
                                              }));
                                            }
                                          }}
                                          disabled={!isRealTimeStreaming || !hasEditLock}
                                          style={{
                                            backgroundColor:
                                              theme === 'dark' ? '#303335 !important' : '#ffffff',
                                            color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                            borderColor:
                                              theme === 'dark' ? '#5c646b' : '#e6e6e6',
                                            width: '160px',
                                          }}
                                        >
                                          {dropdownOptions.map((option) => (
                                            <option key={option} value={option}>
                                              {option}
                                            </option>
                                          ))}
                                        </select>
                                      );
                                    } else {
                                      return (
                                        <input
                                          type="text"
                                          className={`form-control form-control-sm ${
                                            theme === 'dark' ? 'bg-dark text-light' : ''
                                          }`}
                                          value={safeCurrentDescription}
                                          readOnly
                                          disabled={!isRealTimeStreaming || !hasEditLock}
                                          style={{
                                            backgroundColor:
                                              theme === 'dark' ? '#303335' : '#ffffff',
                                            color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                            borderColor:
                                              theme === 'dark' ? '#5c646b' : '#e6e6e6',
                                            width: '120px',
                                          }}
                                        />
                                      );
                                    }
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const c = rateDropConstraints.map[step.id];
                                    return (
                                      <div className="d-flex align-items-center" style={{ flexDirection: 'column' }}>
                                        <div className="d-flex align-items-center">
                                          <Form.Control
                                            type="number"
                                            step="any"
                                            size="sm"
                                            value={editedTreatmentSteps[step.id]?.rate !== undefined ? editedTreatmentSteps[step.id].rate : step.rate}
                                            onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                            onChange={(e) => {
                                              if (hasEditLock) {
                                                const value = parseFloat(e.target.value);
                                                setEditedTreatmentSteps(prev => ({
                                                  ...prev,
                                                  [step.id]: { ...prev[step.id], rate: isNaN(value) ? 0 : value }
                                                }));
                                              }
                                            }}
                                            style={{
                                              backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                                              color: c?.rateOutOfRange ? '#ef4444' : (theme === 'dark' ? '#e5e5e5' : '#333333'),
                                              borderColor: c?.rateOutOfRange ? '#ef4444' : (theme === 'dark' ? '#5c646b' : '#e6e6e6'),
                                              width: '80px'
                                            }}
                                            disabled={!isRealTimeStreaming || !hasEditLock}
                                          />
                                          {hasEditLock &&
                                            editedTreatmentSteps[step.id]?.rate !== undefined &&
                                            editedTreatmentSteps[step.id].rate !== step.rate &&
                                            treatmentSchedule.some(s => s.step > step.step) &&
                                            renderCascadeBtn(
                                              () => cascadeTreatmentField(step.step, 'rate', editedTreatmentSteps[step.id].rate as number),
                                              `Apply this rate to step ${step.step} and all later steps`
                                            )}
                                        </div>
                                        {c && <div style={{ fontSize: '10px', color: c.rateOutOfRange || c.cleanOutOfRange || c.consecutiveRateDrop ? '#ef4444' : (theme === 'dark' ? '#9ca3af' : '#6b7280'), marginTop: '2px' }}>
                                          {c.consecutiveRateDrop ? 'Cannot follow another Rate Drop' : `${c.rateMax}–${c.rateMin} bpm · ${c.cleanSeconds.toFixed(1)}s`}
                                        </div>}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {(() => {
                                      const currentType =
                                        editedTreatmentSteps[step.id]?.type ?? step.type;
                                      const value =
                                        editedTreatmentSteps[step.id]?.conc ?? step.conc;

                                      const inputStyle = {
                                        backgroundColor: theme === "dark" ? "#303335" : "#ffffff",
                                        color: theme === "dark" ? "#e5e5e5" : "#333333",
                                        borderColor: theme === "dark" ? "#5c646b" : "#e6e6e6",
                                        width: "60px",
                                        cursor: (isRealTimeStreaming && hasEditLock) ? "pointer" : "not-allowed",
                                        textAlign: "center" as const,
                                      };

                                      if (currentType === "Sand Ramp" || currentType === "Rate Drop-Sand Ramp") {
                                        return (
                                          <Fragment>
                                            <Form.Control
                                              type="number"
                                              size="sm"
                                              readOnly
                                              value={typeof value === 'number' ? value.toFixed(2) : value}
                                              onClick={() => hasEditLock && handleConcentrationClick(step)}
                                              style={inputStyle}
                                              disabled={!isRealTimeStreaming || !hasEditLock}
                                            />
                                          </Fragment>
                                        );
                                      } else if (currentType === "Proppant" || currentType === "Rate Drop-Proppant") {
                                        return (
                                          <Form.Control
                                            type="number"
                                            step="any"
                                            size="sm"
                                            value={typeof value === 'number' ? value.toFixed(2) : value}
                                            onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                            onChange={(e) => {
                                              if (hasEditLock) {
                                                const v = parseFloat(e.target.value);
                                                setEditedTreatmentSteps((prev) => ({
                                                  ...prev,
                                                  [step.id]: {
                                                    ...prev[step.id],
                                                    conc: isNaN(v) ? 0 : v,
                                                  },
                                                }));
                                              }
                                            }}
                                            onBlur={(e) => {
                                              if (hasEditLock) {
                                                const v = parseFloat(parseFloat(e.target.value).toFixed(2));
                                                setEditedTreatmentSteps((prev) => ({
                                                  ...prev,
                                                  [step.id]: {
                                                    ...prev[step.id],
                                                    conc: isNaN(v) ? 0 : v,
                                                  },
                                                }));
                                              }
                                            }}
                                            style={inputStyle}
                                            disabled={!isRealTimeStreaming || !hasEditLock}
                                          />
                                        );
                                      } else {
                                        return (
                                          <Form.Control
                                            type="number"
                                            size="sm"
                                            readOnly
                                            value={typeof value === 'number' ? value.toFixed(2) : value}
                                            style={inputStyle}
                                            disabled={!isRealTimeStreaming || !hasEditLock}
                                          />
                                        );
                                      }
                                    })()}
                                    <div 
                                      style={{ 
                                        marginLeft: '8px', 
                                        fontSize: '0.85em',
                                        color: theme === 'dark' ? '#fff' : '#6c757d',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {(() => {
                                        const currentType = editedTreatmentSteps[step.id]?.type !== undefined 
                                          ? editedTreatmentSteps[step.id].type 
                                          : step.type;
                                        
                                        if (currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp') {
                                          const editedStep = editedTreatmentSteps[step.id];
                                          const startConc = editedStep?.start_conc !== undefined
                                            ? editedStep.start_conc
                                            : ((step as any).start_conc || step.conc || 0);
                                          const endConc = editedStep?.end_conc !== undefined
                                            ? editedStep.end_conc
                                            : ((step as any).end_conc || step.conc || 0);

                                          return `- (${Number(startConc).toFixed(2)}/${Number(endConc).toFixed(2)})`;
                                        } else {
                                          return '';
                                        }
                                      })()}
                                    </div>
                                    {(() => {
                                      if (!hasEditLock) return null;
                                      if (!treatmentSchedule.some(s => s.step > step.step)) return null;
                                      const currentType = editedTreatmentSteps[step.id]?.type ?? step.type;
                                      const edited = editedTreatmentSteps[step.id];
                                      if (currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp') {
                                        const startChanged = edited?.start_conc !== undefined && edited.start_conc !== ((step as any).start_conc ?? step.conc);
                                        const endChanged   = edited?.end_conc   !== undefined && edited.end_conc   !== ((step as any).end_conc   ?? step.conc);
                                        const concChanged  = edited?.conc       !== undefined && edited.conc       !== step.conc;
                                        if (!startChanged && !endChanged && !concChanged) return null;
                                        return renderCascadeBtn(
                                          () => cascadeSandRampConc(step.step),
                                          `Apply this concentration ramp to step ${step.step} and all later steps`
                                        );
                                      }
                                      if (currentType === 'Proppant' || currentType === 'Rate Drop-Proppant') {
                                        if (edited?.conc === undefined || edited.conc === step.conc) return null;
                                        return renderCascadeBtn(
                                          () => cascadeTreatmentField(step.step, 'conc', edited.conc as number),
                                          `Apply this concentration to step ${step.step} and all later steps`
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </td>
                                <td>
                                  {(() => {
                                    const currentType = editedTreatmentSteps[step.id]?.type ?? step.type;
                                    const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';

                                    if (editBySand && isProppantOrSandRamp && hasEditLock && isRealTimeStreaming) {
                                      // Show back-calculated clean step as readonly
                                      if (editedTreatmentSteps[step.id]?.sand_step !== undefined) {
                                        const conc = (editedTreatmentSteps[step.id]?.conc ?? step.conc) || 0;
                                        const sandStep = editedTreatmentSteps[step.id].sand_step as number;
                                        return Number(conc > 0 ? sandStep / (42 * conc) : 0).toFixed(0);
                                      }
                                      return Number(step.clean_step).toFixed(0);
                                    }

                                    return (
                                      <div className="d-flex align-items-center">
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                          value={Number(editedTreatmentSteps[step.id]?.clean_step ?? step.clean_step ?? 0).toFixed(0)}
                                          onChange={(e) => {
                                            if (hasEditLock) {
                                              const value = parseFloat(e.target.value);
                                              setEditedTreatmentSteps(prev => ({
                                                ...prev,
                                                [step.id]: {
                                                  ...prev[step.id],
                                                  clean_step: isNaN(value) ? 0 : value
                                                }
                                              }));
                                            }
                                          }}
                                          style={{
                                            backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                                            color: theme === 'dark' ? '#e5e5e5' : '#333333',
                                            borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6',
                                            width: '80px'
                                          }}
                                          disabled={!isRealTimeStreaming || !hasEditLock}
                                        />
                                        {hasEditLock &&
                                          editedTreatmentSteps[step.id]?.clean_step !== undefined &&
                                          editedTreatmentSteps[step.id].clean_step !== step.clean_step &&
                                          treatmentSchedule.some(s => s.step > step.step) &&
                                          renderCascadeBtn(
                                            () => cascadeTreatmentField(step.step, 'clean_step', editedTreatmentSteps[step.id].clean_step as number),
                                            `Apply this clean step to step ${step.step} and all later steps`
                                          )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td>
                                  {(() => {
                                    const currentType = editedTreatmentSteps[step.id]?.type ?? step.type;
                                    const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                    const conc = (editedTreatmentSteps[step.id]?.conc !== undefined 
                                      ? editedTreatmentSteps[step.id].conc 
                                      : step.conc) || 0;
                                    const description = (editedTreatmentSteps[step.id]?.description !== undefined 
                                      ? editedTreatmentSteps[step.id].description 
                                      : step.description) || '';
                                    const descriptionValue = getDescriptionValue(description);

                                    let cleanStep: number;
                                    if (editBySand && isProppantOrSandRamp && editedTreatmentSteps[step.id]?.sand_step !== undefined) {
                                      const sandStep = editedTreatmentSteps[step.id].sand_step as number;
                                      cleanStep = conc > 0 ? sandStep / (42 * conc) : 0;
                                    } else {
                                      cleanStep = (editedTreatmentSteps[step.id]?.clean_step !== undefined 
                                        ? editedTreatmentSteps[step.id].clean_step 
                                        : step.clean_step) || 0;
                                    }

                                    const slurryStep = cleanStep + (cleanStep * conc / descriptionValue);
                                    return slurryStep.toFixed(0);
                                  })()}
                                </td>
                                {/* Slurry Total */}
                                <td>{totals[step.id]?.slurry.toFixed(0) ?? 0}</td>
                                <td>
                                  {(() => {
                                    const currentType = editedTreatmentSteps[step.id]?.type ?? step.type;
                                    const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                    const conc = (editedTreatmentSteps[step.id]?.conc ?? step.conc) || 0;
                                    const cleanStep = (editedTreatmentSteps[step.id]?.clean_step !== undefined 
                                      ? editedTreatmentSteps[step.id].clean_step 
                                      : step.clean_step) || 0;

                                    if (editBySand && isProppantOrSandRamp && hasEditLock && isRealTimeStreaming) {
                                      const concIsZero = conc === 0;
                                      const originalSandStep = Math.round((step.clean_step || 0) * 42 * (step.conc || 0));
                                      const editedSandStep = editedTreatmentSteps[step.id]?.sand_step;
                                      const sandStepChanged = editedSandStep !== undefined && Math.round(editedSandStep) !== originalSandStep;
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                          <div className="d-flex align-items-center">
                                            <input
                                              type="number"
                                              onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                                              className={`form-control form-control-sm ${theme === 'dark' ? 'bg-dark text-light' : ''}`}
                                              value={(() => {
                                                if (editedTreatmentSteps[step.id]?.sand_step !== undefined) {
                                                  return Math.round(editedTreatmentSteps[step.id].sand_step as number);
                                                }
                                                // Derive from clean_step * 42 * conc as the authoritative fallback —
                                                // step.sand_step may be 0 when the schedule was populated from MQTT.
                                                const c = (editedTreatmentSteps[step.id]?.conc ?? step.conc) || 0;
                                                const cs = (editedTreatmentSteps[step.id]?.clean_step ?? step.clean_step) || 0;
                                                return Math.round(cs * 42 * c);
                                              })()}
                                              onChange={(e) => {
                                                if (hasEditLock && !concIsZero) {
                                                  const value = parseFloat(e.target.value);
                                                  setEditedTreatmentSteps(prev => ({
                                                    ...prev,
                                                    [step.id]: {
                                                      ...prev[step.id],
                                                      sand_step: isNaN(value) ? 0 : Math.round(value)
                                                    }
                                                  }));
                                                }
                                              }}
                                              disabled={concIsZero}
                                              title={concIsZero ? 'Cannot edit sand step: concentration is 0' : undefined}
                                              style={{
                                                width: '80px', textAlign: 'center',
                                                backgroundColor: concIsZero
                                                  ? (theme === 'dark' ? '#3a1a1a' : '#ffe5e5')
                                                  : (theme === 'dark' ? '#303335' : '#ffffff'),
                                                color: concIsZero
                                                  ? (theme === 'dark' ? '#ff6b6b' : '#cc0000')
                                                  : (theme === 'dark' ? '#e5e5e5' : '#333333'),
                                                borderColor: concIsZero ? '#cc0000' : (theme === 'dark' ? '#5c646b' : '#e6e6e6'),
                                                cursor: concIsZero ? 'not-allowed' : 'auto'
                                              }}
                                            />
                                            {sandStepChanged &&
                                              !concIsZero &&
                                              treatmentSchedule.some(s => s.step > step.step) &&
                                              renderCascadeBtn(
                                                () => cascadeTreatmentField(step.step, 'sand_step', editedSandStep as number),
                                                `Apply this sand step to step ${step.step} and all later steps`
                                              )}
                                          </div>
                                          {concIsZero && (
                                            <small style={{ color: '#cc0000', fontSize: '0.7em', marginTop: '2px', whiteSpace: 'nowrap' }}>
                                              Conc must be &gt; 0
                                            </small>
                                          )}
                                        </div>
                                      );
                                    }

                                    // Not in editBySand mode — show calculated value
                                    return (cleanStep * 42 * conc).toFixed(0);
                                  })()}
                                </td>
                                {/* Sand Total */}
                                <td>{totals[step.id]?.sand.toFixed(0) ?? 0}</td>
                                <td>
                                  {(() => {
                                    if (step.step === currentStep && realTimeData?.elapsedTime !== undefined) {
                                      return (realTimeData.elapsedTime / 60/1000).toFixed(2);
                                    } else {
                                      const rate = editedTreatmentSteps[step.id]?.rate !== undefined 
                                        ? editedTreatmentSteps[step.id].rate 
                                        : step.rate;
                                      const currentType = editedTreatmentSteps[step.id]?.type ?? step.type;
                                      const isProppantOrSandRamp = currentType === 'Proppant' || currentType === 'Rate Drop-Proppant' || currentType === 'Sand Ramp' || currentType === 'Rate Drop-Sand Ramp';
                                      const conc = (editedTreatmentSteps[step.id]?.conc ?? step.conc) || 0;
                                      const cleanStep = (editBySand && isProppantOrSandRamp && editedTreatmentSteps[step.id]?.sand_step !== undefined)
                                        ? (conc > 0 ? (editedTreatmentSteps[step.id].sand_step as number) / (42 * conc) : 0)
                                        : (editedTreatmentSteps[step.id]?.clean_step !== undefined 
                                          ? editedTreatmentSteps[step.id].clean_step 
                                          : step.clean_step);
                                      return rate && cleanStep ? (cleanStep / rate).toFixed(2) : 'NaN';
                                    }
                                  })()}
                                </td>
                              </tr>
                            ));
                          })()
                        ) : (
                          <tr>
                            <td colSpan={12} className="text-center">
                              No steps available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                  

                  {/* ── Chemicals sub-tab ──────────────────────────────────── */}

                  {/* Edit/Save Button */}
                  {!isViewer &&
                    <div className="d-flex justify-content-end mt-3">
                      {!hasEditLock ? (
                        <Button 
                          variant="primary" 
                          onClick={() => currentStage && requestEditLock(currentStage.id)}
                          style={{
                            backgroundColor: theme === 'dark' ? '#8CC540' : '#8CC540',
                            borderColor: theme === 'dark' ? '#8CC540' : '#8CC540',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.2s ease-in-out'
                          }}
                          disabled={!canEditStage || treatmentSchedule.length === 0 || !isCurrentStepVisible()}
                        >
                          Edit
                        </Button>
                      ) : (
                        <div className="d-flex gap-2">
                          <Button 
                            variant="success" 
                            onClick={handleSaveSchedule}
                            style={{
                              backgroundColor: '#8CC540',
                              borderColor: '#8CC540',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.2s ease-in-out'
                            }}
                            disabled={!generateChangeDescription(treatmentSchedule.map(s => ({...s, ...editedTreatmentSteps[s.id]})), originalTreatmentSchedule) || isSavingSchedule || rateDropConstraints.hasViolation || !canEditStage}
                          >
                            {isSavingSchedule ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Waiting for field approval
                              </>
                            ) : (
                              'Save'
                            )}
                          </Button>
                          <Button 
                            variant="secondary" 
                            onClick={() => currentStage && releaseEditLock(currentStage.id)}
                            style={{
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  }
                </div>
              </div>
              </>
            )}
            
            {/* Seismos & Momentum Data Table */}

              {selectedStage && (
                <MomentumFdiComponent
                  stageId={selectedStage.stageId}
                  theme={theme}
                  styles={styles}
                />
              )}

            {selectedStage && (
            <SeismosDataComponent
              stageId={selectedStage.stageId}
              theme={theme}
              styles={styles}
              onMetricsChange={setLiveSeismosMetrics}
              onOpenPrcPlot={handleOpenPrcPlot}
              padId={selectedPadId}
              canRefresh={!isViewer}
              onOpenScorecard={() => {
                setScorecardLevel('stage');
                setScorecardStageId(selectedStage.stageId);
                setScorecardWellId(selectedStage.wellId);
                setShowScorecardModal(true);
              }}
            />
          )}
          </div>
        </div>
      </div>

      {/* Manage Templates Modal */}
      <Modal
        show={showManageTemplates}
        onHide={() => setShowManageTemplates(false)}
        backdrop="static"
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Modal.Title style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
            Manage Templates
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#303335' : '#ffffff' }}>
          <Form>
            <Form.Group controlId="templateName" className="mb-3">
              <Form.Label style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
                Template Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter template name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                style={{
                  backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                  color: theme === 'dark' ? '#e5e5e5' : '#333333',
                  borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6'
                }}
              />
            </Form.Group>
            <Button 
              variant="primary" 
              type="button" 
              onClick={handleSaveTemplate}
              style={{
                backgroundColor: theme === 'dark' ? '#4361ee' : '#4361ee',
                borderColor: theme === 'dark' ? '#3a0ca3' : '#3a0ca3'
              }}
            >
              Save Template
            </Button>
          </Form>

          <hr style={{ borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6' }} />
          
          <h5 style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>Available Templates</h5>
          {templates.length > 0 ? (
            templates.map((templateObj) => (
              <div
                key={templateObj.id}
                className="d-flex justify-content-between align-items-center mb-2"
              >
                <span style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
                  {templateObj.template_name}
                </span>
                <div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="me-2"
                    onClick={() => {
                      setSelectedTemplate({
                        value: templateObj.template_name,
                        label: templateObj.template_name,
                        id: templateObj.id,
                      });
                      applyTemplate(templateObj);
                      setShowManageTemplates(false);
                    }}
                    style={{
                      borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6',
                      color: theme === 'dark' ? '#e5e5e5' : '#333333'
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEditTemplate(templateObj)}
                    style={{
                      borderColor: theme === 'dark' ? '#f59e0b' : '#f59e0b',
                      color: theme === 'dark' ? '#f59e0b' : '#f59e0b'
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteTemplate(templateObj.id)}
                    style={{
                      borderColor: theme === 'dark' ? '#f72585' : '#f72585',
                      color: theme === 'dark' ? '#f72585' : '#f72585'
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>No templates available.</p>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Button 
            variant={theme === 'dark' ? 'dark' : 'secondary'} 
            onClick={() => setShowManageTemplates(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Axis Configuration Modal */}
      <Modal
        show={showAxisConfig}
        onHide={() => setShowAxisConfig(false)}
        backdrop="static"
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Modal.Title style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
            Axis Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#303335' : '#ffffff' }}>
          <style>{`
            .axis-minmax-input::placeholder {
              color: ${theme === 'dark' ? '#8b9cb7' : '#9ca3af'} !important;
            }
          `}</style>
          {/* Axis-first configuration UI */}
          {Object.keys(axisSettings).map((axis) => (
            <Card 
              key={axis} 
              className="mb-4" 
              bg={theme === 'dark' ? 'dark' : 'light'}
              text={theme === 'dark' ? 'white' : 'dark'}
              style={{ 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
                  {axis.charAt(0).toUpperCase() + axis.slice(1)} Axis
                </h5>
              </Card.Header>
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
                    Min/Max Values
                  </Form.Label>
                  <Row>
                    <Col md={6}>
                      <Form.Control
                        className="axis-minmax-input"
                        type="number"
                        placeholder="Min"
                        value={axisSettings[axis].min !== undefined ? axisSettings[axis].min : ''}
                        onChange={(e) => 
                          handleAxisSettingChange(
                            axis, 
                            'min', 
                            e.target.value === '' ? undefined : Number(e.target.value)
                          )
                        }
                        style={{
                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                          color: theme === 'dark' ? '#e5e5e5' : '#333333',
                          borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6'
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        className="axis-minmax-input"
                        type="number"
                        placeholder="Max"
                        value={axisSettings[axis].max !== undefined ? axisSettings[axis].max : ''}
                        onChange={(e) => 
                          handleAxisSettingChange(
                            axis, 
                            'max', 
                            e.target.value === '' ? undefined : Number(e.target.value)
                          )
                        }
                        style={{
                          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                          color: theme === 'dark' ? '#e5e5e5' : '#333333',
                          borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6'
                        }}
                      />
                    </Col>
                  </Row>
                </Form.Group>
                
                <Form.Group>
                  <Form.Label style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
                    Channels
                  </Form.Label>
                  <Select
                    isMulti
                    options={(() => {
                      const assignedElsewhere = new Set(
                        Object.entries(axisSettings)
                          .filter(([key]) => key !== axis)
                          .flatMap(([, v]) => v.channels)
                      );
                      return channelGroupOptions.map(group => ({
                        ...group,
                        options: group.options.filter(
                          opt => selectedChannels.some(ch => ch.value === opt.value) &&
                                !assignedElsewhere.has(opt.value)
                        )
                      })).filter(group => group.options.length > 0);
                    })()}
                    value={channels.filter(ch => (axisSettings[axis]?.channels ?? []).includes(ch.value))}
                    onChange={(sel) =>
                      handleChannelAxisAssignment(axis, (sel as ChannelOption[]).map(s => s.value))
                    }
                    placeholder="Select Channels"
                    className={theme === 'dark' ? 'react-select-dark' : ''}
                    formatGroupLabel={(group) => (
                      <div style={{
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'space-between',
                        color:          theme === 'dark' ? '#F9C430' : '#5a4000',
                        fontWeight:     700,
                        fontSize:       '0.75rem',
                        letterSpacing:  '0.06em',
                        textTransform:  'uppercase',
                        padding:        '4px 0',
                        borderBottom:   `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`,
                      }}>
                        <span>{group.label}</span>
                        <span style={{
                          backgroundColor: theme === 'dark' ? '#5c646b' : '#e2e8f0',
                          borderRadius:    '10px',
                          padding:         '1px 8px',
                          fontSize:        '0.7rem',
                          fontWeight:      600,
                          color:           theme === 'dark' ? '#e5e5e5' : '#4a5568',
                        }}>
                          {group.options.length}
                        </span>
                      </div>
                    )}
                    styles={{
                      option: (provided, state) => ({
                        ...provided,
                        color: theme === 'dark' ? '#ffffff' : '#333333',
                        backgroundColor: state.isSelected
                          ? theme === 'dark' ? '#303335' : '#4361ee'
                          : state.isFocused
                            ? theme === 'dark' ? '#303335' : '#f0f0f0'
                            : theme === 'dark' ? '#303335' : '#ffffff',
                        fontWeight: 500,
                        border: '1px solid #5C646B'
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: theme === 'dark' ? '#303335' : '#e6e6e6',
                        border: '1px solid #5C646B'
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: theme === 'dark' ? '#ffffff' : '#333333',
                        fontWeight: 500
                      }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
                        borderColor: theme === 'dark' ? '#5c646b' : '#e6e6e6'
                      }),
                      groupHeading: (provided) => ({
                        ...provided,
                        padding: '4px 12px 0',
                      }),
                    }}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          ))}
          
          {selectedChannels.length === 0 && (
            <p style={{ color: theme === 'dark' ? '#8b9cb7' : '#6c757d' }}>
              Please select channels first to configure their axes.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Button 
            variant={theme === 'dark' ? 'dark' : 'secondary'} 
            onClick={() => setShowAxisConfig(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Settings Modal */}
      <Modal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        backdrop="static"
        size="lg"
        centered
      >
        <Modal.Header closeButton style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Modal.Title style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>
            Chart Settings
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#303335' : '#ffffff' }}>
          <div className="mb-4">
            <h5 style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>Template</h5>
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: '100%' }}>
                <Select
                  options={templates.map((t) => ({
                    value: t.template_name,
                    label: t.template_name,
                    id: t.id,
                  }))}
                  value={selectedTemplate}
                  onChange={handleTemplateSelect}
                  placeholder="Select Template"
                  isClearable
                  className={theme === 'dark' ? 'react-select-dark' : ''}
                />
              </div>
              <Button
                variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
                size="sm"
                onClick={() => {
                  setShowSettingsModal(false);
                  setShowManageTemplates(true);
                }}
              >
                Manage
              </Button>
            </div>
          </div>
          
          <div className="mb-4">
            <h5 style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>Channels</h5>
            <Select
              isMulti
              options={channelGroupOptions}
              value={selectedChannels}
              onChange={(sel) => setSelectedChannels(sel as ChannelOption[])}
              placeholder="Select Channels"
              className={theme === 'dark' ? 'react-select-dark' : ''}
              formatGroupLabel={(group) => (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  color: theme === 'dark' ? '#F9C430' : '#5a4000',
                  fontWeight: 700, fontSize: '0.75rem',
                  letterSpacing: '0.06em', textTransform: 'uppercase' as const,
                  padding: '4px 0',
                  borderBottom: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`,
                }}>
                  <span>{group.label}</span>
                  <span style={{
                    backgroundColor: theme === 'dark' ? '#5c646b' : '#e2e8f0',
                    borderRadius: '10px', padding: '1px 8px',
                    fontSize: '0.7rem', fontWeight: 600,
                    color: theme === 'dark' ? '#e5e5e5' : '#4a5568',
                  }}>
                    {group.options.length}
                  </span>
                </div>
              )}
              styles={reactSelectStyles}
            />
          </div>
          
          <div className="mb-4">
            <h5 style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333' }}>Axis Configuration</h5>
            <Button
              variant={theme === 'dark' ? 'outline-light' : 'outline-secondary'}
              size="sm"
              onClick={() => {
                setShowSettingsModal(false);
                setShowAxisConfig(true);
              }}
              style={{ width: '100%' }}
            >
              Configure Axes
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#e6e6e6'}`
        }}>
          <Button 
            variant={theme === 'dark' ? 'dark' : 'secondary'} 
            onClick={() => setShowSettingsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Treatment Modal */}
      {selectedStage && (
        <TreatmentModal
          show={showTreatmentModal}
          onHide={() => {
            setShowTreatmentModal(false);
            fetchTreatmentChanges(selectedStage.stageId)
        fetchNextChemSchedule(selectedStage.stageId)
            // Keep the View Treatment button visible when in real-time streaming mode
            if (isRealTimeStreaming) {
              setShowViewTreatmentButton(true);
            }
          }}
          stageId={selectedStage.stageId}
          wellId={selectedStage.wellId}
          stageNumber={selectedStage.stageNumber}
          isRealTimeStreaming={canEditStage}
          currentStep={currentStep}
          onTreatmentScheduleUpdate={refreshTreatmentSchedule}
        />
      )}

      {/* Concentration Modal */}
      <ConcentrationModal
        show={showConcentrationModal}
        onHide={() => setShowConcentrationModal(false)}
        onSave={handleConcentrationSave}
        initialStartConc={selectedStepForConcentration ? (() => {
          // Get the current edited value first, then fall back to original values
          const editedStartConc = editedTreatmentSteps[selectedStepForConcentration.id]?.start_conc;
          if (editedStartConc !== undefined) return editedStartConc;
          
          // Check if original step has start_conc, otherwise use conc
          const originalStartConc = (selectedStepForConcentration as any).start_conc;
          return originalStartConc !== undefined ? originalStartConc : selectedStepForConcentration.conc;
        })() : 0}
        initialEndConc={selectedStepForConcentration ? (() => {
          // Get the current edited value first, then fall back to original values
          const editedEndConc = editedTreatmentSteps[selectedStepForConcentration.id]?.end_conc;
          if (editedEndConc !== undefined) return editedEndConc;
          
          // Check if original step has end_conc, otherwise use conc
          const originalEndConc = (selectedStepForConcentration as any).end_conc;
          return originalEndConc !== undefined ? originalEndConc : selectedStepForConcentration.conc;
        })() : 0}
      />

    <RulesManagementModal
      show={showRulesModal}
      onHide={() => setShowRulesModal(false)}
      padId={selectedPadId}
      chemChannels={channels.filter(c => c.chem_name)}
      scoreboardChannels={channels.filter(c => c.scoreboard_channel)}
      userRole={roles}
      wells={wells}
      stages={stages}
    />

    <ReportsManagementModal
      show={showReportsModal}
      onHide={() => setShowReportsModal(false)}
      padId={selectedPadId}
      userRole={roles[0]}
    />

    {/* Chemical Group Ratios Modal */}
    {chemGroupModal && (
      <Modal
        show={true}
        onHide={() => setChemGroupModal(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderBottom: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`
        }}>
          <Modal.Title style={{ color: theme === 'dark' ? '#e5e5e5' : '#333333', fontSize: '1rem' }}>
            {chemGroupModal.chemName} — Step {chemGroupModal.step} Group Ratios
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme === 'dark' ? '#303335' : '#ffffff' }}>
          <p style={{ fontSize: '0.8rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280', marginBottom: '1rem' }}>
            Edit individual group ratios (gpt). Groups are defined by Petrix and are read-only.
          </p>
          {chemGroupModal.groups.map((g, i) => (
            <div key={i} className="d-flex align-items-center mb-2 gap-2">
              <span style={{
                minWidth: '90px', fontSize: '0.85rem', fontWeight: 600,
                color: theme === 'dark' ? '#e5e5e5' : '#333333'
              }}>
                {g.group || '(No Group)'}
              </span>
              <input
                type="number"
                step="any"
                value={g.ratio}
                onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).focus(); }} onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === "a") e.preventDefault(); }}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  setChemGroupModal(prev => prev ? {
                    ...prev,
                    groups: prev.groups.map((grp, idx) =>
                      idx === i ? { ...grp, ratio: isNaN(v) ? 0 : v } : grp
                    )
                  } : null);
                }}
                style={{
                  width: '80px', textAlign: 'center', fontSize: '0.85rem',
                  padding: '3px 6px', borderRadius: '4px',
                  border: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`,
                  backgroundColor: theme === 'dark' ? '#1a2234' : '#ffffff',
                  color: theme === 'dark' ? '#e5e5e5' : '#333333'
                }}
              />
              <span style={{ fontSize: '0.8rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>gpt</span>
            </div>
          ))}
          <div style={{
            marginTop: '1rem', paddingTop: '0.75rem',
            borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`,
            fontSize: '0.82rem', color: theme === 'dark' ? '#9ca3af' : '#6b7280'
          }}>
            Total: <strong style={{ color: theme === 'dark' ? '#ffe066' : '#5a4000' }}>
              {chemGroupModal.groups.reduce((s, g) => s + g.ratio, 0).toFixed(3)} gpt
            </strong>
          </div>
        </Modal.Body>
        <Modal.Footer style={{
          backgroundColor: theme === 'dark' ? '#303335' : '#ffffff',
          borderTop: `1px solid ${theme === 'dark' ? '#5c646b' : '#dee2e6'}`
        }}>
          <Button variant="secondary" size="sm" onClick={() => setChemGroupModal(null)}>Cancel</Button>
          <Button
            size="sm"
            style={{ backgroundColor: '#8CC540', borderColor: '#8CC540' }}
            onClick={() => {
              const k = `${chemGroupModal.step}::${chemGroupModal.chemName}`;
              setEditedChemGroupRatios(prev => ({ ...prev, [k]: chemGroupModal.groups }));
              setChemGroupModal(null);
            }}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    )}
      {/* ── Seismos Scorecard Modal ───────────────────────────────────────── */}
      {showScorecardModal && (
        <Modal
          show={showScorecardModal}
          onHide={() => setShowScorecardModal(false)}
          size="xl"
          centered
          scrollable
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: theme === 'dark' ? '#242220' : '#f5f5f5',
              borderBottom: '1px solid #38352f',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              {/* Row 1 — title + scorecard type */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Modal.Title style={{ color: '#F9C430', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>
                  Seismos Scorecards
                </Modal.Title>
                <div style={{ display: 'flex', background: '#2c2a28', border: '1px solid #38352f', borderRadius: 6, padding: 2 }}>
                  {([['events', 'Events'], ['rates', 'Rate Drops']] as const).map(([v, label]) => (
                    <button
                      key={v}
                      onClick={() => setScorecardView(v)}
                      style={{
                        background:   scorecardView === v ? '#F9C430' : 'transparent',
                        color:        scorecardView === v ? '#231F20' : '#8a8480',
                        border:       'none',
                        borderRadius: 4,
                        padding:      '4px 16px',
                        fontSize:     12,
                        fontWeight:   600,
                        cursor:       'pointer',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 2 — one Stage / Well / Pad selector shared by both scorecards */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {/* Level toggle */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['stage', 'well', 'pad'] as ScorecardLevel[]).map(lvl => (
                    <button
                      key={lvl}
                      onClick={() => setScorecardLevel(lvl)}
                      style={{
                        background:    scorecardLevel === lvl ? '#F9C430' : 'transparent',
                        color:         scorecardLevel === lvl ? '#231F20' : '#8a8480',
                        border:        `1px solid ${scorecardLevel === lvl ? '#F9C430' : '#38352f'}`,
                        borderRadius:  4,
                        padding:       '3px 12px',
                        fontSize:      11,
                        fontWeight:    600,
                        cursor:        'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {lvl}
                    </button>
                  ))}
                </div>

                {/* Entity selector */}
                <div style={{ marginLeft: 'auto', marginRight: 32, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {scorecardLevel === 'stage' && (
                    <select
                      value={scorecardStageId ?? ''}
                      onChange={e => setScorecardStageId(Number(e.target.value))}
                      style={{
                        background: '#2c2a28', border: '1px solid #38352f',
                        color: '#EDE8DF', borderRadius: 4,
                        padding: '3px 10px', fontSize: 12,
                      }}
                    >
                      {wells.map(well => {
                        const wellStages = stages
                          .filter(s => s.well_id === well.id)
                          .sort((a, b) => a.stage_number - b.stage_number);
                        return wellStages.length ? (
                          <optgroup key={well.id} label={well.well_name}>
                            {wellStages.map(s => (
                              <option key={s.id} value={s.id}>
                                Stage {s.stage_number}
                              </option>
                            ))}
                          </optgroup>
                        ) : null;
                      })}
                    </select>
                  )}
                  {scorecardLevel === 'well' && (
                    <select
                      value={scorecardWellId ?? ''}
                      onChange={e => setScorecardWellId(Number(e.target.value))}
                      style={{
                        background: '#2c2a28', border: '1px solid #38352f',
                        color: '#EDE8DF', borderRadius: 4,
                        padding: '3px 10px', fontSize: 12,
                      }}
                    >
                      {wells.map(w => (
                        <option key={w.id} value={w.id}>{w.well_name}</option>
                      ))}
                    </select>
                  )}
                  {scorecardLevel === 'pad' && (
                    <span style={{ fontSize: 12, color: '#8a8480' }}>{padName}</span>
                  )}
                </div>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body
            style={{
              backgroundColor: '#181716',
              padding: '16px 20px',
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            {(() => {
              const entityId =
                scorecardLevel === 'stage' ? scorecardStageId :
                scorecardLevel === 'well'  ? scorecardWellId  : selectedPadId;
              if (!entityId) return null;
              return (
                <SeismosScorecard
                  key={`${scorecardView}-${scorecardLevel}-${entityId}`}
                  view={scorecardView}
                  padId={selectedPadId}
                  padName={padName}
                  level={scorecardLevel}
                  entityId={entityId}
                  apiBase={process.env.REACT_APP_BACKEND_URL ?? ''}
                  token={localStorage.getItem('token') ?? ''}
                  isEditor={!isViewer}
                />
              );
            })()}
          </Modal.Body>
        </Modal>
      )}
    </section>
  );
};

export default TreatmentDataGraph;